'use client';

// Список сценариев: черновики строителя (этап 3) + готовые с сервера юзера (Этап 8,
// тумблер вкл/выкл). Готовый сценарий, собранный в кабинете, открывается в конструктор
// своего черновика (этап B6) — черновик из блока «Черновики» при этом уходит (не дублим).
// Каркас/заголовки рендерит страница (instant-shell) — здесь динамика.
//
// ОДИН СПИСОК НА ДВА РАЗДЕЛА (канон владельца 21.08, CABINET-UX-FIXLIST «Паритет
// „Рассылки = боты“»). Рассылка — ТОТ ЖЕ БОТ с другим запуском, поэтому «Боты» и
// «Рассылки» ведёт этот компонент: `kind` решает, какую половину общего разбиения
// (lib/broadcast-list.ts) показать. Различий ровно три: вид триггера (он же отбор),
// слова («рассылка» вместо «бот») и кнопка «Отправить/Остановить» вместо
// «Включить/Выключить». Второго списка карточек, второй страницы цифр и второго флоу
// создания в кабинете НЕТ — они снесены, а не починены заплатками.
import { scenariosFast } from '@/lib/bot-live-fast';
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { IgQueueNote } from './ig-queue-note';
import type { IgQueueInfo } from '@/lib/ig-queue-core';
import { chatStartChannelOf, stepKindOf, type BroadcastStatus } from '@scriptera/ui/scenario';
import { PopMenu, PopSelect } from '@scriptera/ui';
import { KActionBar, KBareButton, KButton, KEmpty, KToast } from '@scriptera/ui/kit';
import { FHint, FPane } from './form-kit';
import { TopupForm } from './billing-balance';
import { useTokensTopupAllowed } from './billing-shared';
import { useBillingPlate } from './billing';
import { ProBadge, useProLocked } from './billing-views';
import { hasNativeNotice, showNativeNotice, showNativeNoticeFull } from './confirm-sheet';
import {
  ChannelIcon,
  channelHex,
  nativeChannelTile,
  tplCatalogItems,
  useChainTplItems,
  useWizardDemoWarm,
  type TplItem,
} from './bot-templates';

import { BrandLogo } from './ai/ai-vendor-icon';
import { iconOverride } from './icon-overrides';
import { Link, useRouter } from '@/lib/cabinet-nav';
import { Shape, Sk, useShapeRecord } from './skeleton';
import { LoadError } from './load-error';
import { useSlidePanel } from './native-layer-host';
import { HERO_ICONS, openHeroAsk } from './hero-ask-sheet';
import { PageAction, PageAddButton, PaneActiveContext } from './page-action';
import { useAppLive } from '@/lib/messages-live';
import { usePaneVisible } from '@/lib/pane-visible';
import { usePersistentData } from '@/lib/use-persistent-data';
import { okAccept, warmTarget } from '@/lib/warm';
import { NATIVE_WARM_FIRST, useWarmInView } from '@/lib/warm-in-view';
import { botWindowPrefs, warmBotEvents } from './bot-journal';
import { warmChainStats } from './chain-page';
import { warmBroadcastLaunch } from './broadcast-launch';
import { SCENARIO_TITLES_KEY, SCENARIO_TITLES_URL, scenarioTitlesOk } from '@/lib/bot-title-number';
import { useNichePersonal } from '@/lib/use-niche-personal';
import { demoSceneUrlForEdit, demoThemeNow } from '@/lib/wizard-demo-url';
import { WARM_SCENE_LIMIT, warmDemoScenes } from '@/lib/warm-demo-scenes';
import { wizardById } from '@/lib/template-wizards';
import { openWizardBot, useWizEditSheet } from './wizard-host';
import { BOT_SHEET, readLayoutMode, useIsApp, useLayoutMode, webOnly } from './layout-mode';
import { FilterSlot } from './filter-slot';
import { useChainToggle } from './chain-toggle';
import { useFavorites } from '@/lib/use-favorites';
import { bySlug, slugify } from '@/lib/slug';
import { openNativeSettingsPage } from '@/lib/account-native';
import { FoldersStrip } from './messages-folders';
import { HeadFolders } from './list-head';
import { FolderPage, useFolderPager } from './folder-pager';
import {
  BROADCASTS_CACHE_KEY,
  BROADCASTS_URL,
  acceptBroadcasts,
  broadcastCardTarget,
  keepKind,
  type BroadcastsResp,
  type ListKind,
} from '@/lib/broadcast-list';
import { useBroadcastActs } from './broadcast-launch';
import { SettingsHero } from './settings-hero';
// Самолётик — ОДИН рисунок на продукт (icons.tsx): его же носит кнопка рассылок в шапке
// «Чатов» (И10 п.1). Своей копии значка раздел не держит.
import { IconPlane, IconPower, IconPullDown, IconTray } from './icons';
import { catColor, catList } from '@/lib/script-cats';
import { BT_FOLDERS_PAGE, useOpenBotsFolders } from './bots-folders';
import { monotonePath } from '@/lib/monotone-path';
import { CARD_PREFS_DEFAULT, cardPrefsFor, type CardPrefs } from '@/lib/bot-card-prefs';
import { draftFolders } from '@/lib/bot-folder-set';
import { botFailTextKey } from '@/lib/bot-fail-text';
import { SCR_FILTERS_EVENT } from './split-menu';
import { ASSISTANT_HIDDEN, BOTS_FILTER_HIDDEN, BROADCASTS_WIDE_SECTION, BUILDER_HIDDEN } from '@/lib/hidden-features';
import {
  nativeBridge,
  nativeMethod,
  openWebSheet,
  readRoleHex,
  useNativeApp,
  type NativeBcastArchiveCard,
  type NativeBcastCard,
  type NativeBcastPage,
  type NativeBotsRow,
} from '@/lib/native-app';
// Родная вкладка «Боты» в приложении (docs/NATIVE-TABS-PLAN.md): список рисует натив,
// этот раздел остаётся мозгом — данные, отбор, слова и руки тапов.
import { nativeBotsSupported, useNativeBots, type NativeBotsRect } from '@/lib/bots-native';
// Родные ОКНА карточки (волна 3): страница бота с цифрами и журналом, деталь прогона,
// «О сценарии», «Дубликат», причины отказов. Сборка их не умеет — открываются прежние
// веб-шторки (все вызовы ниже идут через одну руку openWindow).
import { nativeStatsSupported } from '@/lib/stats-native';
import { sectionUrl } from '@/lib/cabinet-urls';
import {
  closeBotWindowOfBot,
  closeBotWindowThen,
  closeNativeBotWindow,
  openNativeBotWindow,
  pushNativeBotWindowPage,
  type BotWindow,
  type BotWindowHooks,
} from './bot-window-native';
import {
  bumpBotWindowWebMenu,
  setBotWindowCloseHref,
  setBotWindowWebMenu,
  clearBotWindowWebMenu,
  type BotWindowWebMenu,
} from './bot-window-web';
import { log as journalLog, noteBridgeEvent } from '@/lib/client-journal';
import { OFF_AMBER } from '@/lib/bots-native';
// Родной раздел «Рассылки» (волна 3): витрину, героя пустого раздела и архив рисует
// натив (BcastListScreen.swift), мозг — тот же, что у веб-витрины.
import { nativeBcastListSupported, useNativeBcastList } from '@/lib/bcast-list-native';
// Прозрачность роли столбика — общая рука родных графиков (её же берёт «Аналитика»).
import { readRoleAlpha } from '@/lib/native-app';
import { useNativePhotos } from './native-photos';
import { closeNativeBroadcasts, subscribeBcastConnect } from './bcast-host';
import { BCAST_CONNECT_KEY, bcastAboutKey, bcastDupKey, useBcastPages } from './bcast-pages-native';
import { bcastRunsKey, bcastStatsKey, useBcastStats } from './bcast-stats-native';
import { N8nIconImg, useIsDark, useN8nIconColor, useN8nIcons } from './n8n-icon';
import { botStatsReady, hasNativeLayers, nativeLayerOpen, popLayer, useSlideOpen } from '@/lib/native-layers';
import { useBotExistsAsk, useCreateGo, useOpenCreateChooser } from './new-scenario-button';
import { QuickStripButton } from './bot-templates';
import { setQuickHasBots } from '@/lib/quick-has-bots';
import { botsEmptyWordKey, isNewAppBuild } from '@/lib/build-caps';
import { tgLinkWarnAction, tgLinkWarnNotice, tgLinkWarnWords, type TgLinkWarnView } from '@/lib/tg-link-warn';
import { useCreateWays, usePointMenuOk } from './create-ways';
import {
  SplitNewChat,
  SplitTemplates,
  scriptsPickSlug,
  scriptsPickCategory,
  scriptsPathFor,
  useScnHref,
  type ScriptsPick,
} from './split-menu';
import { BotStatsPane } from './bot-stats';
import { ManualStart, useAccountCreds } from './manual-start';
import { reportUiError } from '@/lib/ui-error-report';
import { ConfirmInline } from './confirm-inline';
import { askNativeConfirm, hasNativeConfirm } from './confirm-sheet';
import { askConfirm } from './ask';
import { useThemeAttr } from './settings';
import { DOTS } from './card-tools';
import { EDITOR_SHEET } from '@/lib/sheet-detent';
import { scenarioMenuItems, scenarioMenuNativeItems, menuToggleIcon, menuToggleSf, scenarioMenuSections } from './scenario-menu';
import { ScenarioAboutForm } from './scenario-workspace';
import { ScenarioSharePanel } from './scenario-share';
import { ScenarioDuplicatePanel } from './scenario-duplicate';
import { ScenarioRunsSheet, warmRuns } from './scenario-runs';
import { useScenarioRun } from './use-scenario-run';
import type { DraftSnapshot } from './chat';
import type { Pulse } from './overview-dashboard';
import { LINE_INK } from '@/lib/quick-tiles';

/** Узел для стопки логотипов: тип + имя шага (имя = бренд у ИИ-узла — логотип ChatGPT и т.п.). */
export interface ScenarioSvc {
  type: string;
  name?: string | null;
}

interface Scenario {
  id: string;
  name: string;
  active: boolean;
  createdAt?: string | null; // из n8n — фолбэк даты для сделанных в редакторе (этап 5)
  triggerType?: string | null; // тип узла-триггера — иконка запуска у статуса
  services?: ScenarioSvc[]; // узлы без триггера — стопка логотипов перед названием
  logo?: string | null; // выбор аватарки (null = авто-дуэт)
  /** Погашен по неоплате (контракт К17.1 п.4) — см. пояснение у DraftItem.billingOff. */
  billingOff?: boolean;
}

interface DraftItem {
  /** Создан формой быстрого запуска (QUICK-START §2.10). */
  quickStart?: string | null;
  id: string;
  title: string;
  // Описание карточки (этап 5): своё из drawer «О сценарии» или резюме первых шагов.
  description?: string | null;
  createdAt?: string;
  status: string; // draft | deployed | verified
  verified?: boolean; // текущая версия реально прошла проверку (verifiedVersion == version, этап 3)
  workflowId?: string | null; // id workflow на инстансе — связь готового сценария с черновиком (B6)
  /** ЦЕПОЧКА РАССЫЛОК: включена (true) / выключена (false); у обычного бота — null.
   *  Цепочка не едет на сервер сценариев, её тумблер — флаг active в bot_chains. */
  chainActive?: boolean | null;
  /** Боты, прохождение которых запускает эту цепочку (П203): по ним вопрос при удалении
   *  бота называет цепочки, которые он запускает. У бота — пусто. */
  chainSources?: string[];
  /** Удалённые боты среди них — с именами (П203): ими подписана карточка. */
  chainGone?: { id: string; title: string }[];
  /** Все входы цепочки — удалённые боты (П203): в списке стоит «Бот «X» удалён — не запускается». */
  chainNoEntry?: boolean;
  origin?: string; // cabinet | n8n — 'n8n' даёт бейдж «сделан в редакторе» (C1)
  syncState?: string; // ok | conflict | deleted — пометки синка C2
  shared?: boolean; // открыт доступ по публичной ссылке — значок у названия
  formUrl?: string | null; // живая ссылка мини-аппа → кнопка «Открыть форму» (HOW-IT-WORKS §15)
  /** Бот ведёт в Telegram-бот пользователя по ссылке с меткой, а запуска «Переход по
   *  ссылке» на неё нет (UT 14.10.2026 п.10): значок-предупреждение у названия. */
  tgLinkWarn?: TgLinkWarnView | null;
  chatUrl?: string | null; // то же для чата → «Открыть чат» (HOW-IT-WORKS §16)
  triggerType?: string | null; // тип узла-триггера — иконка запуска у статуса
  services?: ScenarioSvc[]; // узлы без триггера — стопка логотипов перед названием
  logo?: string | null; // выбор аватарки (null = авто-дуэт)
  // Аккаунт, от чьего имени работает сценарий («@mmtru») — виден прямо в списке,
  // без открытия сценария (владелец 2026-07-31).
  account?: string | null;
  /** Фото этого аккаунта (адрес нашей ручки) — аватарка карточки. */
  accountAvatar?: string | null;
  /** Канал аккаунта-отправителя (у цепочки — её кред): плитка и значок сети как у бота. */
  accountChannel?: string | null;
  /** Своя категория скрипта (владелец 2026-08-01): имя из личного списка юзера. */
  category?: string | null;
  /** Все папки бота (APP-CLAIMS-1020 ред. 3, П11; первая = `category`): отбор по папке — по ним (`draftFolders`). */
  categories?: string[];
  /** Скрипт рождён мастером-анкетой (FORM-WIZARD) — метка «быстрая настройка» на карточке.
   *  С О5.10 у мастерского бота тут ЗАПИСЬ анкеты (слаг, язык и ответы, если у анкеты есть
   *  сцена-превью): по ней список ЗАРАНЕЕ собирает адрес превью правки. Читателям метки
   *  хватает `!!wizard` — поле осталось falsy у немастерских, как и было. */
  wizard?: DraftWizardRef;
  /** Вид начала РАССЫЛКИ (manual | date | tag); null — обычный бот. Такие строки этот
   *  список ПРЯЧЕТ: они живут в разделе «Рассылки» (общий фильтр lib/broadcast-list.ts). */
  broadcast?: string | null;
  /** Последняя правка (даты из подвала убраны владельцем 2026-08-03, поле живёт для сортировок). */
  updatedAt?: string;
  /** Вошли в бота (people первого шага) — «N написали» на карточке (12.09.2026). */
  entered?: number;
  /** Дошли до цели (шаг «Записать в карточку») — «N подарков»/«N контактов». */
  people?: number;
  /** Доля дошедших до цели от вошедших, 0–100; null — считать не от чего. */
  conv?: number | null;
  /** Цель бота помечена меткой-подарком. */
  goalTag?: boolean;
  /** Подарки по дням за неделю (индекс 0 — сегодня) — вторая линия графика карточки. */
  /** НАСТРОЙКИ ПРЕВЬЮ КАРТОЧКИ (П119): что показывать, за какой срок и каким шагом.
   *  Пусто — человек не настраивал, карточка как раньше. */
  cardPrefs?: unknown;
  /** Готовые линии карточки под эту настройку — считает сервер. */
  cardLine?: { runs: number[]; contacts: number[]; conv: number[] } | null;
  goalDaily?: number[];
  /** Вошедшие по дням за неделю (П77) — конверсия по дням на карточке. */
  enteredDaily?: number[];
  /** Ответили под комментарием — средняя ступень «Кодового слова» (12.09.2026). */
  replied?: number;
  /**
   * ПОГАШЕН ПО НЕОПЛАТЕ (контракт К17.1 п.4, К15 П9 п.1). Бот/цепочка выключены нами, а
   * не человеком: тумблер стоит погашенным, а тап по нему показывает плашку оплаты и
   * НЕ дёргает ручку — сервер всё равно ответит отказом.
   */
  billingOff?: boolean;
  /** ОЧЕРЕДЬ ОТВЕТОВ ИНСТАГРАМА (SCALE-PLAN Б5): сколько ответов бота ждут отправки,
   *  сколько ждать последнему и сколько публичных пропущено за сутки. null — очереди
   *  нет, карточка про неё молчит. */
  igQueue?: IgQueueInfo | null;
}


/** Анкета бота, как её отдаёт список (`GET /api/cabinet/scenarios`): `false` — бот не
 *  мастерский; `true` — мастерский, но запись анкеты не разобралась; объект — слаг, язык
 *  и ответы (ответы есть только у анкет со сценой-превью). */
type DraftWizardRef = boolean | { id: string; locale?: 'ru' | 'en'; answers?: Record<string, unknown> };

/** Почему бот не включился (Г2 BUILDER-INSTANT). Виновник различается (запрет №7):
 *  lint — ошибки в шагах, чинит человек; balance — кончились токены; tech — наша
 *  поломка (сеть, сервер, неизвестное), клиенту без подробностей. */
type FailKind = 'lint' | 'balance' | 'tech';

/** ЧТО ЗНАЕТ МЕНЮ «⋯» КАРТОЧКИ. Одна форма на три показа: веб-выпадашка PopMenu,
 *  родное меню у точки в приложении и тап «⋯» на родном списке «Ботов» — второго
 *  меню карточки в кабинете нет (Э2 BOTS-MENU-NOTIFY). */
interface CardMenuOpts {
  id: string;
  on: boolean;
  busy: boolean;
  /** Ответ (если есть) — промис попытки: его ждёт тумблер страницы бота (П158 п.2). */
  onToggle: () => unknown;
  /** Удалять можно только то, у чего есть черновик (у него и живёт содержимое скрипта). */
  draftId: string | null;
  /** Название человека — заголовок шторки «Поделиться». */
  title: string;
  /** workflow на сервере → пункт «Запуски». */
  workflowId: string | null;
  /** Есть шаги → «Поделиться» и «Проверить» (гейты как на странице, Э2.4). */
  hasNodes: boolean;
  /** Черновик задеплоен — строгая подпись удаления «Удалить сценарий». */
  deployed: boolean;
  /** Адрес страницы цифр бота — пункт «Аналитика» (есть у любой карточки). */
  statsHref: string;
  /** Ключ ряда для РОДНОГО окна аналитики бота (Н38): пункт «Аналитика» обязан открывать
   *  ТО ЖЕ окно, что тап по включённой карточке, — второго пути к цифрам нет. null —
   *  окна нет (браузер, старая сборка), тогда идём адресом statsHref. */
  statsRow: string | null;
  /** Адрес редактора — пункт «Редактировать»; null у ряда без черновика (редактировать
   *  там нечего, такая карточка и раньше никуда не вела). */
  editHref: string | null;
  /** Действие запуска РАССЫЛКИ вместо тумблера бота; null — обычный бот. */
  bcAction?: 'send' | 'stop' | null;
  /** Карточка — РАССЫЛКА (у неё «Аналитика» в меню есть и на телефоне, кадр R06). */
  isBc?: boolean;
  /** Бот собран анкетой (мастерский) — окно дубликата спросит, оставить ли анкету. */
  wizard: boolean;
  /** Это ЦЕПОЧКА (владелец 16.09.2026, М1.6): у неё в меню нет «Создать дубликат» —
   *  копия черновика осталась бы без самой цепочки (bot_chains заводится сохранением
   *  анкеты), и человек получил бы мёртвую карточку. */
  chain?: boolean;
}

interface ScenariosData {
  state: 'none' | 'provisioning' | 'frozen' | 'ready';
  apiKeyValid: boolean;
  /** Подготовка сервера застряла (джоба умерла/висит дольше потолка) — честный текст (Ф28). */
  provisioningStuck?: boolean;
  drafts?: DraftItem[];
  /** ПОЛОСУ ПАПОК «БОТОВ» ЧЕЛОВЕК УБРАЛ (владелец 13.09.2026, П85): личный флаг юзера
   *  (users.bots_folders_hidden). Едет вместе со списком: экран и так за ним ходит,
   *  второго запроса ради булева не заводим. Нет поля — полоса показывается. */
  foldersHidden?: boolean;
  scenarios: Scenario[];
}


/** Аватар сценария перед названием: круг из ДВУХ сервисных иконок, разрезанный пополам —
 *  левый полукруг от одной, правый от другой (проба, владелец 2026-07-23; база не меняется,
 *  берём просто первые два подходящих сервиса). Показываем ТОЛЬКО логотипы сторонних
 *  сервисов: интеграции (stepKindOf === 'service') и ИИ-модели через наш доступ
 *  (stepKindOf === 'ai') СВОИМ логотипом бренда (доктрина SCRIPTERA-AI-DOCTRINE.md, значка
 *  Scriptera тут нет). Технику/формы/чат/медиа-движки не берём. Один сервис — целиком;
 *  ноль — ничего. */
/** Сервис-варианты для аватарки: только сервисы и ИИ-бренды схемы (дедуп по type+name). */
export function svcVariants(items?: ScenarioSvc[] | null): ScenarioSvc[] {
  const out: ScenarioSvc[] = [];
  const seen = new Set<string>();
  for (const s of items ?? []) {
    const kind = stepKindOf({ type: s.type });
    if (kind !== 'ai' && kind !== 'service') continue;
    const key = `${s.type}|${s.name ?? ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
    if (out.length >= 8) break;
  }
  return out;
}

/** Палитра заливки аватара (владелец 2026-07-24): кнопка-палитра перебирает тапом;
 *  null = фирменный цвет сервиса. Ключ цвета живёт в draft.logo суффиксом «~cN». */
export const LOGO_TINTS: Record<string, string> = {
  c1: 'var(--violet)',
  c2: 'var(--blue)',
  c3: 'var(--green)',
  c4: 'var(--amber)',
  c5: 'var(--accent)',
  c6: 'var(--step-media)',
  c7: 'var(--step-chat)',
  c8: 'var(--step-logic)',
};

/** draft.logo → выбор картинки (one:/svc: или null=авто) и заливки (css-цвет или null=фирменный). */
export function parseLogo(logo?: string | null): { icon: string | null; tintKey: string | null; tint: string | null } {
  if (!logo) return { icon: null, tintKey: null, tint: null };
  const m = /^(.*?)(?:~(c[1-9]))?$/.exec(logo);
  const key = m?.[2] ?? null;
  return { icon: m?.[1] || null, tintKey: key, tint: key ? (LOGO_TINTS[key] ?? null) : null };
}

/** Каналы, у которых у сценария есть ЛОГИН (телеграм-бот, инстаграм-аккаунт): только им
 *  рисуем значок канала в подстроке — он стоит вместо «собаки» перед логином. У формы и
 *  чата на сайте логина нет, и значок там не нужен (владелец 2026-08-02). */
/** Каналы, у которых логин вообще бывает: только у них веб рисует значок сети перед
 *  логином и снимает «@». Экспорт — им же живёт карточка «Аналитики» (это тот же
 *  ScnCardBody) и родные списки приложения: второго правила не заводим. */
export const CHAN_WITH_LOGIN = new Set(['telegram', 'instagram']);

/** Ключ выбора одного сервиса для draft.logo. */
export const svcLogoKey = (s: ScenarioSvc): string => `one:${s.type}|${s.name ?? ''}`;

/** Отметка сервисов (соло или «пополам»): size — сторона кружка (--duo), iconSize — логотипы. */
// ПРОБА (владелец 2026-07-23): плитка аватара — цветной фон (как иконки Настроек), логотип белым.
// Цвет псевдослучайный, но СТАБИЛЬНЫЙ по ключу (тип сервиса) — не мигает на ре-рендере.
const TILE_COLORS = [
  'var(--violet)', 'var(--blue)', 'var(--green)', 'var(--amber)', 'var(--accent)',
  'var(--step-media)', 'var(--step-chat)', 'var(--step-form)', 'var(--step-service)',
];
function tileColor(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return TILE_COLORS[h % TILE_COLORS.length]!;
}

export function SvcMark({
  items,
  size = 38,
  // Без кружка значок должен держать прежнюю зрительную массу (заказ владельца 2026-08-01).
  iconSize = 28,
  tint,
  iconType,
}: {
  items: ScenarioSvc[];
  size?: number;
  iconSize?: number;
  /** Заливка, выбранная владельцем сценария (палитра «О сценарии»); null — фирменный цвет. */
  tint?: string | null;
  /**
   * Показать значок ИМЕННО этого типа узла, минуя отбор по виду шага. Нужно каналу
   * сценария: узел запуска формы — вид «Событие», обычный отбор его отбросил бы, а
   * канал показать надо (владелец 2026-07-31).
   */
  iconType?: string | null;
}) {
  const map = useN8nIcons();
  const dark = useIsDark();
  // Фирменный цвет кружка из аудита значков — как в пикере (ICON-AUDIT); нет в карте → фолбэк
  // на стабильный по типу цвет. Владелец 2026-07-24: аватар сценария = свотч пикера 1:1.
  const brandColor = useN8nIconColor(iconType ?? items[0]?.type);
  const iconFor = (t: string) => {
    const ov = iconOverride(t); // своя подмена логотипа побеждает каталог (владелец 2026-07-22)
    if (ov) return ov;
    const e = map?.[t];
    return e ? `/service-icons/${dark && e.dark ? e.dark : e.file}` : null;
  };
  const ics: ReactNode[] = [];
  const forced = iconType ? iconFor(iconType) : null;
  if (forced) ics.push(<N8nIconImg key={`chan-${iconType}`} src={forced} size={iconSize} />);
  for (const s of items) {
    // Один логотип на аватар (владелец 2026-07-23: два нельзя) — берём первый подходящий сервис.
    if (ics.length >= 1) break;
    const kind = stepKindOf({ type: s.type });
    if (kind === 'ai') {
      // ИИ-шаг через наш доступ — логотип настоящего бренда (имя ноды = бренд), НИКОГДА значок Scriptera.
      ics.push(<BrandLogo key={`ai-${s.type}-${s.name ?? ics.length}`} brand={s.name ?? ''} size={iconSize} />);
    } else if (kind === 'service') {
      const src = iconFor(s.type);
      if (src) ics.push(<N8nIconImg key={`svc-${s.type}`} src={src} size={iconSize} />);
    }
  }
  const style = {
    // Фирменный цвет красит САМ значок (заказ владельца 2026-08-01), а не плитку под ним.
    color: tint ?? brandColor ?? tileColor(iconType ?? items[0]?.type ?? items[0]?.name ?? 'x'),
    ...(size !== 38 ? { '--duo': `${size}px` } : {}),
  } as React.CSSProperties;
  if (ics.length === 0) return null;
  if (ics.length === 1) {
    return (
      <span className="scn-duo" style={style} aria-hidden>
        <span className="scn-duo-solo">{ics[0]}</span>
      </span>
    );
  }
  return (
    <span className="scn-duo scn-duo-split" style={style} aria-hidden>
      <span className="scn-duo-half scn-duo-a">{ics[0]}</span>
      <span className="scn-duo-half scn-duo-b">{ics[1]}</span>
    </span>
  );
}

/** Стопка карточки с учётом ВЫБОРА аватарки (draft.logo): 'one:<type>|<name>' — выбранный
 *  сервис схемы соло; 'svc:<type>' — любой сервис каталога (владелец 2026-07-24);
 *  null/не найден — прежний авто-дуэт первых двух.
 *  В карточке списка НЕ используется с 2026-08-01 (крупный логотип канала владелец убрал),
 *  но нужен как готовый вид аватарки — вернём, когда решим, где ей стоять. */
function SvcDuo({
  items,
  logo,
  trigger,
}: {
  items?: ScenarioSvc[] | null;
  logo?: string | null;
  /** Тип узла ЗАПУСКА — по нему берётся канал сценария (тот же признак, что у группировки). */
  trigger?: string | null;
}) {
  const { icon, tint } = parseLogo(logo);
  let picked = items ?? [];
  if (icon?.startsWith('one:')) {
    const key = icon.slice(4);
    const hit = (items ?? []).find((s) => `${s.type}|${s.name ?? ''}` === key);
    if (hit) picked = [hit];
  } else if (icon?.startsWith('svc:')) {
    picked = [{ type: icon.slice(4), name: null }];
  } else if (trigger && chatStartChannelOf({ type: trigger })) {
    // ЗНАЧОК КАРТОЧКИ = КАНАЛ СЦЕНАРИЯ (владелец 2026-07-31: у телеграм-сценария висел
    // розовый значок «Контактов»). Раньше аватар брался из services — а это шаги схемы
    // БЕЗ запуска, то есть по факту второй шаг: чем бы сценарий ни начинался, в кружке
    // оказывался случайный сервис из середины. Канал — тот же признак, по которому список
    // уже группирует ряды. Свой выбор владельца (one:/svc:) остаётся главнее.
    return <SvcMark items={picked} tint={tint} iconSize={20} iconType={trigger} />;
  }
  return <SvcMark items={picked} tint={tint} iconSize={20} />;
}

/** Фото аккаунта в кружке карточки; нет фото или не отдалось — прежние значки схемы
 *  (владелец 2026-08-01: аватарка — лицо аккаунта, но дыры вместо неё быть не должно). */
function AccountPhoto({ src, children }: { src: string | null; children: ReactNode }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return <>{children}</>;
  // eslint-disable-next-line @next/next/no-img-element -- своя ручка, не next/image
  return (
    <img className="scn-card-ava-photo" src={src} alt="" width={44} height={44} decoding="async" loading="lazy" onError={() => setFailed(true)} />
  );
}


// ── Карточка скрипта: аватар и тело — ОБЩИЕ компоненты (владелец 2026-08-03) ─────────
// «Аналитика» рисует скрипты ТЕМИ ЖЕ карточками, что этот список (заказ владельца:
// дизайн заимствовать из «Скриптов», близнецов не заводить) — поэтому аватар и тело
// вынесены экспортируемыми компонентами, а не живут замыканиями Scenarios.

/** Аватар карточки: кружок 40 с фотографией аккаунта. Значка канала на нём НЕТ
 *  (владелец 2026-08-01: «с аватарки убери, плохо смотрится») — он переехал в нижнюю
 *  строку, к никнейму. */
export function ScnCardAvatar({
  trigger,
  services,
  logo,
  /** Фото аккаунта, от чьего имени работает скрипт (инстаграм-профиль или бот). */
  accountAvatar,
  /** Канал аккаунта (цепочка: по её отправителю) — главнее типа узла запуска. */
  channel,
  bcast = false,
}: {
  trigger?: string | null;
  services?: ScenarioSvc[] | null;
  logo?: string | null;
  accountAvatar?: string | null;
  channel?: string | null;
  /** КАРТОЧКА РАССЫЛКИ (ТЗ WEB-PARITY-3 Р13, кадр iOS R06; ТЗ WEB-CAUSES М44): кружок 44 —
   *  фото аккаунта, которым уходит рассылка; нет фото — серая заглушка с четырьмя квадратами
   *  в кружке. Один вид на 402/460/960/1280 (CSS .scn-card-bc без ограничения ширины); значок
   *  канала — у логина в подстроке, на аватаре его нет. */
  bcast?: boolean;
}) {
  const chan = channel ?? chanOf(trigger);
  const appTile = nativeChannelTile(chan);
  if (bcast) {
    return (
      <span className="scn-card-ava" aria-hidden>
        <AccountPhoto src={accountAvatar ?? null}>
          <span className="scn-ava-tile scn-ava-bc">
            <svg className="scn-ava-cat" width="18" height="18" viewBox="0 0 17.3 17.3" fill="none" stroke="currentColor" strokeWidth={1.7} aria-hidden>
              <rect x="0.85" y="0.85" width="6.6" height="6.6" rx="2.1" />
              <rect x="9.85" y="0.85" width="6.6" height="6.6" rx="2.1" />
              <rect x="0.85" y="9.85" width="6.6" height="6.6" rx="2.1" />
              <rect x="9.85" y="9.85" width="6.6" height="6.6" rx="2.1" />
            </svg>
          </span>
        </AccountPhoto>
      </span>
    );
  }
  return (
    <span className="scn-card-ava" aria-hidden>
      {/* ЛИЦО АККАУНТА главнее логотипов схемы (владелец 2026-08-01: «аватарка должна
          быть фото инстаграм-аккаунта или бота»). Фото отдаёт наша ручка; не отдала —
          ПЛИТКА КАНАЛА (владелец 2026-08-02, донос макета): мягкая цветная подложка
          со значком канала вместо голого значка. Свой выбор владельца (logo) главнее
          всего — его по-прежнему уважает SvcDuo. */}
      <AccountPhoto src={accountAvatar && !logo ? accountAvatar : null}>
        {logo ? (
          <SvcDuo items={services} logo={logo} trigger={trigger ?? null} />
        ) : (
          <span
            className={`scn-ava-tile scn-ava-${chan}${appTile.icon === 'QuickTile-catalog' ? ' scn-ava-other' : ''}`}
            style={{ '--app-tile': channelHex(chan) } as React.CSSProperties}
          >
            {/* Глиф 22 в плитке 44 — как у родной карточки (glyphSide; паритет 09.09.2026). */}
            <ChannelIcon channel={chan} size={22} />
            {/* ПЛИТКА КАК В ПРИЛОЖЕНИИ (ТЗ WEB-LOOK-CLAIM-1 Р4): приложению плитку выбирает
                nativeChannelTile — своя только у Telegram и Instagram, у прочих каталог
                (серая сетка). Тот же выбор здесь; показывает его CSS в браузере на телефоне. */}
            {appTile.icon === 'QuickTile-catalog' ? (
              <svg className="scn-ava-cat" width="18" height="18" viewBox="0 0 17.3 17.3" fill="none" stroke="currentColor" strokeWidth={1.7} aria-hidden>
                <rect x="0.85" y="0.85" width="6.6" height="6.6" rx="2.1" />
                <rect x="9.85" y="0.85" width="6.6" height="6.6" rx="2.1" />
                <rect x="0.85" y="9.85" width="6.6" height="6.6" rx="2.1" />
                <rect x="9.85" y="9.85" width="6.6" height="6.6" rx="2.1" />
              </svg>
            ) : null}
          </span>
        )}
      </AccountPhoto>
    </span>
  );
}

/** ЛИНИЯ ОТКРЫТИЙ ПО ДНЯМ на карточке бота (владелец 10.09.2026, BACKLOG «обход iOS
 *  10.09» п.16б): не столбики, а ровная линия, у самой верхней и самой нижней точки
 *  подписаны значения. Точки — в порядке рисования (старые → сегодня). Открытий не
 *  было — линии нет вовсе (честное пустое состояние, правило 2026-08-03). Цвет линии —
 *  роль «нарисованное» (--accent-stroke), подписи — вторичный серый. Та же линия на
 *  родной карточке приложения (BotsScreen.swift, `BotsLineView`). */
/** ЛИНИЯ КАРТОЧКИ (владелец 10.09.2026 п.16б; 12.09.2026: «добавь на график подарки и
 *  цифры на график»): ровная кривая запусков по дням с подписями верхней и нижней точки,
 *  под ней заливка от акцента к прозрачному; ВТОРАЯ линия — подарки по дням, зелёной
 *  ролью, с подписью своего максимума над её верхней точкой. Обе линии — в одной шкале
 *  по общему максимуму, чтобы «сколько из запусков стало подарками» читалось глазом.
 *  Тот же вид у родной карточки (BotsLineView). */
export function CardLine({
  points,
  hi,
  lo,
  points2,
  color2,
  hi2,
  points3,
  color3,
  color,
  compact,
  fixedMax,
  fixedMax2,
}: {
  points: number[];
  hi: string;
  lo: string;
  points2?: number[];
  /** Цвет второй линии; не задан — цвет из стилей (`scn-card-line__l2`). */
  color2?: string;
  hi2?: string;
  /** Потолок оси ВТОРОЙ линии (100 у конверсии, П94); пусто — общая шкала. */
  fixedMax2?: number;
  /** Цвет линии запусков — цвет сети бота (12.09.2026); пусто — роль «нарисованное». */
  color?: string;
  /** Компактная высота (П77: два графика на карточке один под другим). */
  compact?: boolean;
  /** Жёсткий потолок оси (100 у конверсии); пусто — по точкам. */
  fixedMax?: number;
  /** ТРЕТЬЯ ЛИНИЯ — конверсия по своей оси 0…100 (П119, владелец 14.09.2026: на карточке
   *  показываются те линии, которые человек выбрал в «О боте»). Пусто — линии нет, и
   *  карточка выглядит ровно как до правки. */
  points3?: number[];
  color3?: string;
}) {
  const n = points.length;
  if (n < 2) return null;
  const has2 = !!points2 && points2.length === n && Math.max(...points2) > 0;
  // Третья линия живёт СВОЕЙ осью 0…100 (проценты), в общую шкалу людей не входит.
  const has3 = !!points3 && points3.length === n;
  const own2 = has2 && fixedMax2 !== undefined;
  const all = has2 && !own2 ? [...points, ...points2!] : points;
  const max = fixedMax ?? Math.max(...all);
  const min = fixedMax !== undefined ? 0 : Math.min(...all);
  if (max <= 0) return null;
  const x = (i: number) => (i / (n - 1)) * 100;
  const y = (v: number) => (max === min ? 50 : 100 - ((v - min) / (max - min)) * 100);
  // Вторая линия по своей оси 0…fixedMax2 (П94); без потолка — общая шкала, как было.
  const y2 = (v: number) => (own2 ? 100 - (Math.min(v, fixedMax2!) / fixedMax2!) * 100 : y(v));
  // Монотонная кривая (П97): без провалов ниже нуля и горбов над потолком.
  const curve = (pts: number[], yy: (v: number) => number) => monotonePath(pts.map((v, i) => [x(i), yy(v)] as const), 2);
  const d1 = curve(points, y);
  const hiAt = points.indexOf(Math.max(...points));
  const loAt = points.lastIndexOf(Math.min(...points));
  const plotTop = compact ? 8 : 12;
  const plotH = compact ? 20 : 26;
  const top = (v: number) => `calc(${plotTop}px + ${(y(v) / 100) * plotH}px)`;
  const gid = `scnLineFill${n}${compact ? 'c' : ''}${fixedMax ?? ''}`;
  return (
    <div
      className={`scn-card-line${compact ? ' compact' : ''}${has2 ? ' two' : ''}`}
      aria-hidden
      style={color ? { color } : undefined}
    >
      <svg viewBox="0 0 100 100" preserveAspectRatio="none">
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            {/* Мягкая заливка (APP-CLAIMS-1020 П3 п.2): α 0,14 вверху → 0 внизу. */}
            <stop offset="0" stopColor="currentColor" stopOpacity={0.14} />
            <stop offset="1" stopColor="currentColor" stopOpacity={0} />
          </linearGradient>
        </defs>
        <path d={`${d1} L100 100 L0 100 Z`} fill={`url(#${gid})`} stroke="none" />
        <path d={d1} fill="none" stroke="currentColor" strokeWidth={1.5} vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
        {has2 ? (
          // Вторая линия тоньше и тише первой (APP-CLAIMS-1020 П3 п.1): 1,25 и прозрачность 0,55, роль та же.
          <path
            d={curve(points2!, y2)}
            className="scn-card-line__l2"
            fill="none"
            stroke={color2}
            strokeWidth={1.25}
            strokeOpacity={0.55}
            vectorEffect="non-scaling-stroke"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ) : null}
        {has3 ? (
          <path
            d={curve(points3!, (v) => 100 - Math.max(0, Math.min(100, v)))}
            fill="none"
            stroke={color3 ?? 'currentColor'}
            strokeWidth={1.5}
            vectorEffect="non-scaling-stroke"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ) : null}
      </svg>
      {/* ТОЧЕК ВЕРХА И НИЗА НА ЛИНИИ НЕТ (владелец 14.09.2026: «не очевидно, что точка стоит
          на максимуме и минимуме — лучше убрать вообще»): линия читается сама, а кружки без
          подписи объясняли себя только знающему. Тем же правилом снята точка второй линии. */}
      {/* ЦИФР ШКАЛЫ НА КАРТОЧКЕ НЕТ (владелец 14.09.2026: «11 стоит так, что непонятно, к чему
          относится») — подписи рисуются только когда веб их прислал (пустые — не рисуем). */}
      {hi ? <span className="scn-card-line__lbl hi" style={{ left: `${x(hiAt)}%` }}>{hi}</span> : null}
      {lo && Math.max(...points) !== Math.min(...points) ? (
        <span className="scn-card-line__lbl lo" style={{ left: `${x(loAt)}%` }}>{lo}</span>
      ) : null}
      {has2 && hi2 ? (
        (() => {
          const at2 = points2!.indexOf(Math.max(...points2!));
          return (
            <span className="scn-card-line__lbl l2" style={{ left: `${x(at2)}%`, top: top(points2![at2]!) }}>{hi2}</span>
          );
        })()
      ) : null}
    </div>
  );
}

/** Тело карточки: название (со значком сети справа) · описание · строка категории · две
 *  цифры · подвал-состояние (статус, чип сбоев за сутки). Дат в карточке НЕТ
 *  (владелец 2026-08-03).
 *
 *  СТРОКИ «значок сети · @аккаунт» НЕТ У КАРТОЧЕК БОТОВ (владелец 13.09.2026, П78:
 *  «строку с никнеймом убирать на странице „Боты“, у списка ботов») — сеть переехала
 *  значком к названию. КАРТОЧКИ РАССЫЛОК владелец трогать не просил, и они остались
 *  прежними: у них строка с логином на месте, значка сети у названия нет. Кто есть кто —
 *  говорит `bcast`. */
export function ScnCardBody(o: {
  title: string;
  desc?: string | null;
  trigger?: string | null;
  /** Канал аккаунта бота (цепочка: по её отправителю) — главнее типа узла запуска. Тот же
   *  расчёт, что у родной карточки приложения (`draft.accountChannel ?? chanOf(trigger)`):
   *  без него у цепочки на сайте не было значка сети после названия. */
  channel?: string | null;
  /** Логин аккаунта. У КАРТОЧКИ БОТА ОН БОЛЬШЕ НЕ РИСУЕТСЯ (владелец 13.09.2026, П78:
   *  «аккаунт понятен по аватарке»); у карточки рассылки (`bcast`) остаётся, как был. */
  account?: string | null;
  /** ЭТО КАРТОЧКА РАССЫЛКИ, а не бота. Заказ П78 (владелец 13.09.2026) про список «Боты»
   *  и только про него: у рассылок строка «значок сети · @аккаунт» остаётся на месте, а
   *  значка сети у названия не появляется. */
  bcast?: boolean;
  /** Бот выключен — рядом со значком сети встаёт значок «выключен» (П119). */
  off?: boolean;
  category?: string | null;
  /** Скрипт рождён мастером-анкетой — метка «быстрая настройка» в подстроке
   *  (владелец 2026-08-05); открывается он режимом мастера, не схемой. */
  wizard?: boolean;
  extra?: ReactNode;
  /** Статус подвала карточки. Владелец 2026-08-04: статусов ДВА — «Включено» и
   *  «Выключено», «черновика» для человека не существует (draft здесь = выключено,
   *  значение оставлено ради «Аналитики», которая различает их внутри). */
  st?: 'run' | 'off' | 'draft';
  /** ПОДПИСЬ И ТОН чипа, заданные вызывающим: у РАССЫЛКИ состояний пять (черновик,
   *  запланирована, отправляется, готово, остановлена), и слова считает её словарь.
   *  Задано — общий расчёт «Включено/Выключено» не применяется; сам чип и его вид —
   *  те же (.scn-stchip), второй пилюли в карточках нет. */
  stLabel?: string;
  stTone?: 'run' | 'wait' | 'off';
  /** Тап по чипу статуса — включить/выключить (список «Скриптов» зовёт шторку
   *  подтверждения). Нет обработчика — чип статичен (списки «Аналитики»). */
  onSt?: () => void;
  /** Переключение в полёте: чип пишет «включаем…/выключаем…» и не тапается. */
  stBusy?: boolean;
  /** Включение не прошло (Г2 BUILDER-INSTANT): чип пишет «Не включился», тап по нему
   *  открывает окно с причиной — полос ошибок над списком больше нет. Вид чипа — тот же
   *  «выключенный» (бот и правда не работает), меняется только подпись. */
  stFail?: boolean;
  /** Собрано контактов — людей, дошедших до шага «Записать в карточку» (считает ручка
   *  списка ботов, поле people). ОДНА ИЗ ДВУХ ЦИФР КАРТОЧКИ (владелец 2026-08-18:
   *  «на карточке бота две метрики — конверсия и количество собранных контактов»);
   *  «открытий за неделю» на карточке больше нет, третья цифра из этого заказа выпала. */
  contacts?: number;
  /** Конверсия — доля дошедших до записи контакта от вошедших в бота, 0–100. Считается
   *  там же, где контакты (ручка списка), и той же формулой, что показывает «Аналитика»:
   *  двух разных конверсий в продукте нет. */
  conv?: number | null;
  /** Место под мелкий график («Аналитика», владелец 2026-08-03): между нижней строкой
   *  и подвалом. В списке «Скриптов» пусто — графика в карточке нет (решение 2026-08-02). */
  chart?: ReactNode;
  /** Очередь ответов Инстаграма (SCALE-PLAN Б5) — подпись под названием, обычным
   *  вторичным текстом. Пусто — строки нет. */
  igQueue?: IgQueueInfo | null;
}) {
  const t = useTranslations('cabinet.automations');
  return (
    <>
      {/* Название — В СВОЁМ span (владелец 2026-08-18: «обрез многоточием на всех
          ширинах»). Строка названия — флекс (рядом живут значки .extra), а в флексе
          многоточие голому тексту не достаётся: длинное имя резалось на полубукве.
          Обрезает теперь сам span, поле под «⋯» держит .scn-card-name. */}
      <div className="scn-card-name">
        <span className="scn-card-t">{o.title}</span>
        {o.extra}
        {/* ЗНАЧОК СЕТИ — СПРАВА ОТ НАЗВАНИЯ, ПОСЛЕ ЗНАЧКОВ-ССЫЛОК (владелец 13.09.2026,
            П78: «значок сети — справа от названия у всех ботов»). Аккаунт человек узнаёт
            по аватарке карточки, поэтому логин со своей строкой у бота убран целиком.
            Значок рисуем только у НАСТОЯЩИХ сетей (телеграм, инстаграм) — у формы и чата
            на сайте сети нет, и серая плитка каталога там была бы шумом. У РАССЫЛОК
            значка нет: их карточки заказ не трогает. */}
        {!o.bcast && CHAN_WITH_LOGIN.has(o.channel ?? chanOf(o.trigger)) ? (
          <span className="scn-name-chan" style={{ '--chan-c': channelHex(o.channel ?? chanOf(o.trigger)) } as React.CSSProperties} aria-hidden>
            <ChannelIcon channel={o.channel ?? chanOf(o.trigger)} size={14} />
          </span>
        ) : null}
        {/* ВЫКЛЮЧЕННЫЙ БОТ — ЗНАЧКОМ РЯДОМ СО ЗНАЧКОМ СЕТИ (владелец 14.09.2026: «„Включено“
            уберём, а у выключенных рядом со значком сети сделаем значок, что выключен»).
            У включённого рядом не стоит ничего — обычная жизнь бота молчалива. */}
        {!o.bcast && o.off ? (
          <span className="scn-name-off" title={t('stOff')} aria-label={t('stOff')}>
            <IconPower width={13} height={13} />
          </span>
        ) : null}
      </div>
      {o.desc ? <div className="scn-card-desc">{o.desc}</div> : null}
      {/* ОЧЕРЕДЬ ОТВЕТОВ ИНСТАГРАМА (Б5): ответы на комментарии уходят по порядку — это
          штатная жизнь бота, поэтому подпись обычная, без сигнальных цветов. Строка одна
          на весь кабинет (IgQueueNote), у «Подключений» она же. */}
      <IgQueueNote queue={o.igQueue} />
      {/* Подстрока «КТО ЭТОТ БОТ» — категория своим цветом (владелец 2026-08-01), а у
          РАССЫЛКИ следом ещё и «значок сети · @аккаунт», как было всегда. У БОТА этой
          строки с логином больше нет (владелец 13.09.2026, П78): сеть переехала значком к
          названию, а имя аккаунта человеку и так видно по аватарке карточки.
          МЕТКИ МАСТЕРА («быстрая настройка») тут нет с 06.09.2026: поле `wizard`
          по-прежнему решает, как бот открывается, а в подстроке его нет. */}
      {/* НАЗВАНИЯ ПАПКИ НА КАРТОЧКЕ НЕТ (владелец 14.09.2026: «почему ты принял решение
          выводить название категории — убирай»). Папка живёт в полосе над списком и в
          «О боте»; на самой карточке её имени не бывает. */}
      {/* СТРОКИ «✦ быстрая настройка · @логин» У РАССЫЛКИ НЕТ (APP-CLAIMS-1020 ред. 6, П17/П26.3): карточка рассылки —
          та же разметка, что у бота (название, описание до 3 строк, низ — статус и цифры отправки). */}
      {o.chart ?? null}
      {/* ДВЕ ЦИФРЫ БОТА — СВОЕЙ СТРОКОЙ (владелец 2026-08-18: «на карточке бота две
          метрики — конверсия и количество собранных контактов»). Строка своя, потому что
          в подвале рядом с чипом и логином пара переносилась и рвалась пополам.
          И обе цифры показываются ВСЕГДА: пока их прятали на нуле, карточка нового бота
          молчала и человек не знал, что она вообще считает. Ноль — честный ответ «пока
          никого», оценок «мало/много» рядом нет. Конверсия без движения — 0%. */}
      {/* Две цифры карточки живут в легендах линий (П77, 13.09.2026): отдельной строки нет. */}
      {/* Подвал (владелец 2026-08-04, отмена решения 03.08 «включённый ничем не помечен»):
          ЧЁТКИЙ статус у КАЖДОЙ карточки — «Включено» или «Выключено», других статусов
          нет (черновик = выключено). Чип с обработчиком — кнопка вкл/выкл (шторка
          подтверждения), тап не проваливается в карточку-ссылку. Подвал — ТОЛЬКО строка
          состояния (владелец 2026-08-20): статус и, если бот сбоил, чип сбоев; логин
          аккаунта ушёл отсюда в подстроку. */}
      {o.st ? (
        <div className="scn-card-foot">
          {o.st
            ? (() => {
                const on = o.st === 'run' && !o.stFail;
                const label = o.stLabel
                  ? o.stLabel
                  : o.stBusy
                    ? t(on ? 'chipBusyOff' : 'chipBusyOn')
                    : o.stFail
                      ? t('stFailed')
                      : t(on ? 'stRun' : 'stOff');
                const tone = o.stLabel ? (o.stTone ?? 'off') : o.stFail ? 'err' : on ? 'run' : 'off';
                // ПЕРЕКЛЮЧАТЕЛЯ НА КАРТОЧКЕ НЕТ (владелец 14.09.2026): включение и выключение —
                // только пунктом меню «⋯». Здесь — слово состояния (у бота оно стоит в нижней
                // строке cardWide, сюда попадает только рассылка).
                return (
                  <span className={`scn-card-state scn-card-state-${tone}`}>
                    <i className="scn-card-state__dot" aria-hidden />
                    {label}
                  </span>
                );
              })()
            : null}
          {/* СБОИ КЛИЕНТУ НЕ ПОКАЗЫВАЕМ (владелец 19.09.2026, запрет №7): упавшие прогоны —
              техошибка платформы, она уходит владельцу в TG через центр ошибок. Чип
              «Сбои: N за сутки» (2026-08-20) снят. */}
        </div>
      ) : null}
    </>
  );
}

// Читаемое имя триггера для заголовка пузыря-группы (владелец 2026-07-23: сценарии группируем
// по триггеру, как модели по компаниям). Служебные триггеры — свои подписи в двух языках (в
// клиентских текстах без «вебхук/нода»); сервисный триггер (telegramTrigger…) — имя сервиса.
const TRIGGER_LABELS = {
  schedule: { ru: 'По расписанию', en: 'On schedule' },
  event: { ru: 'По событию', en: 'On event' },
  manual: { ru: 'Вручную', en: 'Manually' },
  form: { ru: 'Форма', en: 'Form' },
  chat: { ru: 'Чат', en: 'Chat' },
  error: { ru: 'При ошибке', en: 'On error' },
} as const;
function triggerLabel(type: string | null | undefined, locale: string): string {
  const en = locale.startsWith('en');
  const L = (k: keyof typeof TRIGGER_LABELS) => (en ? TRIGGER_LABELS[k].en : TRIGGER_LABELS[k].ru);
  if (!type) return en ? 'Other' : 'Другое';
  const low = type.toLowerCase();
  if (low.includes('scheduletrigger') || low.includes('cron') || low.includes('interval')) return L('schedule');
  if (low.includes('manualtrigger')) return L('manual');
  // Наши точки входа мини-аппов (§15/§16) называются не по-n8n'овски: без этой строки
  // сценарий из шаблона «Форма заявки на сайте» стоял в списке под заголовком
  // «ScripteraForm» — служебное имя узла вместо канала (Р8, сквозная приёмка).
  if (low.includes('scripteraform') || low.includes('scripterascreen')) return L('form');
  if (low.includes('formtrigger')) return L('form');
  if (low.includes('chattrigger')) return L('chat');
  if (low.includes('errortrigger')) return L('error');
  if (low.includes('webhook') || low.includes('respondto')) return L('event');
  // сервисный триггер: имя сервиса из типа ноды (бренд — показывать можно).
  const seg = (type.split('.').pop() ?? type).replace(/[Tt]rigger.*$/, '');
  return seg ? seg.charAt(0).toUpperCase() + seg.slice(1) : (en ? 'Event' : 'Событие');
}

/** Канал по типу узла-триггера — для ЗНАЧКА КАНАЛА в ряду (владелец 2026-08-01).
 *  Один словарь на проект не заводим: значок берёт тот же ChannelIcon, что каталог. */
export function chanOf(type: string | null | undefined): string {
  const low = (type ?? '').toLowerCase();
  if (low.includes('telegram')) return 'telegram';
  if (low.includes('facebook') || low.includes('instagram')) return 'instagram';
  if (low.includes('vk')) return 'vkontakte';
  if (low.includes('max')) return 'max';
  if (low.includes('scripteraform') || low.includes('formtrigger')) return 'form';
  if (low.includes('chattrigger') || low.includes('scripterascreen')) return 'chat';
  return 'chat';
}

// Мини-иконки формы/чата у названия (мини-апп сценария после деплоя, §15/§16).
const FormSvg = () => (
  <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="4" y="3.5" width="16" height="17" rx="2" />
    <path d="M7.5 8h9" />
    <rect x="7.5" y="11" width="9" height="3" rx="1" />
    <path d="M9.5 17.5h5" />
  </svg>
);
const ChatSvg = () => (
  <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M21 11.5a8.5 8.5 0 0 1-8.5 8.5 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 8.5-8.5 8.5 8.5 0 0 1 8.5 8.5z" />
    <path d="M8.5 11.5h.01M12.5 11.5h.01M16.5 11.5h.01" />
  </svg>
);
// Значок-предупреждение «ссылку в Telegram никто не встречает» (UT 14.10 п.10) — тот же
// треугольник, что SF-символ exclamationmark.triangle у родной карточки.
const WarnSvg = () => (
  <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M10.3 4.2 2.6 17.5A2 2 0 0 0 4.3 20.5h15.4a2 2 0 0 0 1.7-3L13.7 4.2a2 2 0 0 0-3.4 0z" />
    <path d="M12 9.5v4.2M12 17h.01" />
  </svg>
);
// Значок «схема» для чипа создания (тот же глиф, что в плашке «Добавить»).
const SchemaSvg = () => (
  <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="3" width="8" height="8" rx="2" />
    <rect x="13" y="13" width="8" height="8" rx="2" />
    <path d="M7 11v3a2 2 0 0 0 2 2h4" />
  </svg>
);
// Значок «из шаблона» — те же слои, что у строки в чузере «+».
const TplSvg = () => (
  <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="m12 3 9 5-9 5-9-5 9-5z" />
    <path d="m3 13 9 5 9-5" />
  </svg>
);

// ── СЛОВА РАССЫЛКИ (один из трёх ифов: «рассылка» вместо «бота») ─────────────────────

/** Статус рассылки словами + тон пилюли — теми же чипами карточки, что «Включено/
 *  Выключено» у бота (.scn-stchip): второго вида пилюли в списке нет. */
function useStatusChip(): (status: BroadcastStatus) => { label: string; tone: 'run' | 'wait' | 'off' } {
  const t = useTranslations('cabinet.broadcasts');
  return (status) => {
    switch (status) {
      case 'sending':
        return { label: t('stSending2'), tone: 'wait' };
      case 'scheduled':
        return { label: t('stScheduled'), tone: 'wait' };
      case 'done':
        // APP-CLAIMS-1020 ред. 7, Р3: у отправленной статуса нет (ни «Готово», ни точки) — дату и цифры несёт строка цифр.
        return { label: '', tone: 'run' };
      case 'stopped':
        return { label: t('stStopped'), tone: 'off' };
      default:
        // APP-CLAIMS-1020 ред. 6, П16: сохранённая и не отправленная — «Не отправлена» (было «Черновик»).
        return { label: t('statusNotSent'), tone: 'off' };
    }
  };
}

/** Состояния рассылки для отбора «Состояние» — тот же порядок, что у статусов ручки. */
const BCAST_STATES: BroadcastStatus[] = ['draft', 'scheduled', 'sending', 'done', 'stopped'];

export function Scenarios({
  kind = 'bots',
  pick = null,
  onPick,
  chatNonce = 0,
  channel = '',
  account = '',
  state = '',
  q = '',
  onFilter,
  onCreate,
  tail,
  archive = [],
}: {
  /** Какую половину общего списка показываем: «Боты» или «Рассылки» (canon: один и тот
   *  же код ведёт обоих). */
  kind?: ListKind;
  /** Создание раздела «Рассылки» — герой пустого экрана и плавающий «+» зовут его
   *  (сам флоу живёт в витрине рассылок: выпадашка путей «Мастером/Схемой» у кнопки).
   *  Событие тапа передаётся дальше: по нему выпадашка встаёт У КНОПКИ. */
  onCreate?: (e?: React.MouseEvent<HTMLElement>) => void;
  /** Хвост под списком (архив старого журнала рассылок) — витрина отдаёт готовым. */
  tail?: ReactNode;
  /** ТОТ ЖЕ архив ГОТОВЫМИ строками для родного экрана приложения: разметку веб-хвоста
   *  натив не читает, а второй правды у архива нет — обе половины собирает витрина. */
  archive?: NativeBcastArchiveCard[];
  /** Выбор во второй колонке десктопа (владелец 2026-08-01). null — колонки нет,
   *  раздел выглядит как раньше: остров создания сверху и весь список под ним. */
  pick?: string | null;
  onPick?: (p: import('./split-menu').ScriptsPick) => void;
  /** Счётчик «новый разговор»: меняется на каждый выбор «С ассистентом» — чат в правом
   *  поле начинается с чистого листа (владелец 2026-08-01). */
  chatNonce?: number;
  /** Фильтры «Моих скриптов» из адреса: канал (подпись запуска), аккаунт (@бот) и
   *  статус (?state=on|off — «Включено/Выключено», владелец 2026-08-04). */
  channel?: string;
  account?: string;
  state?: string;
  /** Строка поиска (мобильная шапка и родной поиск приложения). */
  q?: string;
  onFilter?: (kind: 'channel' | 'account' | 'state', value: string) => void;
} = {}) {
  const t = useTranslations('cabinet.automations');
  // ИФ №2 (слова): у рассылки свой словарь — имя раздела, состояния, «Отправить».
  // Всё остальное (пункты меню, цифры, ошибки) берётся из общих ключей ботов.
  const tb = useTranslations('cabinet.broadcasts');
  // Слово вида вложения в превью карточки рассылки (Р3 ред. 7) — словарь переписки (`KIND_KEY`).
  const tm = useTranslations('cabinet.messages');
  const tBill = useTranslations('cabinet.billing');
  const proLock = useProLocked();
  const tsec = useTranslations('cabinet.sections');
  const bcast = kind === 'broadcasts';
  const locale = useLocale();
  // ПРИМЕР «КАК ВСЁ РАБОТАЕТ» ГОТОВ ДО ТОГО, КАК ЕГО ОТКРОЮТ (владелец 02.09.2026, Н16; О5.9-В): портрет клиента и фото
  // сцены всех его аккаунтов — цели `niche` и `scene.photos` плана прогрева (У1 на входе в кабинет, ТЗ HELP-WARM-FIX §5).
  // Здесь ниша нужна только для адресов сцен правки.
  const { data: nicheData } = useNichePersonal();
  // Свои категории юзера — по ним имя категории из адреса (слаг) превращается в имя.
  const { favs: cats } = useFavorites('scriptCategories');
  // Тексты карточек-пульса (последний запуск, за неделю, подсказки столбиков) — прежние
  // ключи «Обзора»: карточки переехали сюда вместе с разделом (владелец 2026-07-21).
  const to = useTranslations('cabinet.overview');
  const tc = useTranslations('cabinet'); // «Точно удалить?» — общий словарь кабинета
  // Подписи пунктов меню «⋯» карточки — словарь страницы сценария: пункты ОБЩИЕ
  // (scenarioMenuItems, Э2 BOTS-MENU-NOTIFY), вторых ключей не заводим.
  const ts = useTranslations('cabinet.scenario');
  // Открытие сценария в приложении — родным боковым выездом (ЕДИНЫЙ WebView, без
  // перезагрузки); в браузере/старых сборках Link уходит обычной навигацией.
  const slideOpen = useSlideOpen();
  // Страница бота (цифры) открывается ТЕМ ЖЕ способом, что сам бот, — вторую механику
  // перехода не заводим (ТЗ 4 CABINET-UX-FIXLIST): отличается только признак «новый
  // экран нарисован» (у страницы бота нет фокус-режима, ждать .cab-focus нечего).
  const readyOf = (href: string) => (href.endsWith('/stats') ? botStatsReady : undefined);
  // МАСТЕРСКИЙ БОТ ОТКРЫВАЕТСЯ СВОЕЙ ШТОРКОЙ ПРЯМО ЗДЕСЬ (владелец 30.08.2026, П-23:
  // «при типе мастер конструктор показывать запрещено вообще»). Страницу бота слоем для
  // него не открываем совсем: слой въезжает вбок, стоит под шторкой пустым (форму рисует
  // натив) и остаётся на экране, когда шторку закрыли, — это и есть тот «конструктор».
  // Сборка родного мастера не умеет — прежний переход на страницу, один в один.
  const openWizHere = (id: string | null, href: string): boolean => {
    if (!id) return false;
    if (!nativeMethod('wizSupported')) return openWizSheetWeb(id, href);
    openWizardBot(id, () => {
      if (!slideOpen(href, readyOf(href))) router.push(href);
    });
    return true;
  };
  // ПРАВКА МАСТЕРСКОГО БОТА В ВЕБЕ — та же шторка, что создание, на любой ширине
  // (useWizEditSheet, wizard-host.tsx; паритет 09.09.2026). В приложении сюда не приходим —
  // выше стоит родной мастер.
  const wizEditSheet = useWizEditSheet();
  const openWizSheetWeb = (id: string, href: string): boolean =>
    wizEditSheet(
      id,
      () => {
        if (!slideOpen(href, readyOf(href))) router.push(href);
      },
      // ✓ ПРАВКИ РАССЫЛКИ — СПИСОК «РАССЫЛКИ» (APP-CLAIMS-1020 ред. 6, П16/П26.2), не «Боты» и не окно рассылки.
      bcast ? { onSaved: () => void closeBotWindowThen(() => router.push(sectionUrl('broadcasts', ''))) } : undefined,
    );
  const openScn = (href: string, wizId?: string | null) => (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (openWizHere(wizId ?? null, href)) {
      e.preventDefault();
      return;
    }
    if (slideOpen(href, readyOf(href))) e.preventDefault();
  };
  // То же самое пунктом меню «⋯»: ссылки под пальцем нет, навигацию делаем сами.
  const goScn = (href: string, wizId?: string | null) => {
    if (openWizHere(wizId ?? null, href)) return;
    if (!slideOpen(href, readyOf(href))) router.push(href);
  };
  // Адрес страницы цифр бота — из общего правила «выбор ↔ путь» (split-menu), своего
  // формата адреса тут не пишем.
  // ИСКЛЮЧЕНИЕ — РАССЫЛКА НА ШИРОКОМ ЭКРАНЕ (Д1.1, Закон 5 п.4): у «Рассылок» свой раздел, и окно
  // рассылки живёт по его адресу /app/broadcasts/<id> (поле в трёх слоях, панель 460 в двух).
  // Телефон и приложение — прежний общий адрес окна бота.
  // (Ширина и среда читаются в момент вызова: их хуки объявлены ниже, а зовут руку карточки позже.)
  const statsHref = (id: string) =>
    bcast && BROADCASTS_WIDE_SECTION && !isApp && layoutMode !== 'phone'
      ? `/app/broadcasts/${id}`
      : scriptsPathFor(`stats:${id}`);
  // Адрес бота — один на кабинет (useScnHref, split-menu): страница статистики ведёт в
  // редактор ровно тем же адресом, что карточка списка. Мастерский бот по клику
  // открывается шторкой (openWizHere), адрес — запасной путь и прямая ссылка.
  const scnHref = useScnHref();
  // Значок формы/чата стоит ПЕРЕД названием (владелец 2026-07-21), т.е. ВНУТРИ
  // Link-карточки — вложенный <a> невалиден (SSR его разрезает), открываем руками.
  // window.open с _blank в приложении идёт тем же путём WebView, что target="_blank".
  // ССЫЛКА КАРТОЧКИ («Открыть форму», «Открыть чат») — ОДНА РУКА НА ВСЕ ВХОДЫ (Н38,
  // владелец 02.09.2026: «Открыть форму / Открыть чат — стопка»): в приложении мини-апп
  // поднимается родным листом-стопкой (openWebSheet), в браузере и на старых сборках —
  // прежним новым окном. Второго способа открытия ссылки в разделе нет.
  const openLink = (url: string) => {
    if (openWebSheet(url, themeAttr)) return;
    window.open(url, '_blank', 'noopener');
  };
  const openExt = (url: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openLink(url);
  };
  // Родное меню «у точки» для «⋯» карточки (Э5 BOTS-MENU-NOTIFY): умеет ли сборка —
  // спрашивает общий хук (тот же, которым живёт выпадашка путей создания).
  const pointMenuOk = usePointMenuOk();
  // Тап по меню карточки не должен проваливаться в саму карточку-ссылку.
  const stopCard = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  /** «Спросить об удалении» каждой карточки — его отдаёт скрытый ConfirmInline. */
  const delAskRef = useRef<Record<string, (() => void) | undefined>>({});
  // ПОДТВЕРЖДЕНИЕ ВЫКЛЮЧЕНИЯ (владелец 15.09.2026: «при выключении бота предупреждать
  // модалкой»): вопрос задаёт `confirmOff` ниже — в приложении родное окно iOS, на сайте
  // окно-вопрос кабинета (askConfirm), без привязки к карточке (О8). Включение — без
  // вопроса, как было.
  /** Карточки родного списка ПОСЛЕДНЕГО рендера: окно бота живёт дольше рендера, и его
   *  тумблер (П158 п.2) обязан звать руку по СВЕЖЕМУ состоянию бота, а не по снимку на
   *  момент открытия окна. */
  const cardsRef = useRef<Map<string, { menu: CardMenuOpts }>>(new Map());
  /** Рука тумблера страницы бота (П158 п.2) — та же `onToggle`, что у «⋯» карточки. Ряд
   *  ищется по id карточки, а не нашёлся (черновик стал запущенным ботом) — по черновику. */
  const windowToggle = (id: string, draftId: string | null) => (): unknown => {
    const c =
      cardsRef.current.get(id) ??
      [...cardsRef.current.values()].find((x) => !!draftId && x.menu.draftId === draftId);
    if (!c || c.menu.busy) return undefined;
    return c.menu.onToggle();
  };
  // Ответ — промис: кончается, когда человек ответил и (при «да») выключение прошло. Его
  // ждёт тумблер страницы бота (П158 п.2), чтобы вернуться на место после «Отмены».
  // ВОПРОС НЕ ПРИВЯЗАН К КАРТОЧКЕ (ТЗ WEB-CAUSES О8, TODO 367): прежде браузер спрашивал
  // скрытым ConfirmInline ВНУТРИ карточки списка; нет карточки в разметке (окно по адресу,
  // другая папка, длинный список staging) — вопроса не было, и `run()` шёл сразу. Теперь один
  // путь на карточку, окно бота и «⋯» на любой ширине: приложение — родное окно (как было),
  // браузер — `askConfirm` (KDialog по центру, кадр iOS B08-botwin-toggle-off-ask). После
  // `run()` — свежая выгрузка списка; не выключился — строка в журнал клиента
  // (`bot.off.fail`), человеку отказ уже показала рука тумблера (openActionFail).
  const confirmOff = (id: string, run: () => unknown): Promise<void> => {
    const opts = {
      question: t('offSure'),
      confirmLabel: t('stTurnOff'),
      cancelLabel: tc('cancel'),
      destructive: true,
    };
    const native = hasNativeConfirm();
    // QUICK-START §11.34 п.13 (владелец 30.10: «эту модалку тоже сделай полушторку снизу, красиво с героем»): телефон в
    // браузере — тот же герой-лист, что «Удалить бота?» (`askDeleteHalf`), не окно по центру; приложение — родное окно.
    const ask = native
      ? askNativeConfirm(opts)
      : !isApp && layoutMode === 'phone'
        ? new Promise<boolean>((resolve) => {
            openHeroAsk(panel, {
              icon: HERO_ICONS.power,
              title: t('offSure'),
              actions: [{ key: 'off', label: t('stTurnOff'), tone: 'danger', onPick: () => resolve(true) }],
              closeLabel: tc('cancel'),
              onCancel: () => resolve(false),
            });
          })
        : askConfirm(opts);
    return ask.then(async (yes) => {
      journalLog('bot.off.ask', { id, yes });
      if (!yes) return;
      const res = await Promise.resolve(run()).catch((e: unknown) => ({ ok: false, error: String(e) }));
      const failed = res === false || (typeof res === 'object' && res !== null && (res as { ok?: boolean }).ok === false);
      if (failed) {
        journalLog('bot.off.fail', {
          id,
          error: typeof res === 'object' && res !== null ? String((res as { error?: unknown }).error ?? '') : '',
        });
      }
      // Приложение перечитывает список своим путём (как было) — свежая выгрузка только браузеру.
      if (!native) reload();
    });
  };
  // Остров создания НАД списком (владелец 2026-07-26: «страница должна начинаться не со
  // скучного списка»): ТРИ пути — те же, что в меню «+» (SCRIPTS-MODE Ф5.4) — теми же
  // чипами, что фильтры «Моделей» (канон: классов-близнецов не заводим). «Из шаблона»
  // открывает ту же шторку каталога, что «+». go({}) открывает пустой редактор —
  // черновик родится при первом шаге.
  const create = useCreateGo();
  const openChooser = useOpenCreateChooser();
  const router = useRouter();
  // Штатный механизм шторок кабинета: в приложении — родной лист, в вебе — drawer.
  // Им живут и шторка «+» (три пути), и подтверждение тумблера ниже.
  const panel = useSlidePanel();
  // Лист «Папки» стопкой справа — шестерёнка полосы в двух слоях (Н4).
  const openBotsFolders = useOpenBotsFolders();
  // ОКНО БОТА В БРАУЗЕРЕ — ПАНЕЛЬ НАД СПИСКОМ (ТЗ WEB-LOOK Э2.1, владелец 30.09.2026: «сам
  // бот, аналитика, мастер — модалка»): адрес /app/bots/<id>/stats (`stats:<id>`) открывает
  // окно бота (мозг bot-window-native + bot-window-web) вертикальным листом на телефоне и
  // панелью 460 на широком (Д1.4), поле «Ботов» показывает список. В приложении окно рисует
  // натив, а веб-страница BotStatsPane остаётся запасным путём старых сборок (ниже).
  const isApp = useIsApp();
  const statsId = pick?.startsWith('stats:') ? pick.slice(6) : null;
  // БОТ ОТКРЫТ ШТОРКОЙ ПОВЕРХ СПИСКА (телефон в браузере, ТЗ WEB-LOOK-CLAIM-1): список под
  // шторкой — ТОТ ЖЕ, что до нажатия, а не разворот с шапкой «Все боты». Иначе позади
  // шторки менялось содержимое, а после закрытия подменялось обратно — карточки прыгали
  // (претензия владельца 01.10.2026, видео). Для вида списка выбор тогда «пустой».
  const layoutMode = useLayoutMode();
  // Компьютер с мышью (владелец 13.10.2026): тянуть страницу нечем — стрелки «потяните вниз» нет ни при какой ширине;
  // полосу открывает значок-молния (у заголовка и в пустом «Ботов нет, нажмите»), см. bot-templates QuickStripButton.
  // С ред. 8 (Р19, вопрос 108) пустой экран телефона (≤ 820) одинаков для мыши и сенсора — признак мыши здесь не нужен.
  // ТЕЛЕФОН И ПЛАНШЕТ В БРАУЗЕРЕ — ТА ЖЕ МОЛНИЯ (ТЗ USERTEST-1014 п.2, владелец 14.10.2026): ограничение
  // «только мышь» снято, жест на телефоне остаётся как был. Приложение — молния родная.
  const deskStrip = !bcast && !isApp;
  // ПАПКА НА ТЕЛЕФОНЕ — ТОТ ЖЕ ЭКРАН «БОТЫ» (кадр iOS B01-bots-list-folder-filter, ТЗ
  // WEB-PARITY-3 6.0 п.6): шапка и «+» остаются, отбирает список полоса; заголовок-поле
  // «<папка>» с фильтрами — только у второй колонки широкого экрана.
  // ДВА СЛОЯ — ТОТ ЖЕ ЭКРАН ТЕЛЕФОНА (партия Н4, владелец 03.10.2026, «Фундамент дизайна» п.4):
  // колонки нет, значит и заголовка-поля «<папка>» нет — список отбирает полоса папок над ним,
  // справа «+» в шапке. Разворот с заголовком папки — только у трёх слоёв.
  const viewPick =
    !isApp && layoutMode !== 'three' && (statsId || (pick ?? '').startsWith('cat:')) ? null : pick;
  // ТЕЛЕФОН В БРАУЗЕРЕ — ТО ЖЕ ОКНО, ЧТО В ПРИЛОЖЕНИИ (ТЗ WEB-LOOK-CLAIM-1, претензия владельца
  // 01.10.2026): страницу собирает общий мозг (`bot-window-native.tsx`), рисует
  // `bot-window-web.tsx`. Руки меню и тумблера — рефами: эффект живёт дольше рендера.
  const webMenuRef = useRef<() => BotWindowWebMenu | null>(() => null);
  const webToggleRef = useRef<() => unknown>(() => undefined);
  const webHooksRef = useRef<Partial<BotWindowHooks>>({});
  // ШИРОКИЙ ЭКРАН — ТО ЖЕ ТЕЛО (пакет Д1.4, владелец 04.10.2026, Закон 5 п.9): панель 460 справа
  // рисует ровно телефонное окно бота (тумблер «Включён», Создан/Запущен/Аккаунт, Название/
  // Описание, Папка, Превью, «⋯» в шапке), а не отдельную урезанную BotStatsPane. Разница с
  // телефоном — только хост: лист снизу / панель справа (её даёт drawer по раскладке).
  useEffect(() => {
    if (!statsId || isApp) return;
    // Окно рассылки на широком экране открыто из своего раздела (Д1.1) — закрытие ведёт туда же.
    setBotWindowCloseHref(bcast && BROADCASTS_WIDE_SECTION && readLayoutMode() !== 'phone' ? '/app/broadcasts' : '/app/bots');
    const menuFn = () => webMenuRef.current();
    setBotWindowWebMenu(menuFn);
    openNativeBotWindow(
      { kind: 'stats', rowKey: statsId },
      { ...webHooksRef.current, menu: () => {}, toggle: () => webToggleRef.current() },
    );
    return () => {
      // Снимаем только СВОЁ меню (О7): два списка (раздел «Рассылки» и «Боты») живут в стопке
      // вкладок одновременно, и уход одного не должен обнулять меню окна, открытого другим.
      clearBotWindowWebMenu(menuFn);
      closeNativeBotWindow();
    };
  }, [statsId, isApp]);
  // ОДИН пузырь на три пути (владелец 2026-08-01 по живому виду: три отдельные плашки
  // «нагромождённо смотрятся»): плашка одна, внутри — строки с отступами между ними.
  // Подписи ПОЛНЫЕ (владелец 2026-08-01: «нигде не написано „создать бота“»).
  // «Создать вручную» ведёт на страницу настройки старта (владелец 2026-08-03):
  // конструктор откроется после «Создать» уже с готовым «Начало». В приложении плитки
  // запусков открываются своим слоем поверх меню создания (стопка §11 п.8).
  const openManual = () => {
    if (panel.available) {
      panel.open({ title: t('splitTrigger'), body: <ManualStart bare /> });
      return;
    }
    panel.closeAll();
    router.push('/app/bots/manual');
  };
  const createChips = (
    <div className="scn-create">
      {/* «Вручную» спрятано вместе со всем конструктором (владелец 31.08.2026,
          lib/hidden-features.ts): боты запускаются только мастерами. */}
      {!BUILDER_HIDDEN && (
        <KBareButton className="scn-create-i" disabled={create.busy} onClick={openManual}>
          <SchemaSvg />
          {t('chipManual')}
        </KBareButton>
      )}
      {!ASSISTANT_HIDDEN && (
        <KBareButton className="scn-create-i" disabled={create.busy} onClick={() => void create.go({}, 'dialog')}>
          <ChatSvg />
          {t('chipAssistant')}
        </KBareButton>
      )}
      <KBareButton className="scn-create-i" disabled={create.busy} onClick={() => openChooser('templates')}>
        <TplSvg />
        {t('chipCatalog')}
      </KBareButton>
    </div>
  );
  // ТЕЛЕФОН (владелец 2026-08-02): блок трёх путей со страницы скрыт (CSS ≤820),
  // вместо него «+» — с 31.08 круглая кнопка СПРАВА СВЕРХУ (PageAddButton ниже): в
  // Safari веб-кнопка шапки, в приложении родная кнопка шапки (right2 → scr-create-open).
  // Плавающего кружка снизу больше нет (владелец 31.08). Выбранный путь уводит
  // в редактор сам («результатный» переход схлопывает стопку слоёв — flattenLayers,
  // SCRIPTS-MODE Ф5.4), закрывать за собой выпадашке нечего.
  // Меню «+» — НЕ шторка, а ВЫПАДАШКА У КНОПКИ (владелец 2026-08-04: «пункты с
  // подписями мелким шрифтом»), и механизм у неё ОБЩИЙ с «Рассылками» (create-ways.tsx,
  // Р1 владельца 21.08): в ПРИЛОЖЕНИИ — родное UIMenu у самого кружка (pointMenu,
  // iOS 17.4+; владелец 12.08 по скрину веб-поповера в приложении: «хули тут ненативная
  // шторка»), в вебе и на старых сборках — общий слой меню набора (PopMenuLayer, П1б).
  // Своей разметки меню тут больше нет: пункты — это данные, вид один на кабинет.
  const createWays = useCreateWays([
    ...(BUILDER_HIDDEN
      ? []
      : [
          {
            key: 'manual',
            label: t('chipManual'),
            sub: t('createManualSub'),
            icon: 'square.on.square.dashed',
            glyph: <SchemaSvg />,
            onPick: openManual,
            disabled: create.busy,
          },
        ]),
    ...(ASSISTANT_HIDDEN
      ? []
      : [
          {
            key: 'dialog',
            label: t('chipAssistant'),
            sub: t('createAssistantSub'),
            icon: 'bubble.left.and.text.bubble.right',
            glyph: <ChatSvg />,
            onPick: () => void create.go({}, 'dialog'),
            disabled: create.busy,
          },
        ]),
    {
      key: 'tpl',
      label: t('chipCatalog'),
      sub: t('createCatalogSub'),
      icon: 'square.stack.3d.up',
      glyph: <TplSvg />,
      onPick: () => {
        // Список названий — заранее (П202): к открытию мастера номер уже в кэше.
        warmTarget('templates.titles', SCENARIO_TITLES_KEY, SCENARIO_TITLES_URL, { accept: scenarioTitlesOk }, true);
        openChooser('templates');
      },
      disabled: create.busy,
    },
    // «ЦЕПОЧКА РАССЫЛОК» ПУТЁМ СОЗДАНИЯ ЗДЕСЬ НЕ СТОИТ (владелец 03.09.2026: «никаких
    // выпадашек у „+“»): второй путь превращал «+» в меню. Цепочка — ЧЕТВЁРТАЯ КАРТОЧКА
    // каталога шаблонов (bot-templates.tsx, useChainTplItems), «+» ведёт прямо в каталог.
  ]);
  const openCreateSheetRef = useRef(createWays.open);
  openCreateSheetRef.current = createWays.open;
  // Пункт РОДНОГО меню «+» (headerAction scrnew:* из shell): сразу выбранный путь,
  // без выпадашки. Ref — обработчики держат свежие замыкания.
  const createPickRef = useRef(createWays.pick);
  createPickRef.current = createWays.pick;
  useEffect(() => {
    const onOpen = () => openCreateSheetRef.current();
    const onPick = (e: Event) => {
      const a = (e as CustomEvent<{ action?: string }>).detail?.action;
      if (a) createPickRef.current(a);
    };
    window.addEventListener('scr-create-open', onOpen);
    window.addEventListener('scr-create-pick', onPick);
    return () => {
      window.removeEventListener('scr-create-open', onOpen);
      window.removeEventListener('scr-create-pick', onPick);
    };
  }, []);
  // Персистентный кэш + скелетон — единый хук. mutate зовётся ПОСЛЕ ответа сервера (не оптимистично).
  const { data, error, stale, mutate, reload } = usePersistentData<ScenariosData>(
    'cabinet:scenarios',
    '/api/cabinet/scenarios',
    {
      accept: (j) => !!(j as { ok?: boolean })?.ok,
      // Быстрый ответ из базы первым, живая правда следом (П162 п.2).
      ...scenariosFast<ScenariosData>(),
    },
  );
  // ── ПРЕВЬЮ ПРАВКИ ГОТОВО ЗАРАНЕЕ (О5.10 П3, владелец 03.09.2026: «всё, что телефон
  // показывает, загружено заранее» — и при создании, и при ТАПЕ ПО БОТУ) ── адрес сцены у
  // настроенного бота зависит от его ответов, поэтому список получает их вместе с рядами
  // (`wizard: {id, answers}`) и собирает адреса ТЕМ ЖЕ правилом, что мастер
  // (lib/wizard-demo-url.ts, demoSceneUrlForEdit) — разойдись хоть один параметр, натив
  // грузил бы сцену заново на глазах у человека. Порядок — сверху списка; потолок набора
  // (вместе с адресами создания из каталога) держит общий заказчик warmDemoScenes.
  const { creds, ready: credsReady } = useAccountCreds();
  const tPers = useTranslations('auth.onboarding.pers');
  const wizDrafts = data?.drafts;
  const editWarmUrls = useMemo(() => {
    if (typeof window === 'undefined' || !wizDrafts) return [] as string[];
    const theme = demoThemeNow();
    const seen = new Set<string>();
    const out: string[] = [];
    for (const d of wizDrafts) {
      const w = d.wizard;
      // Ответы приходят только у анкет со сценой-превью — остальным греть нечего.
      if (!w || typeof w !== 'object' || !w.answers) continue;
      const def = wizardById(w.id);
      if (!def?.demoScene) continue;
      const url = demoSceneUrlForEdit({
        origin: window.location.origin,
        locale,
        def,
        answers: w.answers,
        // Язык ТЕКСТОВ БОТА запечён при создании (нет записи — русский), как у мастера.
        botLoc: w.locale === 'en' ? 'en' : 'ru',
        creds,
        credsReady,
        data: nicheData,
        t: tPers,
        theme,
      });
      if (!url || seen.has(url)) continue;
      seen.add(url);
      out.push(url);
      if (out.length >= WARM_SCENE_LIMIT) break;
    }
    return out;
  }, [wizDrafts, locale, nicheData, creds, credsReady, tPers]);
  useEffect(() => warmDemoScenes('bots', editWarmUrls, demoThemeNow()), [editWarmUrls]);
  // ── СЦЕНЫ КАТАЛОГА «ИЗ ШАБЛОНА» — ГОТОВЫ ДО ОТКРЫТИЯ СТОПКИ (владелец 24.09.2026, видео:
  // «веб-часть, которая демонстрирует бота, загружается не сразу: сначала верхняя полоска,
  // потом пост; она должна быть загружена до того, как я открыл стопку — для всех
  // шаблонов») ── каталог заказывал грев ТОЛЬКО когда сам открыт (useWizardDemoWarm в
  // tpl-sheet-host / BotTemplatesList), а от «+» до тапа по карточке проходит секунда —
  // страница сцены не успевала. Отсюда те же адреса (тот же кэш каталога, то же правило
  // адреса) заказываются при заходе на «Ботов»; открытый каталог повторяет их же — набор
  // не меняется, натив второй раз не грузит.
  const { data: tplData } = usePersistentData<{ ok: boolean; items: TplItem[] }>(
    `cabinet:bot-templates:${locale}`,
    `/api/cabinet/bot-templates?lang=${locale}`,
    { accept: (j) => Array.isArray((j as { items?: unknown } | null)?.items) },
  );
  const chainTplItems = useChainTplItems();
  const tplAll = tplCatalogItems(tplData?.items ?? [], chainTplItems);
  useWizardDemoWarm(tplAll, 'templatesAhead');

  // ── ПРОГРЕВ БОТОВ СПИСКА (владелец 30.08.2026: «почему при открытии любого бота я жду,
  // почему ты заранее не загружаешь всё что надо») ── карточка открывается мгновенно ТОЛЬКО
  // если бот уже в кэше: пока данных нет, родной слой ждёт, пока веб нарисует страницу
  // (waitForSelector, native-layers.ts), и человек всё это время смотрит в список.
  // Греем двумя ходами: в простое — ВСЕ карточки списка, и точечно (срочно) — ту, к которой
  // человек уже поднёс палец (pointerdown приходит раньше клика). Ключ кэша тот же, что у
  // страницы бота и у анкеты мастера, — второго источника не заводим.
  const warmed = useRef<Set<string>>(new Set());
  const warmedRoutes = useRef<Set<string>>(new Set());
  // Вместе с черновиком греются данные ОКНА бота (PREFETCH-TZ §2.1, видео владельца 10.10.2026): ленты событий
  // («Контакты» и «Все») за сохранённый период, у рассылки — её состояние. Всё — цели плана прогрева (lib/app-pages/warm-plan.ts, очередь lib/warm.ts:
  // шаг 300 мс, один запрос, после первого экрана); касание (`urgent`) — первым и сразу.
  const warmDraft = (id?: string | null, urgent = false) => {
    if (!id) return;
    if (warmed.current.has(id) && !urgent) return;
    warmed.current.add(id);
    warmTarget('bots.row', `cabinet:scenario-draft:${id}`, `/api/cabinet/scenarios/drafts/${id}`, { accept: okAccept }, urgent);
    // ВСЕ СТРАНИЦЫ ОКНА (PREFETCH-TZ §3 п.1): у рассылки — состояние и запуски; у цепочки — её цифры; у бота — ленты
    // событий и запуски. Период и шаг — сохранённые (их же попросит окно).
    const item = (data?.drafts ?? []).find((x) => x.id === id);
    const { days, step } = botWindowPrefs();
    if (bcast) {
      warmBroadcastLaunch(id, urgent);
      if (item?.workflowId) warmRuns(item.workflowId);
    } else if (item?.chainActive != null) {
      warmChainStats(id, days, step);
    } else {
      warmBotEvents(id, urgent);
      if (item?.workflowId) warmRuns(item.workflowId, days);
    }
  };
  // Второй кусок ожидания — сама страница бота: её код и серверный ответ роутера. Тянем их
  // тем же касанием (router.prefetch), что и данные бота: слой ждёт отрисовки страницы
  // (waitForSelector), а она не начнётся, пока не приедет маршрут.
  const warmRoute = (href?: string | null) => {
    if (!href || warmedRoutes.current.has(href)) return;
    warmedRoutes.current.add(href);
    try {
      router.prefetch(href);
    } catch {
      /* маршрут не прогрелся — откроется обычным путём, просто чуть дольше */
    }
  };
  const warmRef = useRef(warmDraft);
  warmRef.current = warmDraft;
  // ГРЕЕМ ТОЛЬКО ПО СВЕЖЕМУ СПИСКУ (ТЗ WEB-CAUSES М75 п.4): список из кэша браузера может держать
  // черновики, которые с тех пор ушли в архив, — их прогрев давал 404 на /drafts/<id> при каждом входе
  // в кабинет. Пока идёт сверка с сервером (stale), прогрева нет; пришёл свежий список — греем его.
  // ВИДИМЫЕ КАРТОЧКИ И ПОЛЭКРАНА ВПЕРЁД (PREFETCH-CAREFUL-TZ §2 п.1, владелец 11.10.2026: «аккуратный прогрев») —
  // хук useWarmInView ниже (после пейджера папок); прежнее «все боты списка» (PREFETCH-TZ §3 п.1) отменено.
  const warmIds = stale ? '' : (data?.drafts ?? []).map((d) => d.id).join(',');
  useEffect(() => {
    if (!warmIds) return;
    // В ПРОСТОЕ, а не сразу: список сам только что приехал, и его отрисовка важнее.
    const run = () => {
      // Тяжёлый чанк холста (@xyflow) — тот же, что греет сама страница бота на своём
      // монтировании: греем ЗАРАНЕЕ, отсюда, иначе он качается уже во время ожидания.
      void import('./scenario-schema-panel');
    };
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
      cancelIdleCallback?: (h: number) => void;
    };
    if (w.requestIdleCallback) {
      const h = w.requestIdleCallback(run, { timeout: 2000 });
      return () => w.cancelIdleCallback?.(h);
    }
    const h = window.setTimeout(run, 400);
    return () => window.clearTimeout(h);
  }, [warmIds]);
  // ЦИФРЫ И СТАТУСЫ РАССЫЛОК — второй ручкой поверх ОДНОГО общего списка
  // (/api/cabinet/broadcasts, ТЗ 13г): имена, аватарки и аккаунты приходят той же
  // ручкой, что у ботов, здесь только то, чего в ней нет. Витрина ботов её не дёргает
  // вовсе (пустой url = запроса нет).
  const { data: bcData, error: bcError, reload: reloadBc } = usePersistentData<BroadcastsResp>(
    BROADCASTS_CACHE_KEY,
    bcast ? BROADCASTS_URL : '',
    { accept: acceptBroadcasts },
  );
  const bcItems = useMemo(
    () => new Map((bcData?.items ?? []).map((i) => [i.draftId, i])),
    [bcData],
  );
  /** Статус рассылки этой строки; у бота — null (иф не срабатывает). */
  const bcStatusOf = (id: string | null): BroadcastStatus | null =>
    bcast ? (bcItems.get(id ?? '')?.status ?? 'draft') : null;
  const statusChip = useStatusChip();
  // Живой прогресс: пока рассылка идёт — освежаем цифры раз в 5 секунд, как журнал, но только
  // пока раздел на экране в видимом документе (ТЗ SPEED §8.1). Повод `broadcasts` из канала
  // (рассылка ушла, сменила статус, обновила цифры — §8.5) перечитывает список сразу.
  const bcLive = (bcData?.items ?? []).some((i) => i.status === 'sending');
  const bcVisible = usePaneVisible();
  useEffect(() => {
    if (!bcast || !bcLive || !bcVisible) return;
    const id = setInterval(reloadBc, 5000);
    return () => clearInterval(id);
  }, [bcast, bcLive, bcVisible, reloadBc]);
  useAppLive(
    'broadcasts',
    useCallback(() => {
      if (bcast) reloadBc();
    }, [bcast, reloadBc]),
  );
  // ИФ №3 (кнопка): у рассылки вместо «Включить/Выключить» — «Отправить»/«Остановить»,
  // и зовёт он существующую ручку запуска (broadcast-launch.tsx), а не тумблер бота.
  const bcActs = useBroadcastActs(() => reloadBc());

  // Источники (Telegram, Instagram, форма, чат…) для второй колонки десктопа — РОВНО те
  // же подписи, по которым список группируется справа (triggerLabel). Владелец 2026-08-01:
  // прежде сюда шли аккаунты, но аккаунт есть только у выбранного бота — соцсети, где выбор
  // ещё не сделан, в колонке не появлялись вовсе. Ключ-строка гасит лишние отправки.
  // Значения фильтров (каналы и аккаунты) берём из самих данных: только то, что реально
  // встречается. Считаем ДО любых ранних выходов — иначе хук ниже оказался бы условным.
  const channels: string[] = [];
  const accounts: string[] = [];
  for (const d of data?.drafts ?? []) {
    // Отбор витрины — своими значениями: в «Рассылках» не предлагаем аккаунты ботов и
    // наоборот (общее разбиение lib/broadcast-list.ts, второго условия не пишем).
    if (!keepKind(d, kind)) continue;
    const l = triggerLabel(d.triggerType, locale);
    if (l && !channels.includes(l)) channels.push(l);
    if (d.account && !accounts.includes(d.account)) accounts.push(d.account);
  }
  // Те же списки нужны оболочке (мобильная шапка и родное меню приложения) — шлём событием,
  // как это делает лента «Сообщений»: раздел знает данные, шапка знает, где их показать.
  const fltKeyRef = useRef('');
  const fltKey = `${channels.join('|')}//${accounts.join('|')}`;
  useEffect(() => {
    if (fltKey === fltKeyRef.current) return;
    fltKeyRef.current = fltKey;
    const [ch, ac] = fltKey.split('//');
    // Витрин две, и обе живут в стеке одновременно — без `kind` в письме они затирали бы
    // друг другу значения отбора в шапке (в «Рассылках» показывались бы аккаунты ботов).
    window.dispatchEvent(
      new CustomEvent(SCR_FILTERS_EVENT, {
        detail: { kind, channels: ch ? ch.split('|') : [], accounts: ac ? ac.split('|') : [] },
      }),
    );
  }, [fltKey, kind]);

  // Пульс сценариев — тот же эндпоинт, что был у «Обзора». Сам пульс карточки больше не
  // читают (чип сбоев снят 19.09.2026, запрет №7): здесь остаётся только перезагрузка,
  // которой родное окно бота и тумблер обновляют кэш пульса для страницы «Запуски».
  const { reload: reloadPulse } = usePersistentData<Pulse>(
    'cabinet:overview:pulse',
    '/api/cabinet/overview/pulse',
    {
      accept: (j) => !!(j as { ok?: boolean })?.ok,
    },
  );
  // ЛИНИЯ ОТКРЫТИЙ КАРТОЧКИ (п.16б): точки пульса по дням — в порядке рисования (пульс
  // отдаёт свежие первыми), подписи крайних точек — числа на языке кабинета. Пульс ещё
  // едет или открытий не было — линии нет. Одна рука на веб-карточку и родную.
  /** Линия карточки: запуски по дням (пульс) и ВТОРАЯ линия — подарки по дням
   *  (владелец 12.09.2026: «добавь на график подарки»), тем же порядком дней. */
  /** ДВЕ ЛИНИИ КАРТОЧКИ (владелец 13.09.2026, П77, как в ChatPlace): контакты по дням и
   *  конверсия по дням за PULSE_DAYS — те же цифры, что на странице бота за 7 дней.
   *  Линия запусков и подарков снята. Пусто — контактов за неделю не было (линии нет). */
  const lineData = (
    goalDaily?: number[],
    enteredDaily?: number[],
    ready?: { runs: number[]; contacts: number[]; conv: number[] } | null,
  ): { contacts: number[]; entered: number[]; conv: number[]; sum: number; enteredSum: number; convTotal: number; hi: string; lo: string } | null => {
    // ЛИНИИ СЧИТАЕТ СЕРВЕР ПО НАСТРОЙКЕ БОТА (П119): срок и шаг у каждого свои, и резать
    // их на экране нечем — дневных корзин нужной глубины у него нет. Старый ответ без
    // `cardLine` (кэш прошлой версии) ещё рисуется по-прежнему, за неделю.
    if (ready && ready.runs.length >= 2) {
      const max = Math.max(...ready.runs, ...ready.contacts);
      if (max <= 0) return null;
      const sum = ready.contacts.reduce((a, v) => a + v, 0);
      const enteredSum = ready.runs.reduce((a, v) => a + v, 0);
      const num = (v: number) => new Intl.NumberFormat(locale).format(v);
      return {
        contacts: ready.contacts,
        entered: ready.runs,
        conv: ready.conv,
        sum,
        enteredSum,
        convTotal: enteredSum > 0 ? Math.max(0, Math.min(100, Math.round((sum / enteredSum) * 100))) : 0,
        hi: num(max),
        lo: num(Math.min(...ready.contacts)),
      };
    }
    if (!goalDaily || goalDaily.length < 2) return null;
    const contacts = [...goalDaily].reverse();
    const entered = enteredDaily && enteredDaily.length === goalDaily.length ? [...enteredDaily].reverse() : [];
    const max = Math.max(...contacts, ...entered);
    if (max <= 0) return null;
    const conv = contacts.map((c, i) => {
      const e = entered[i] ?? 0;
      return e > 0 ? Math.max(0, Math.min(100, Math.round((c / e) * 100))) : 0;
    });
    const sum = contacts.reduce((a, v) => a + v, 0);
    const enteredSum = entered.reduce((a, v) => a + v, 0);
    const convTotal = enteredSum > 0 ? Math.max(0, Math.min(100, Math.round((sum / enteredSum) * 100))) : 0;
    const num = (v: number) => new Intl.NumberFormat(locale).format(v);
    return { contacts, entered, conv, sum, enteredSum, convTotal, hi: num(max), lo: num(Math.min(...contacts)) };
  };
  /** Легенда линий карточки: кружок цвета линии + имя и итог за неделю. Одна рука на
   *  веб-карточку и родную (натив получает строки и цвета готовыми). */
  // ЛЕГЕНДА КАРТОЧКИ НАЗЫВАЕТ ЛИНИИ (П113, владелец 14.09.2026: «график запуски и контакты,
  // а подписано контакты и конверсия»): «Запустили бота» серым, «Контакты» цветом сети, и
  // третьим числом без кружка — конверсия.
  // ЦВЕТА СЕРИЙ — КОНСТАНТЫ ПЛИТОК ПАСХАЛКИ, ОДНИ С ТЕЛЕФОНОМ (APP-CLAIMS-1020 ред. 6, П13; `LINE_INK`
  // lib/quick-tiles.ts — тот же hex, что сервер шлёт полем `ink`): запуски — плитка Instagram, контакты — плитка
  // Telegram, конверсия — бирюзовый `--green` плиток справки.
  const runsInk = () => LINE_INK.runs;
  const contactsInk = () => LINE_INK.contacts;
  const convInk = () => LINE_INK.conv;
  const lineLegend = (
    line: { sum: number; enteredSum: number; convTotal: number },
    trigger?: string | null,
    prefs: CardPrefs = CARD_PREFS_DEFAULT,
    // КАНАЛ БОТА — ГЛАВНЕЕ УЗЛА ЗАПУСКА (владелец 21.09.2026: «контакты не тем цветом»).
    // У ЦЕПОЧКИ узла запуска нет вовсе, и `chanOf` возвращал «чат» — кружок «Контактов»
    // выходил серым вместо голубого Телеграма. Канал у карточки уже посчитан по
    // аккаунту-отправителю, им и красим.
    chan?: string | null,
  ): Array<{ text: string; label: string; value: string; ink: string; plain?: boolean }> => {
    // Подписи идут ОДНОЙ строкой и только у выбранных линий (владелец 14.09.2026: «запуски,
    // контакты и конверсия помещаются в одну строчку — давай сделаем в одну»). Слово и число —
    // отдельно (APP-CLAIMS-1020 П3 п.5): карточка рисует «Запуски 1 · Контакты 0» без точек.
    const out: Array<{ text: string; label: string; value: string; ink: string; plain?: boolean }> = [];
    // Канал и узел запуска цвет серий больше не решают (П10) — параметры остались ради прежних вызовов.
    void trigger;
    void chan;
    // «ЗАПУСКИ» — ГОЛУБЫМ `--blue` (П10, 20.10.2026; прежде розовым по слову 22.09.2026).
    if (prefs.lines.includes('runs')) {
      out.push({
        text: t('cardLegendEntered', { n: line.enteredSum }),
        label: t('statsRuns'),
        value: String(line.enteredSum),
        ink: runsInk(),
      });
    }
    if (prefs.lines.includes('contacts')) {
      out.push({
        text: t('cardLegendContacts', { n: line.sum }),
        label: t('statsContacts'),
        value: String(line.sum),
        // П10: контакты — роль `--green` при любом канале (цвет сети больше не красит серию).
        ink: contactsInk(),
      });
    }
    if (prefs.lines.includes('conv')) {
      out.push({
        text: `${t('statsConv')} · ${line.convTotal} %`,
        label: t('statsConv'),
        value: `${line.convTotal} %`,
        ink: convInk(),
      });
    }
    return out;
  };
  // ОТДЕЛЬНОЙ СТРОКИ КОНВЕРСИИ НА КАРТОЧКЕ НЕТ (П119, владелец 14.09.2026: «запуски,
  // контакты и конверсия помещаются в одну строчку — давай сделаем в одну»): конверсия
  // стоит подписью линии там же, где запуски и контакты.
  const cardLine = (
    goalDaily?: number[],
    enteredDaily?: number[],
    trigger?: string | null,
    ready?: { runs: number[]; contacts: number[]; conv: number[] } | null,
    prefs?: CardPrefs,
    chan?: string | null,
  ) => {
    const line = lineData(goalDaily, enteredDaily, ready);
    if (!line) return null;
    // ЧТО РИСОВАТЬ — РЕШАЕТ ЧЕЛОВЕК В «О БОТЕ» (П119): линии те же, показываются
    // выбранные. Не выбрано ничего — графика на карточке нет вовсе.
    const p = prefs ?? CARD_PREFS_DEFAULT;
    if (p.lines.length === 0) return null;
    const legend = lineLegend(line, trigger, p, chan);
    // ОДИН ГРАФИК, ДВЕ ЛИНИИ (П94, владелец 13.09.2026): контакты цветом сети и конверсия
    // по своей оси 0…100; легенда двумя парами одной строкой под графиком.
    return (
      <>
        <CardLine
          // Основная линия — контакты, если их показывают; иначе первая из выбранных.
          points={p.lines.includes('contacts') ? line.contacts : p.lines.includes('runs') ? line.entered : line.conv}
          // Цифр шкалы на карточке нет (владелец 14.09.2026): числа стоят в подписях линий.
          hi=""
          lo=""
          points2={p.lines.includes('contacts') && p.lines.includes('runs') ? line.entered : undefined}
          hi2=""
          // Запуски — `--blue` (П10) — и второй линией, и единственной.
          color2={runsInk()}
          points3={p.lines.includes('conv') && p.lines.length > 1 ? line.conv : undefined}
          color3={convInk()}
          fixedMax={p.lines.length === 1 && p.lines[0] === 'conv' ? 100 : undefined}
          // ЦВЕТ ЛИНИИ И ЕЁ ПОДПИСИ — ОДИН (владелец 14.09.2026: «график одного цвета, а
          // конверсия точка другого — у тебя там разлад»). Осталась одна конверсия — линия
          // рисуется бирюзовой, как её кружок; иначе цвет сети, как у контактов и запусков.
          // П10: цвет — роль серии (конверсия / запуски / контакты), как на телефоне.
          color={
            p.lines.length === 1 && p.lines[0] === 'conv'
              ? convInk()
              : !p.lines.includes('contacts') && p.lines.includes('runs')
                ? runsInk()
                : contactsInk()
          }
        />
        {/* ЛЕГЕНДА БЕЗ ТОЧЕК (APP-CLAIMS-1020 П3 п.5, как на телефоне): «Запуски 1 · Контакты 0» — слово
            вторичным серым, число цветом своей линии, разделитель «·» третичным; срок — справа. */}
        <div className="scn-card-legend">
          {legend.map((l, i) => (
            <span key={l.text} className="scn-card-legend__it">
              {i > 0 ? (
                <span className="scn-card-legend__sep" aria-hidden>
                  ·
                </span>
              ) : null}
              <span className="scn-card-legend__label">{l.label}</span>
              <span className="scn-card-legend__val" style={l.ink ? { color: l.ink } : undefined}>
                {l.value}
              </span>
            </span>
          ))}
          {/* СРОК ОДНОЙ ЦИФРОЙ В КОНЦЕ СТРОКИ (владелец 14.09.2026: «за какой срок
              показываем — надо где-то аккуратно написать одну циферку»). Шаг по неделям
              подписан тем же местом, чтобы не гадать, откуда взялись пять точек. */}
          <span className="scn-card-legend__days">
            {p.step === 'week' ? t('cardDaysWeeks', { n: p.days }) : t('cardDays', { n: p.days })}
          </span>
        </div>
      </>
    );
  };
  /** НИЗ КАРТОЧКИ БОТА НА ВСЮ ШИРИНУ (владелец 14.09.2026): график и подписи линий стоят от
   *  края до края карточки (сосед колонки слов, не её часть), под ними строка «Конверсия N %»
   *  (за всё время) и справа состояние точкой и словом. Переключателя на карточке нет —
   *  включить и выключить можно только в меню «⋯». Чип сбоев — в той же строке. */
  /**
   * СЛОВО СОСТОЯНИЯ ЦЕПОЧКИ БЕЗ ЖИВЫХ ВХОДОВ (П203, владелец 29.09.2026: «„Не запускается:
   * бот удалён“ — непонятно»): называем удалённый бот по имени — «Бот «Разбор фото» удалён —
   * не запускается». Строка у карточки ОДНА (веб nowrap, натив numberOfLines 1), поэтому
   * длинное имя режется до 20 знаков с многоточием. Пусто — подписи нет.
   */
  const chainGoneLabel = (d: DraftItem | null | undefined): string => {
    if (!d?.chainNoEntry) return '';
    const cut = (s: string) => (s.length > 20 ? `${s.slice(0, 19)}…` : s);
    const names = (d.chainGone ?? []).map((g) => `«${cut(g.title || '…')}»`);
    if (names.length === 0) return '';
    return names.length > 1
      ? t('stChainGoneMany', { names: names.join(', ') })
      : t('stChainGone', { names: names[0] ?? '' });
  };
  const cardWide = (o: {
    goalDaily?: number[];
    enteredDaily?: number[];
    /** Готовые линии под настройку бота (П119) — их считает сервер. */
    cardLine?: { runs: number[]; contacts: number[]; conv: number[] } | null;
    prefs?: CardPrefs;
    trigger?: string | null;
    /** Канал бота по аккаунту-отправителю (у цепочки — её кред): им красится «Контакты». */
    chan?: string | null;
    st: 'run' | 'off' | 'draft';
    stFail?: boolean;
    stBusy?: boolean;
    /** Цепочка включена, но все её входы — удалённые боты (П203): готовое слово состояния
     *  (`chainGoneLabel`) — сигнальным тоном, как сбой. Пусто — подписи нет. */
    stNoEntry?: string;
  }) => {
    const on = o.st === 'run' && !o.stFail;
    // СОСТОЯНИЕ СЛОВОМ — ТОЛЬКО КОГДА ОНО НЕ «ВКЛЮЧЕНО» (владелец 14.09.2026: «это
    // „Включено“ уберём, а у выключенных рядом со значком сети сделаем значок, что бот
    // выключен»). Сбой и переключение остаются словом: это не обычная жизнь бота, и
    // молчать о них нельзя. У включённого в строке не стоит ничего.
    const noEntry = !!o.stNoEntry && on && !o.stBusy;
    const label = o.stBusy ? t(on ? 'chipBusyOff' : 'chipBusyOn') : o.stFail ? t('stFailed') : noEntry ? o.stNoEntry! : '';
    const tone = o.stBusy ? 'wait' : o.stFail || noEntry ? 'err' : on ? 'run' : 'off';
    // Чипа сбоев здесь больше нет (владелец 19.09.2026, запрет №7): сбои — владельцу в TG.
    return (
      <div className="scn-card-wide">
        {cardLine(o.goalDaily, o.enteredDaily, o.trigger, o.cardLine, o.prefs, o.chan)}
        <div className="scn-card-row2">
          {/* Конверсия стоит в подписях линий (она такая же линия), отдельной строкой её
              больше нет — цифры карточки живут одной строкой (владелец 14.09.2026). */}
          {label ? (
            <span className={`scn-card-state scn-card-state-${tone}`}>
              <i className="scn-card-state__dot" aria-hidden />
              {label}
            </span>
          ) : null}
        </div>
      </div>
    );
  };
  // BUILDER-UX-FIXLIST 1.4: раздел живёт в стеке НЕ размонтируясь (tab-stack) — без этого
  // возврат со страницы сценария показывал УСТАРЕВШИЙ статус включённости до Cmd+R
  // (включение прослушкой/запуском список не видел). Панель снова активна → тихая
  // ревалидация: кэш показывается сразу, статусы доезжают фоном.
  const paneActive = useContext(PaneActiveContext);
  const wasPaneActive = useRef(paneActive);
  // Вспышка новой карточки (владелец 05.08, видео: «непонятно, какой скрипт новый»):
  // создание пишет метку scn:new (useCreateGo) — при возврате к списку карточка мягко
  // въезжает и коротко подсвечивается акцентом. Метка одноразовая и живёт 2 минуты.
  const [freshId, setFreshId] = useState<string | null>(null);
  useEffect(() => {
    if (!paneActive) return;
    try {
      const raw = sessionStorage.getItem('scn:new');
      if (!raw) return;
      const v = JSON.parse(raw) as { id?: string; at?: number };
      sessionStorage.removeItem('scn:new');
      if (v.id && Date.now() - (v.at ?? 0) < 120_000) setFreshId(v.id);
    } catch {
      /* приватный режим */
    }
  }, [paneActive]);
  // Р5 (владелец 21.08): показ панели освежает данные САМ ХУК (usePersistentData слушает
  // PaneActiveContext) — своего «paneActive → reload» здесь больше нет. Остаётся только
  // то, чего хук знать не может: пульс карточек живёт вторым ключом и должен освежаться
  // вместе со списком.
  useEffect(() => {
    if (paneActive && !wasPaneActive.current) reloadPulse();
    wasPaneActive.current = paneActive;
  }, [paneActive, reloadPulse]);
  // Возврат из bfcache Safari и «окно снова на виду» тоже освежают список — и это тоже
  // делает сам хук (Р5): pageshow(persisted) и visibilitychange слушает
  // usePersistentData, с общим тормозом по ключу. Прежние слушатели по месту сняты.
  // Приложение iOS/Android: платёжные зовы под запретом (CLAUDE.md п.7) — см. frozen-баннер.
  const native = useNativeApp();
  // Плашка «бот погашен по неоплате» — ОБЩАЯ рука кабинета (billing.tsx): она же
  // открывает ШТАТНОЕ окно подписки, второго платёжного окна в разделе не заводим. В
  // приложении оно не открывается вовсе (запрет п.7) — там плашка без ссылок.
  // Тема кабинета живая (меняется на месте) — родной список и родные меню красятся ею.
  const [themeAttr] = useThemeAttr();
  // СПИСОК РИСУЕТ НАТИВ — ВЕБ-ВЕРСИЮ ПОД НИМ НЕ РИСОВАТЬ ВОВСЕ (тот же гейт, что у
  // родного «Аккаунта»): пустая панель не мелькает при уходе с вкладки и под шторками.
  // Признак — состоянием ПОСЛЕ маунта, не при рендере: isNativeApp на сервере всегда
  // false, гейт в рендере дал бы расхождение гидрации (инцидент 355a478f на /app/bots).
  const [nativeDraws, setNativeDraws] = useState(false);
  // ФОРМА ЭКРАНА (APP-CLAIMS-1020 ред. 9, 7.4.2–7.4.3): список «Ботов»/«Рассылок» показан с данными — снять форму на
  // устройство; следующая заглушка этого экрана (`<Shape/>` ниже, при `!data`) будет ровно его формы.
  const shapeName = bcast ? 'broadcasts' : 'bots';
  // Рассылки — одним кадром со статусами (ручка /api/cabinet/broadcasts): без них карточки сменили бы вид после показа.
  const listReady = !!data && (!bcast || !!bcData || bcError);
  useShapeRecord(shapeName, listReady && !nativeDraws);
  // useLayoutEffect, а не кадр спустя через rAF (волна 3, реестр «скелетон холодного
  // старта»): гейт обязан встать ДО первой отрисовки. С rAF человек на холодном старте
  // успевал увидеть веб-скелетон списка, который через кадр сменялся родным экраном, —
  // это и есть то «мелькание», которого не бывает. Эффект раскладки идёт до кадра, и
  // расхождения гидрации не даёт: на сервере он не выполняется вовсе.
  useLayoutEffect(() => {
    // У «Рассылок» с волны 3 тоже есть родной экран (BcastListScreen) — веб-витрину
    // под ним не рисуем ровно так же.
    setNativeDraws(bcast ? nativeBcastListSupported() : nativeBotsSupported());
  }, [bcast]);
  // РОДНОЙ РАЗДЕЛ «РАССЫЛКИ» ПОДНИМАЕТСЯ ТОЛЬКО У ПОКАЗАННОЙ ПАНЕЛИ (владелец
  // 02.09.2026, Н2: «после холодного запуска само открывается „Создать рассылку“»).
  // Панель раздела живёт в стеке разделов и домонтируется ФОНОМ через WARM_DELAY
  // (tab-stack.tsx) — мозг просыпался на пустом месте и поднимал родной экран поверх
  // «Ботов», хотя человек ничего не нажимал.
  //
  // INVARIANT(scriptera): родной экран раздела «Рассылки» открывается ТОЛЬКО когда его
  // панель показана (PaneActiveContext) либо когда раздел открыли рукой (хозяин
  // BroadcastsNativeHost стоит вне стека — там провайдера нет и значение по умолчанию
  // true). Причина: иначе фоновый прогрев стека открывает раздел сам собой на холодном
  // старте. Прогрев при этом остаётся: скрытая панель по-прежнему тянет данные
  // (usePersistentData), и первый тап по «Рассылкам» показывает готовый экран без
  // ожидания (Н17). Канон: docs/NATIVE-TABS-PLAN.md (волна 3), docs/HOW-IT-WORKS.md §6.
  const bcNative = bcast && nativeDraws && paneActive;
  /** Стопка страниц РОДНОГО раздела «Рассылки» (цифры, «О сценарии», «Дубликат»). */
  const [bcStack, setBcStack] = useState<string[]>([]);
  /** Осечка действия (не удалилось, не выключилось) страницей стопки: в вебе это
   *  такая же шторка с заголовком и одной строкой без техподробностей. */
  const [bcMsg, setBcMsg] = useState<{ title: string; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // ЗАПРЕТ ОДИНАКОВЫХ ВХОДОВ (раздел Л правило 4): включение бота или цепочки со словом,
  // которое на аккаунте уже занято, отбивается 409 bot_exists — та же шторка, что при
  // создании из мастера: выключить хозяина слова и включить своего снова (П160 п.4).
  const botExists = useBotExistsAsk();
  /** `retry` — повторить включение своего после выключения бота-хозяина кнопкой шторки
   *  (П160 п.4); «Поменять запуск» на тумблере просто закрывает шторку. */
  const showBotExists = (r: { names?: unknown; existingId?: unknown }, retry: () => void) => {
    if (typeof r.existingId !== 'string') return;
    const names = Array.isArray(r.names) ? r.names.filter((n: unknown) => typeof n === 'string') : [];
    botExists.show({ names: names as string[], existingId: r.existingId }, { retry });
  };
  // Пре-чек пересечений перед шторкой подтверждения — чип пишет «включаем…» на время.
  const [checking, setChecking] = useState<string | null>(null);
  /** Какой скрипт сейчас удаляется — меню этой карточки на время замирает. */
  // §11.34 п.14б: удаление больше не держит карточку «занятой» — строка уходит сразу (removeDraft).
  const delBusy = null as string | null;
  // Включение не прошло — говорит САМА КАРТОЧКА (Г2 BUILDER-INSTANT, владелец 13.08:
  // полос ошибок над списком больше нет). Причина хранится по карточке и разделена по
  // виновнику (запрет №7 CLAUDE.md): 'lint' — чинит человек в своём боте, 'balance' —
  // кончились токены, 'tech' — наша поломка (клиенту без подробностей, нам reportUiError).
  // Живёт до следующей попытки включения или ухода со страницы.
  const [fail, setFail] = useState<Record<string, FailKind>>({});
  const markFail = (id: string, kind: FailKind) => setFail((prev) => ({ ...prev, [id]: kind }));
  const clearFail = (id: string) =>
    setFail((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  // Шторка подтверждения (владелец 2026-07-21): ЛЮБОЕ переключение — только через явное
  // «сейчас включим/выключим». Штатный механизм шторок кабинета: в приложении — родной
  // нижний лист с нативной шапкой (× слева | заголовок | действие справа), в вебе —
  // iOS-шторка drawer (sheet: true, канон NATIVE-SLIDE-PLAN §11 п.9/10).
  // Чип статуса на карточке снова зовёт её (владелец 2026-08-04: «должна быть
  // возможность включить/выключить» — чипы, снятые пробой 2026-07-24, возвращены).

  /** Ответ — `{ ok, error }` ручки тумблера: его читает вопрос выключения (О8, журнал отказа). */
  const toggle = async (s: Scenario): Promise<{ ok: boolean; error?: string }> => {
    setBusy(s.id);
    clearFail(s.id);
    const next = !s.active;
    const r = await fetch(`/api/cabinet/scenarios/${s.id}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ active: next }),
    })
      .then((res) => res.json())
      // network: обрыв сети/подмена контейнеров при выкатке — до сервера не долетело
      .catch(() => ({ ok: false, error: 'network' }));
    setBusy(null);
    // НЕОПЛАТА = ВЫКЛЮЧЕНИЕ (К17.4 п.1, К15 П9 п.1): сервер не даёт включить, пока
    // подписка не подтверждена. Это не поломка — плашка вместо окна ошибки, и в центр
    // ошибок такое не уходит.
    if (r.error === 'billing') {
      openBillingPlate();
      return { ok: false, error: 'billing' };
    }
    // ЗАПРЕТ ОДИНАКОВЫХ (раздел Л правило 4): такой же запуск на аккаунте уже занят другим
    // ботом или цепочкой — включить нельзя, бот остаётся выключенным. Это не поломка:
    // показываем ту же шторку, что при создании, и уводим к хозяину слова.
    if (r.error === 'bot_exists') {
      showBotExists(r, () => void toggle(s));
      return { ok: false, error: 'bot_exists' };
    }
    if (r.ok && data) {
      // Ф5: правило A12 снято — конкурентов сервер больше не выключает, меняется один ряд.
      mutate({
        ...data,
        scenarios: data.scenarios.map((x) => (x.id === s.id ? { ...x, active: next } : x)),
      });
    } else {
      // Тумблер готового бота ходит только к нашему серверу и n8n: любая осечка тут —
      // НАША (сеть/n8n/неизвестное), клиенту без подробностей. Модалка — сразу (приказ
      // владельца 13.08); при неудачном включении чип карточки дальше держит «Не включился».
      if (next) {
        markFail(s.id, 'tech');
        openFail('tech', null);
      } else {
        openActionFail(t('failOffTitle'), t('failOffText'));
      }
      // Клиент видит «⚠ не получилось» — смотрящий обязан видеть то же (BUILDER-UX 5.1):
      // роут тумблера глотает падение n8n в общий 502 n8n_error и в центр сам не пишет.
      reportUiError('scenario_toggle_failed', `тумблер сценария не сработал: ${r.error ?? 'unknown'}`, {
        scenarioId: s.id,
        active: next,
      });
      return { ok: false, error: String(r.error ?? 'unknown') };
    }
    return { ok: true };
  };

  // Удаление скрипта ИЗ СПИСКА (владелец 2026-08-06: «три точечки с выпадающим меню,
  // где будет включить, выключить, удалить — чтобы потом убрать их с открытой страницы»).
  // Спрашивает ConfirmInline: в приложении это РОДНОЕ окно iOS, на сайте — окошко по
  // центру; вертикальной шторки тут нет по прямому заказу.
  /** ТУМБЛЕР ЦЕПОЧКИ РАССЫЛОК (владелец 03.09.2026: «выключатель нужен, как у всех»):
   *  цепочка не едет на сервер сценариев, включение/выключение — флаг active у неё в
   *  базе; выключенная не берёт новых людей и не шлёт писем идущим.
   *
   *  РУКА ОДНА НА КАБИНЕТ (chain-toggle.tsx, 16.09.2026): те же четыре разговора ведёт
   *  страница цепочки (М1.6) и родное окно приложения — копии тут больше нет. Списку
   *  остаются только его дела: занятость чипа, метка «не включился» и свой ряд в кэше. */
  const chainToggleRun = useChainToggle({
    onBusy: (id) => {
      setBusy(id);
      if (id) clearFail(id);
    },
    onOk: (id, next) => {
      if (!data) return;
      mutate({
        ...data,
        drafts: (data.drafts ?? []).map((x) => (x.id === id ? { ...x, chainActive: next } : x)),
      });
    },
    onFail: (id, next) => {
      if (next) {
        markFail(id, 'tech');
        openFail('tech', null);
      } else {
        openActionFail(t('failOffTitle'), t('failOffText'));
      }
    },
    note: (title, text) => openActionFail(title, text),
    onExists: showBotExists,
  });
  const toggleChain = (d: DraftItem) =>
    chainToggleRun.run({ id: d.id, active: d.chainActive, billingOff: d.billingOff });
  const removeDraft = async (id: string) => {
    // QUICK-START §11.34 п.14б (владелец 30.10: «все запросы должны быть асинхронные»): строка уходит из списка СРАЗУ
    // после подтверждения, DELETE — фоном; отказ — строка возвращается и окно со словами ошибки. Перечитка — фоном.
    const before = data;
    if (before) mutate({ ...before, drafts: (before.drafts ?? []).filter((x) => x.id !== id) });
    const r = await fetch(`/api/cabinet/scenarios/drafts/${id}`, { method: 'DELETE' })
      .then((res) => res.json())
      .catch(() => ({ ok: false, error: 'network' }));
    if (!r.ok && before) mutate(before);
    if (r.ok) {
      reload();
      reloadPulse();
      // ОКНО УДАЛЁННОГО БОТА ЗАКРЫВАЕТСЯ (П165, владелец 27.09.2026): родное окно
      // приложения — лист уезжает; страница цифр в браузере (вторая колонка или
      // страница раздела) — уходим к списку; в приложении страница могла приехать родным
      // боковым выездом — тогда снимаем слой (как «‹» страницы, bot-stats.tsx). Экран
      // «Бот удалён.» человек после своего удаления не видит.
      void closeBotWindowOfBot(id);
      if (pick === `stats:${id}`) {
        if (hasNativeLayers() && nativeLayerOpen()) void popLayer();
        else router.push(bcast ? '/app/broadcasts' : '/app/bots');
      }
      return;
    }
    // Не удалилось — модалка сразу (приказ владельца 13.08). bot_busy — единственная
    // клиентская причина (ассистент в чате бота прямо сейчас делает ход): говорим
    // словами «попробуйте через минуту». Всё остальное — НАША поломка: текст без
    // техдеталей, алярм уходит в центр ошибок.
    if (r.error === 'bot_busy') {
      openActionFail(t('failDelTitle'), t('failDelBusy'));
    } else {
      openActionFail(t('failDelTitle'), t('failDelText'));
      reportUiError('draft_delete_failed', `удаление скрипта из списка не прошло: ${r.error ?? 'unknown'}`, {
        draftId: id,
      });
    }
  };

  /** Спросить об удалении, когда веб-карточки в разметке нет (родной список приложения):
   *  ТЕМ ЖЕ системным окном iOS, которым спрашивает ConfirmInline на веб-карточке —
   *  второго диалога в кабинете не заводим. */
  /**
   * ВОПРОС ПРИ УДАЛЕНИИ БОТА (П203, владелец 29.09.2026: «удалил бота, проход которого служит
   * триггером для другого бота — это произошло тихо»). Бот запускает цепочки — они названы
   * в вопросе, кнопка «Удалить всё равно»; молча — больше никогда. Слова ОДНИ на веб-окошко
   * (ConfirmInline) и на родное окно приложения. Заодно вопрос стал «Удалить бота?»: прежний
   * общий ключ кабинета читался «Удалить контакт?».
   */
  const deleteAsk = (id: string) => {
    const names = (data?.drafts ?? [])
      .filter((d) => d.id !== id && d.chainActive != null && (d.chainSources ?? []).includes(id))
      .map((d) => `«${d.title}»`);
    if (names.length === 0) return { question: t('deleteBotSure'), confirmLabel: t('cardDelete'), chains: '' };
    // ДО ТРЁХ ИМЁН — ВСЕ, БОЛЬШЕ — ЧИСЛО И ТРИ ПЕРВЫХ (владелец 29.09.2026: «будет 100 ботов —
    // ты будешь их перечислять в этой модалке?»): системное окно — не место для простыни.
    const words =
      names.length <= 3
        ? t('deleteBotChains', { names: names.join(', ') })
        : t('deleteBotChainsMany', { count: names.length, names: names.slice(0, 3).join(', ') });
    return { question: `${t('deleteBotSure')}\n\n${words}`, confirmLabel: t('deleteAnyway'), chains: words };
  };
  const askDeleteHalf = (id: string) => {
    const ask = deleteAsk(id);
    openHeroAsk(panel, {
      icon: HERO_ICONS.trash,
      title: t('deleteBotSure'),
      ...(ask.chains ? { text: ask.chains } : {}),
      actions: [{ key: 'delete', label: ask.confirmLabel, tone: 'danger', onPick: () => void removeDraft(id) }],
      closeLabel: tc('cancel'),
    });
  };
  const askDeleteNative = (id: string) => {
    if (!hasNativeConfirm()) return;
    const ask = deleteAsk(id);
    void askNativeConfirm({
      question: ask.question,
      confirmLabel: ask.confirmLabel,
      cancelLabel: tc('cancel'),
      destructive: true,
    }).then((yes) => {
      if (yes) void removeDraft(id);
    });
  };

  // ── РОДНЫЕ ОКНА КАРТОЧКИ В ПРИЛОЖЕНИИ (docs/NATIVE-TABS-PLAN.md, волна 3) ─────────
  // ОДНА рука на все окна карточки: сборка умеет родные окна — открываем их (мозг живёт
  // скрытым хозяином, bot-window-native.tsx), не умеет (старая установка, Android) —
  // ниже по коду идёт прежняя веб-шторка, один в один как раньше. Второго решения
  // «где рисовать» в разделе нет.
  const openWindow = (win: BotWindow, extra: Partial<BotWindowHooks> = {}): boolean => {
    if (!nativeStatsSupported()) return false;
    openNativeBotWindow(win, { goScn, reload, reloadPulse, fresh: setFreshId, ...extra });
    return true;
  };

  // «О сценарии» из карточки (Э2.3): ТА ЖЕ форма, что на странице сценария — черновик
  // подтягивает обёртка CardAboutSheet тем же кэшом страницы. Кнопка ✓ шторки зовёт
  // сохранение формы общим ref (шторка открыта одна — одного ref хватает).
  const aboutSaveRef = useRef<(() => void) | null>(null);
  const openCardAbout = (draftId: string) => {
    // В РОДНОМ РАЗДЕЛЕ «Рассылки» это страница стопки поверх витрины (веб-шторка под
    // родным экраном стояла бы пустым листом): состав тот же, форма та же.
    if (bcNative) {
      setBcStack((st) => [...st.filter((k) => k !== bcastAboutKey(draftId)), bcastAboutKey(draftId)]);
      return;
    }
    if (openWindow({ kind: 'about', draftId })) return;
    panel.open({
      title: ts('aboutTitle'),
      ...EDITOR_SHEET,
      // Всё про одного бота — стопкой над окном бота (ТЗ WEB-LOOK Э2.5); в приложении как было.
      ...webOnly({ winKey: BOT_SHEET }),
      actionLabel: ts('aboutSave'),
      actionIcon: 'checkmark',
      onAction: () => aboutSaveRef.current?.(),
      body: <CardAboutSheet draftId={draftId} saveRef={aboutSaveRef} onSaved={reload} />,
    });
  };
  // «Создать дубликат» из «⋯» (ТЗ 17): копия бота или рассылки — той же шторкой, что
  // остальные окна карточки. Готово — окно закрываем, список перечитываем и подсвечиваем
  // новую карточку той же вспышкой, что у только что созданного бота.
  const openDuplicate = (o: { draftId: string; title: string; wizard: boolean }) => {
    // Та же причина, что у «О сценарии»: в родном разделе — страница стопки.
    if (bcNative) {
      setBcStack((st) => [...st.filter((k) => k !== bcastDupKey(o.draftId)), bcastDupKey(o.draftId)]);
      return;
    }
    if (openWindow({ kind: 'dup', draftId: o.draftId, title: o.title, wizard: o.wizard })) return;
    panel.open({
      title: ts('dupTitle'),
      ...EDITOR_SHEET,
      // Всё про одного бота — стопкой над окном бота (ТЗ WEB-LOOK Э2.5); в приложении как было.
      ...webOnly({ winKey: BOT_SHEET }),
      body: (
        <ScenarioDuplicatePanel
          draftId={o.draftId}
          title={o.title}
          wizard={o.wizard}
          onDone={(id) => {
            // Копия создана — человек возвращается к списку, где она подсвечена: окно
            // закрывается ЦЕЛИКОМ (лист копии лежит уровнем над окном бота).
            panel.closeAll();
            setFreshId(id);
            reload();
          }}
        />
      ),
    });
  };
  // «Не включился» на карточке (Г2): тап по чипу открывает окно с причиной и ходом —
  // штатной поверхностью кабинета (в приложении родной слой, в вебе шторка), своего
  // окошка тут не заводим.
  // Журнал запусков бота — ОДНО место открытия на оба входа: пункт «Запуски» в «⋯» и
  // чип сбоев в подвале карточки. Чип зовёт его с onlyErrors: человек нажал на «Сбои: N»
  // и обязан сразу увидеть упавшие прогоны, а не искать их среди удачных.
  const openRuns = (workflowId: string, onlyErrors = false) => {
    if (openWindow({ kind: 'runs', workflowId, onlyErrors })) return;
    panel.open({
      title: onlyErrors ? to('runsErrTitle') : ts('runsBtn'),
      ...EDITOR_SHEET,
      // Всё про одного бота — стопкой над окном бота (ТЗ WEB-LOOK Э2.5); в приложении как было.
      ...webOnly({ winKey: BOT_SHEET }),
      body: <ScenarioRunsSheet workflowId={workflowId} onlyErrors={onlyErrors} />,
    });
  };
  const openFail = (kind: FailKind, href: string | null) => {
    // В ПРИЛОЖЕНИИ — РОДНОЙ НИЗКИЙ ЛИСТ (владелец 19.09.2026: «Бот не включился» приехал
    // страницей окна бота, а должен полушторкой). Слова — те же, что у веб-окна и страницы
    // (lib/bot-fail-text.ts); у «схема не готова» — кнопка «Открыть бота» тем же листом.
    // Старая сборка без метода `notice` — прежняя страница окна бота.
    if (hasNativeNotice()) {
      void showNativeNotice({
        style: 'sheet',
        title: t('failTitle'),
        text: t(botFailTextKey(kind, true)),
        ...(kind === 'lint' && href ? { primary: t('failOpenCta') } : {}),
        haptic: 'error',
      }).then((ok) => {
        if (ok && kind === 'lint' && href) goScn(href);
      });
      return;
    }
    if (openWindow({ kind: 'fail', fail: kind, href })) return;
    panel.open({
      title: t('failTitle'),
      ...EDITOR_SHEET,
      // Всё про одного бота — стопкой над окном бота (ТЗ WEB-LOOK Э2.5); в приложении как было.
      ...webOnly({ winKey: BOT_SHEET }),
      body: <EnableFailBody kind={kind} href={href} onClose={() => panel.close()} />,
    });
  };
  // Осечка выключения/удаления (приказ владельца 13.08): клиент видит модалку СРАЗУ,
  // молча не оставляем. Текст без техдеталей (запрет №7) — такое чиним мы, алярм
  // владельцу уходит reportUiError по месту вызова.
  const openActionFail = (title: string, text: string) => {
    // В родном разделе «Рассылки» — страница стопки: веб-шторка под родным экраном не
    // видна вовсе, а молча оставлять человека без ответа нельзя (приказ 13.08).
    if (bcNative) {
      setBcMsg({ title, text });
      setBcStack((st) => [...st.filter((k) => k !== 'msg'), 'msg']);
      return;
    }
    // В ПРИЛОЖЕНИИ — РОДНОЙ НИЗКИЙ ЛИСТ (владелец 18.09.2026: «ошибка должна была быть
    // полушторкой», скрин «Бот выключен» страницей окна бота). Метод моста `notice`
    // (волна 3, NoticeCard.swift): лист по содержимому с вибрацией отказа. Старая сборка
    // без него — прежняя страница окна бота.
    if (hasNativeNotice()) {
      void showNativeNotice({ style: 'sheet', title, text, haptic: 'error' });
      return;
    }
    if (openWindow({ kind: 'note', title, text })) return;
    panel.open({
      title,
      ...EDITOR_SHEET,
      // Всё про одного бота — стопкой над окном бота (ТЗ WEB-LOOK Э2.5); в приложении как было.
      ...webOnly({ winKey: BOT_SHEET }),
      body: (
        <FPane>
          <FHint>{text}</FHint>
        </FPane>
      ),
    });
  };
  /**
   * ССЫЛКА В TELEGRAM, КОТОРУЮ НИКТО НЕ ВСТРЕЧАЕТ (UT 14.10.2026 п.10, итерация 2 п.26): пока предупреждение есть, тап
   * по ВСЕЙ карточке бота (и по треугольнику у названия) открывает окно «Telegram-бот не настроен» с объяснением и
   * двумя кнопками: «Перейти к аналитике» — окно бота (то, что открыл бы тап по карточке), «Больше не беспокоить» —
   * флаг на бота+ссылку на сервере (tg-link-mute), после него ни окна, ни треугольника.
   */
  const muteTgLink = (draftId: string, w: TgLinkWarnView) => {
    void fetch(`/api/cabinet/scenarios/drafts/${draftId}/tg-link-mute`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ credId: w.credId, code: w.code }),
    })
      .catch(() => undefined)
      .finally(() => reload());
  };
  const openTgLinkWarn = (draftId: string | null, w: TgLinkWarnView, openBot: () => void) => {
    const words = tgLinkWarnWords(w, {
      title: t('tgLinkWarnTitle'),
      body: (v) => t('tgLinkWarnBody', v),
      analytics: t('tgLinkWarnAnalytics'),
      mute: t('tgLinkWarnMute'),
    });
    if (hasNativeNotice()) {
      // РОДНОЕ ОКНО (лист сообщения). «Перейти к аналитике» — главной кнопкой, «Больше не беспокоить» — второй
      // (APP-CLAIMS-1020 П1). Сборка 251+ отвечает `choice` (закрытие — ничего); 1.1 — только «да/нет», там «нет»
      // (вторая кнопка или закрытие) — ничего. Разбор ответа — tgLinkWarnAction.
      void showNativeNoticeFull({ style: 'sheet', ...tgLinkWarnNotice(words) }).then((r) => {
        const act = tgLinkWarnAction(r);
        if (act === 'mute') {
          if (draftId) muteTgLink(draftId, w);
        } else if (act === 'analytics') openBot();
      });
      return;
    }
    panel.open({
      title: words.title,
      ...EDITOR_SHEET,
      // ПОЛУШТОРКА ПО СОДЕРЖИМОМУ (UT 14.10 п.30): стартовая высота коротких листов, точную ставит FPane fit.
      ...webOnly({ sheetDetent: 0.4 }),
      body: (
        <FPane fit>
          <FHint>{words.text}</FHint>
          <KButton
            tone="primary"
            action
            block
            onClick={() => {
              panel.close();
              openBot();
            }}
          >
            {words.analytics}
          </KButton>
          {draftId ? (
            <KButton
              action
              block
              onClick={() => {
                panel.close();
                muteTgLink(draftId, w);
              }}
            >
              {words.mute}
            </KButton>
          ) : null}
        </FPane>
      ),
    });
  };
  /** Значок-предупреждение у названия (рядом со значками-ссылками формы и чата). */
  const tgWarnMark = (draftId: string | null, w: TgLinkWarnView, openBot: () => void) => (
    <span
      className="scn-name-link scn-name-link--warn"
      role="button"
      title={t('tgLinkWarnText')}
      aria-label={t('tgLinkWarnText')}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        openTgLinkWarn(draftId, w, openBot);
      }}
    >
      <WarnSvg />
    </span>
  );
  /** ПЛАШКА ОПЛАТЫ (К17.4 п.1, К15 П9 п.1) — ОБЩАЯ рука кабинета: её же показывает
   *  страница цепочки и родное окно приложения (billing.tsx, useBillingPlate). В
   *  приложении она без цен и ссылок — слова показывает своё окно раздела. */
  const openBillingPlate = useBillingPlate((title, text) => openActionFail(title, text));

  // «Проверить» из карточки (Э2.3): живой прогон ведёт CardCheckHost тем же
  // useScenarioRun, что страница сценария; здесь — только чей прогон сейчас идёт.
  const [checkRun, setCheckRun] = useState<string | null>(null);
  // Тост пересечений (Э3, Р2): включение не блокирует — жёлтая пилюля тем же местом,
  // что зелёный тост «Проверено», сама гаснет; текст длинный, держим дольше зелёного.
  const [warnToast, setWarnToast] = useState<string | null>(null);
  const warnTimer = useRef<number | null>(null);
  const showWarnToast = (text: string) => {
    setWarnToast(text);
    if (warnTimer.current) window.clearTimeout(warnTimer.current);
    warnTimer.current = window.setTimeout(() => setWarnToast(null), 8000);
  };
  useEffect(
    () => () => {
      if (warnTimer.current) window.clearTimeout(warnTimer.current);
    },
    [],
  );

  // Тумблер черновика (владелец 2026-07-21: статусов «черновик/установлен/проверен» нет,
  // есть только выкл/вкл): включение = штатный путь «Запустить» (установка на сервер +
  // проверка + активация). Не получилось — честный баннер «исправь ошибки», сам черновик
  // остаётся выключенным; получилось — список перечитываем, карточка становится готовой.
  // ЗАПУСК ОДНОГО БОТА — ОДИН ЗА РАЗ (владелец 19.09.2026: «запуск не завершён и делать
  // ещё один запуск — твоя ошибка»): пока ответ первого не пришёл, второй тап/автозапуск
  // по тому же черновику не уходит. Сервер держит свой замок, здесь — чтобы и запрос
  // не отправлять.
  const runningRef = useRef<Set<string>>(new Set());
  const runDraft = async (d: DraftItem) => {
    if (runningRef.current.has(d.id)) return;
    runningRef.current.add(d.id);
    setBusy(d.id);
    clearFail(d.id);
    const r = await fetch(`/api/cabinet/scenarios/drafts/${d.id}/run`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({}),
    })
      .then((res) => res.json())
      .catch(() => ({ ok: false }));
    runningRef.current.delete(d.id);
    setBusy(null);
    const run = r?.run as { ok?: boolean; reason?: string } | undefined;
    if (r.ok && run?.ok) {
      reload();
      reloadPulse();
    } else if (!r.ok || !run) {
      // До разбора запуска даже не дошли (обрыв сети, наш роут упал) — это НАША поломка,
      // клиенту без подробностей (запрет №7 CLAUDE.md). Модалка — сразу (приказ 13.08).
      markFail(d.id, 'tech');
      openFail('tech', null);
    } else {
      // Причину юзеру не расшифровываем (техжаргон линта — для бота): роут уже написал
      // системную строку в чат сценария, там ассистент и чинит. Исключение — пустой
      // баланс: чинить в сценарии нечего, честно зовём пополнить. Модалка с причиной и
      // ходом открывается сразу; чип «Не включился» остаётся для повторного открытия.
      // КОНСТРУКТОР СКРЫТ (BUILDER_HIDDEN): «откройте бота и исправьте» вести некуда — бот
      // из мастера собирали мы, отказ линта наш, клиент видит «техническая» без хода, а
      // владелец получает алярм с сервера (scenario-run). Владелец 02.09: «конструктор не
      // должен видеть пользователю никогда».
      const kind: FailKind = run.reason === 'no_balance' ? 'balance' : BUILDER_HIDDEN ? 'tech' : 'lint';
      markFail(d.id, kind);
      openFail(kind, kind === 'lint' ? scnHref(d.id, !!d.wizard) : null);
    }
  };

  // Шторки подтверждения тумблера БОЛЬШЕ НЕТ (владелец 2026-08-09, Э3 BOTS-MENU-NOTIFY:
  // «она не нужна») — тап действует сразу, ход и так виден чипом «включаем…/выключаем…».
  // Пре-чек мягких пересечений (Ф5) остаётся: включение спрашивает сервер
  // checkOnly/toggle-check, имена конкурентов уходят ТОСТОМ-предупреждением (Р2) и
  // включение не блокируют.
  const preCheck = async (url: string, init?: RequestInit): Promise<string[]> => {
    const r = await fetch(url, init)
      .then((res) => res.json())
      .catch(() => null);
    return Array.isArray(r?.warnings) ? (r.warnings as string[]) : [];
  };
  const warnNames = (warnings: string[]) => {
    if (warnings.length === 0) return;
    showWarnToast(t('conflictWarn', { other: warnings.map((w) => `«${w}»`).join(', ') }));
  };
  const askScenario = async (s: Scenario) => {
    // ПОГАШЕН ПО НЕОПЛАТЕ (К17.4 п.1): включить нельзя — показываем плашку и НЕ ходим на
    // сервер: ответ там всё равно один, а лишний зов — лишнее ожидание человеку.
    if (!s.active && s.billingOff) {
      openBillingPlate();
      return;
    }
    if (s.active) {
      await confirmOff(s.id, () => toggle(s));
      return;
    }
    setChecking(s.id);
    const warnings = await preCheck(`/api/cabinet/scenarios/${s.id}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ active: true, checkOnly: true }),
    });
    setChecking(null);
    warnNames(warnings);
    await toggle(s);
  };
  const askDraft = async (d: DraftItem) => {
    // Погашен по неоплате — тот же ответ, что у готового бота (К17.4 п.1).
    if (d.billingOff) {
      openBillingPlate();
      return;
    }
    setChecking(d.id);
    const warnings = await preCheck(`/api/cabinet/scenarios/drafts/${d.id}/toggle-check`);
    setChecking(null);
    warnNames(warnings);
    await runDraft(d);
  };

  // ЗАПУСК СРАЗУ ПОСЛЕ СОЗДАНИЯ (владелец 18.09.2026: «сейчас бот не запущен, а я хочу,
  // чтобы ты его сразу же запускал»). Создание (useCreateGo, «Вручную») оставляет метку
  // scn:autorun; как только новый черновик есть в списке, он включается ТЕМ ЖЕ ходом,
  // что тап по тумблеру карточки (askDraft: пре-чек пересечений, чип «включаем…»,
  // модалка отказа, плашка неоплаты) — второй дороги запуска нет. Метка одноразовая,
  // живёт 2 минуты; рассылки (свой запуск кнопкой «Отправить») не трогаем.
  const autorunRef = useRef<string | null>(null);
  useEffect(() => {
    if (!paneActive) return;
    try {
      const raw = sessionStorage.getItem('scn:autorun');
      if (!raw) return;
      const v = JSON.parse(raw) as { id?: string; at?: number };
      sessionStorage.removeItem('scn:autorun');
      if (v.id && Date.now() - (v.at ?? 0) < 120_000) autorunRef.current = v.id;
    } catch {
      /* приватный режим */
    }
  }, [paneActive]);
  useEffect(() => {
    const id = autorunRef.current;
    if (!id) return;
    const d = (data?.drafts ?? []).find((x) => x.id === id);
    if (!d) return;
    autorunRef.current = null;
    if (bcast && bcItems.has(id)) return;
    void askDraft(d);
    // askDraft пересобирается каждый рендер; ход один — по появлению черновика в данных.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, bcast, bcItems]);

  // ── ОТБОР, ПОРЯДОК И НАЧИНКА КАРТОЧЕК — ДО ЛЮБЫХ РАННИХ ВЫХОДОВ ──────────────
  // Раньше это считалось перед самой разметкой, но родной список «Ботов» приложения
  // (lib/bots-native.ts) кормится ТЕМИ ЖЕ рядами, а его хук обязан зваться безусловно
  // (правило хуков React). Поэтому отбор переехал сюда целиком: второго отбора и второй
  // сборки карточки в разделе нет — разойтись им негде.
  /**
   * НАЧИНКА КАРТОЧКИ РАССЫЛКИ — все три ифа в одном месте. У бота возвращает null, и
   * карточка рисуется ровно как раньше: слов, цифр и кнопок бота рассылка не трогает.
   */
  const bcCard = (id: string) => {
    if (!bcast) return null;
    const item = bcItems.get(id) ?? null;
    const status: BroadcastStatus = item?.status ?? 'draft';
    const chip0 = statusChip(status);
    // APP-CLAIMS-1020 ред. 6, П16: запланированная на дату — «Запланирована на <когда>» (дата раньше жила в описании).
    const at = status === 'scheduled' && item?.mode === 'date' && item.runAt ? new Date(item.runAt) : null;
    const chip =
      at && !Number.isNaN(at.getTime())
        ? { ...chip0, label: tb('stScheduledAt', { when: at.toLocaleString(locale, { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }) }) }
        : chip0;
    const launch = item?.launch ?? null;
    // Р3 ред. 7: одна строка «Отправлено N · доставлено M · <когда>» (формат строки «Отправки» отчёта).
    const sentLine = launch
      ? tb('cardSent', {
          sent: launch.started,
          ok: launch.delivered,
          when: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(launch.createdAt)),
        })
      : '';
    return {
      status,
      /** APP-CLAIMS-1020 ред. 7, Р3: на месте описания — ПРЕВЬЮ СООБЩЕНИЯ (текст письма или слово вида первого вложения,
       *  сервер — `bcastPreviewParts`); описание черновика в карточку рассылки не идёт. */
      desc: item?.preview || (item?.previewKind ? tm(item.previewKind) : ''),
      stLabel: chip.label,
      stTone: chip.tone,
      /** У отправленной статуса нет вовсе — ни слова, ни точки (Р3): подвал карточки не рисуется. */
      noState: chip.label === '',
      /** КРАТКИЕ ЦИФРЫ С ДАТОЙ. Ни разу не отправляли — цифр нет вовсе, нули тут ничего не объясняют. Строка ОДНА на
       *  веб-разметку и на родной экран приложения — второго счёта у карточки нет. */
      numsText: sentLine,
      chart: launch ? (
        <div className="scn-card-nums">
          <span className="scn-runs">{sentLine}</span>
        </div>
      ) : null,
      /** «Остановить» у идущей и ждущей своего повода, «Отправить» у остальных — те же
       *  правила, что у кнопок страницы цифр (broadcast-launch.tsx). */
      action: (status === 'sending' || status === 'scheduled' ? 'stop' : 'send') as 'send' | 'stop',
    };
  };
  // Готовые сценарии, собранные в кабинете: связь по workflowId — такой ряд ведёт в
  // конструктор черновика (B6), а сам черновик из блока «Черновики» уходит (не дублим).
  const readyIds = new Set((data?.scenarios ?? []).map((s) => s.id));
  const draftByWorkflow = new Map(
    (data?.drafts ?? []).filter((d) => d.workflowId).map((d) => [d.workflowId as string, d]),
  );
  // Черновики строителя — не требуют инстанса, показываются в любом состоянии сервера.
  // Deployed-черновик без ряда в «готовых» (workflow удалили на сервере / ключ протух)
  // остаётся здесь — иначе до него не добраться.
  // Выбран источник во второй колонке (Telegram, Instagram, форма…) — показываем только
  // его скрипты; подпись та же, по которой список группируется ниже (владелец 2026-08-01).
  // Отбор списка (владелец 2026-08-01): своя КАТЕГОРИЯ приходит выбором колонки (адресом),
  // канал и аккаунт — фильтрами страницы (параметрами того же адреса).
  const catSlug = pick ? scriptsPickSlug(pick) : null;
  // Имена категорий — из строк личного списка (в них может быть суффикс цвета).
  const catNames = catList(cats).map((c) => c.name);
  const catName = catSlug ? bySlug(catNames, catSlug) : null;
  // ПАПКИ-КАТЕГОРИИ (владелец 13.09.2026, П81): капсула над списком как у «Чатов» — «Все»
  // и категории, в которых есть боты. В приложении отбор по папке делает натив (ряды едут
  // все, страница папки — ряды её категории), поэтому категория адреса список НЕ режет;
  // на сайте — та же капсула, отбор адресом, как было у колонки категорий.
  const foldersHidden = !!data?.foldersHidden;
  const catFilterOn = nativeDraws ? null : catSlug;
  // В адресе фильтры стоят латиницей (?channel=telegram&account=chpttbtbot) — сравниваем
  // слаги, чтобы ссылка была читаемой и не разъезжалась с подписью на экране.
  const keep = (d: { category?: string | null; categories?: string[]; triggerType?: string | null; account?: string | null }) =>
    (!catFilterOn || (!!catName && draftFolders(d).includes(catName))) &&
    (!channel || slugify(triggerLabel(d.triggerType, locale)) === channel) &&
    (!account || slugify(d.account ?? '') === account);
  const needle = q.trim().toLowerCase();
  const drafts = (data?.drafts ?? [])
    // БОТОВ БЕЗ АНКЕТЫ В СПИСКЕ НЕТ (владелец 31.08.2026: «убрать»), пока спрятан
    // конструктор — открыть и починить их всё равно нечем. Сами боты живы и работают,
    // их цифры остаются в «Аналитике»; вернётся конструктор — вернутся и карточки.
    .filter((d) => !BUILDER_HIDDEN || !!d.wizard)
    // ИФ №1 (вид триггера): витрина берёт СВОЮ половину общего разбиения — что здесь
    // показано, того нет в соседнем разделе, и наоборот. Условие — общий фильтр.
    .filter((d) => keepKind(d, kind))
    .filter((d) => d.status === 'draft' || !d.workflowId || !readyIds.has(d.workflowId))
    .filter(keep)
    // Фильтр состояния. У бота их два (владелец 2026-08-04): черновик = выключено, при
    // «Включено» его нет. У рассылки состояний пять — они приходят статусом запуска.
    .filter((d) => (bcast ? !state || bcStatusOf(d.id) === state : state !== 'on'))
    .filter((d) => !needle || d.title.toLowerCase().includes(needle));
  // Сводка разворота (владелец 2026-08-02, донос макета): «N скриптов · M работают ·
  // за неделю K прохождений». Те же фильтры, что и у списка ниже.
  const keepScn = (s: Scenario) => {
    const d = draftByWorkflow.get(s.id);
    // Бот без анкеты в списке не показывается (владелец 31.08.2026) — см. отбор
    // черновиков выше. Ряд без черновика анкеты не имеет по определению.
    if (BUILDER_HIDDEN && !d?.wizard) return false;
    // Та же половина общего разбиения, что у черновиков: схему видно через черновик, а у
    // сценария из редактора судим по типу узла запуска.
    if (!keepKind({ trigger: d?.triggerType ?? s.triggerType, broadcast: d?.broadcast }, kind)) {
      return false;
    }
    return (
      keep({
        category: d?.category ?? null,
        categories: d?.categories,
        triggerType: d?.triggerType ?? s.triggerType,
        account: d?.account ?? null,
      }) &&
      // Состояние: у бота on/off (владелец 2026-08-04), у рассылки — статус её запуска.
      (bcast
        ? !state || bcStatusOf(d?.id ?? s.id) === state
        : !state || (state === 'on' ? s.active : !s.active)) &&
      (!needle || s.name.toLowerCase().includes(needle))
    );
  };
  const shownScns = (data?.scenarios ?? []).filter(keepScn);
  /** ПОРЯДОК КАРТОЧЕК: включённые сверху (владелец 2026-08-01), дальше черновики и
   *  выключенные. Один набор на веб-разметку и на родной список приложения. */
  // Ряд сценария несёт СВОЙ черновик анкеты (по воркфлоу), как веб-карточка ниже:
  // без него родная карточка приложения теряла логин, цифры и «настроить» у каждого
  // выкаченного бота (найдено 01.09 на тест3: пустая карточка, только вкл/выкл).
  const withDraft = (scn: Scenario) => ({ scn, draft: draftByWorkflow.get(scn.id) });
  const ordered: Array<{ scn?: Scenario; draft?: DraftItem }> = [
    ...shownScns.filter((sc) => sc.active).map(withDraft),
    ...drafts.map((draft) => ({ draft })),
    ...shownScns.filter((sc) => !sc.active).map(withDraft),
  ];
  // ПАПКИ СЧИТАЮТСЯ ПО ВСЕМ ПОКАЗАННЫМ КАРТОЧКАМ (владелец 14.09.2026: «положил бота в
  // папку и ничего не появилось»). Раньше отбор шёл только по `drafts` — а туда не
  // попадают ЗАПУЩЕННЫЕ боты: их карточка берётся из готовых сценариев. У человека в
  // папке лежал именно запущенный бот, папка считалась пустой, и полоса не появлялась.
  // Считать надо ПОСЛЕ `ordered` — он и есть список карточек экрана (прошлое падение
  // «Cannot access before initialization» случилось ровно из-за счёта раньше времени).
  // ПОЛОСА НЕ ТЕРЯЕТ СОСЕДНИЕ ПАПКИ (партия Н4, 03.10.2026): с выбранной папкой `ordered`
  // уже отобран по ней, и полоса сжималась до одной капсулы — перейти в соседнюю папку можно
  // было только через «Все». В приложении полоса считается по всем рядам (отбор делает
  // натив), колонка трёх слоёв — тоже по всем; здесь так же: те же карточки, но без отбора
  // по папке (и без поиска/фильтров — папка с ботами есть, даже если они сейчас не видны).
  const catsInUse = catNames.filter((n) =>
    (data?.drafts ?? []).some(
      (d) => draftFolders(d).includes(n) && (!BUILDER_HIDDEN || !!d.wizard) && keepKind(d, kind),
    ),
  );
  // КАПСУЛА ПОЯВЛЯЕТСЯ С ПЕРВОЙ ПАПКОЙ (П104, владелец 13.09.2026: «пока папки не
  // появились, ничего не показывать; заводит их пользователь сам — в настройках или в
  // „О боте“»): «Все» + папки с ботами + шестерёнка.
  // ПОЛОСА САЙТА ПОКАЗЫВАЕТ ВСЕ ПАПКИ, И ПУСТЫЕ ТОЖЕ (ТЗ WEB-CAUSES О13/TODO 380, кадр владельца
  // 1c31213a: в полосе приложения стоит пустая «ТЕСТ папка»): пустую папку человек открывает
  // и видит пустое состояние (М53), а не ищет её адресом. Приложению ряды папок едут прежние
  // (`catsInUse`) — что видит приложение, этот пункт не меняет.
  const stripCats = isApp ? catsInUse : catNames;
  const botFolders =
    !bcast && !foldersHidden && stripCats.length > 0
      ? [{ id: 'all', name: t('foldersAll'), unread: 0 }, ...stripCats.map((n) => ({ id: n, name: n, unread: 0 }))]
      : null;
  // Отбор, из-за которого список бывает пуст, хотя боты есть: поиск, канал, аккаунт,
  // «включено/выключено» и своя категория (все они приходят адресом страницы).
  const filtered = !!(needle || channel || account || state || catFilterOn);
  // БОТЫ У УЧЁТКИ ЕСТЬ ВООБЩЕ (до любого отбора — папки, поиска, фильтров): от этого зависит,
  // чем говорит пустой список (ТЗ WEB-CAUSES М53, владелец 05.10 21:35). Стрелка «потяните» —
  // только у того, у кого ботов нет совсем; пустая папка при живых ботах — пустое состояние.
  const anyBots =
    (data?.drafts ?? []).some((d) => (!BUILDER_HIDDEN || !!d.wizard) && keepKind(d, kind)) ||
    (data?.scenarios ?? []).some((sc) => {
      const d = draftByWorkflow.get(sc.id);
      if (BUILDER_HIDDEN && !d?.wizard) return false;
      return keepKind({ trigger: d?.triggerType ?? sc.triggerType, broadcast: d?.broadcast }, kind);
    });
  // Признак — полосе-пасхалке (ТЗ USERTEST-1014 п.2): без ботов она сама не закрывается, с ботами —
  // через 10 с. Только список «Ботов» (не «Рассылки») и только когда список доехал.
  const hasBotsKnown = !bcast && !!data;
  useEffect(() => {
    if (hasBotsKnown) setQuickHasBots(anyBots);
  }, [hasBotsKnown, anyBots]);
  // ПАПКИ ЛИСТАЮТСЯ ХОДОМ (ТЗ ANIM-FIX О5 П6, folder-pager.tsx). Выбор папки — тем же
  // мгновенным переходом стека, что пункт колонки (onPick → navTo, history.pushState): прежний
  // router.push ждал ответа сервера, и список подменялся с задержкой.
  const folderPager = useFolderPager(
    botFolders?.map((f) => f.id) ?? [],
    catName ?? 'all',
    (id) => {
      const to: ScriptsPick = id === 'all' ? 'my' : `cat:${slugify(id)}`;
      if (onPick) onPick(to);
      else router.push(scriptsPathFor(to));
    },
  );
  // ── ПРОГРЕВ ОКОН БОТОВ: ВИДИМЫЕ КАРТОЧКИ И ПОЛЭКРАНА ВПЕРЁД (PREFETCH-CAREFUL-TZ §2 п.1) ──
  // Карточки с черновиком помечены `data-warm-id`; список свежий (не stale) — хук греет попавшие в окно. Родной
  // список приложения карточек в DOM не держит — там первые NATIVE_WARM_FIRST ботов.
  const warmDraftIds = useMemo(() => (warmIds ? warmIds.split(',') : []), [warmIds]);
  const warmInView = useCallback((id: string) => warmRef.current(id), []);
  // Корень — страница папки (FolderPage): в ней стоит сетка карточек; состояния «сервер готовится» без папок не
  // греются (там одни черновики, их добирает палец).
  useWarmInView(folderPager.wrapRef, warmDraftIds, warmInView, !nativeDraws);
  const nativeFirstIds = nativeDraws ? warmDraftIds.slice(0, NATIVE_WARM_FIRST).join(',') : '';
  useEffect(() => {
    if (nativeFirstIds) nativeFirstIds.split(',').forEach((id) => warmRef.current(id));
  }, [nativeFirstIds]);

  /** ВСЁ, ЧТО РЕШАЕТ КАРТОЧКА: куда ведёт тап, что лежит в её «⋯» и чем она подписана.
   *  ОДНА рука на веб-разметку и на родной список приложения — вторых правил у натива
   *  нет (канон NATIVE-TABS-PLAN: натив только рисует). */
  const cardCore = (scn: Scenario | null, draft: DraftItem | null) => {
    const id = scn ? scn.id : draft!.id;
    const title = draft?.title ?? scn?.name ?? '';
    const on = scn ? !!scn.active : draft?.chainActive === true;
    // Пока спрятан конструктор (владелец 31.08.2026): у бота БЕЗ анкеты редактировать
    // нечего — «Редактировать» из меню уходит, адрес ведёт на карточку с цифрами.
    const edit = draft && !(BUILDER_HIDDEN && !draft.wizard) ? scnHref(draft.id, !!draft.wizard) : null;
    // Страница цифр знает бота и по черновику, и по воркфлоу (ряд без черновика — тоже бот).
    const stats = statsHref(draft?.id ?? id);
    // Рассылка ведётся по своему черновику: цифры и кнопки запуска живут у него.
    const actId = draft?.id ?? id;
    const bc = bcCard(actId);
    // ЦЕПОЧКА ОТКРЫВАЕТСЯ СВОЕЙ СТРАНИЦЕЙ ВСЕГДА (владелец 16.09.2026, М1.5): выключенная
    // цепочка — не «недособранная», её состав уже готов, и вести её тап в правку незачем;
    // «Редактировать» стоит в шапке самой страницы.
    const chain = draft?.chainActive != null;
    // КУДА ВЕДЁТ КЛИК ПО КАРТОЧКЕ — РЕШАЕТ СОСТОЯНИЕ БОТА (ТЗ 4 CABINET-UX-FIXLIST):
    // включённый уже собран, человеку нужны цифры; выключенный открывается редактором.
    // У рассылки «включённость» называется статусом запуска — правило перехода общее.
    // ЦЕПОЧКА БЕЗ ЖИВЫХ ВХОДОВ — В РЕДАКТИРОВАНИЕ (П203, владелец 29.09.2026: «в таких
    // случаях лучше редактирование открывать»): цифры ей нечем набирать, человеку нужно
    // поставить другой бот — тап ведёт в мастер. Родной список читает тот же href.
    // ТЕЛЕФОН В БРАУЗЕРЕ — КАК В iOS: тап по карточке ЛЮБОГО бота открывает его окно
    // (цифры, тумблер, «Редактировать» в меню «⋯»); правка — из окна или из «⋯» карточки
    // (ТЗ WEB-PARITY-3 6.0 п.3, FINDINGS-2 п.1: выключенный бот открывался сразу мастером,
    // и окно с телефона было недостижимо). Рассылки — по своему правилу.
    // ШИРОКИЙ ЭКРАН В БРАУЗЕРЕ — ТАК ЖЕ (пакет Д2.2 п.13, владелец 04.10.2026, Закон 5 п.9): окно
    // бота там — панель 460 с тем же телом, что у телефона; тап по выключенному боту открывал мастер
    // «Шаг 1 из 6». Правка — из «⋯» окна или карточки, как на телефоне.
    const webWin = !isApp;
    const href =
      chain && draft?.chainNoEntry && edit
        ? edit
        : chain || (bc ? broadcastCardTarget(bc.status) === 'stats' : on || (webWin && !bc))
          ? stats
          : (edit ?? stats);
    const menu: CardMenuOpts = {
      id,
      statsHref: stats,
      statsRow: draft?.id ?? id,
      editHref: edit,
      on,
      bcAction: bc?.action ?? null,
      isBc: !!bc,
      busy: bc ? bcActs.busyId === actId : busy === id || checking === id,
      onToggle: bc
        ? () => bcActs.act(actId, bc.action)
        : scn
          ? () => askScenario(scn)
          : draft!.chainActive != null
            ? () => (draft!.chainActive ? confirmOff(draft!.id, () => toggleChain(draft!)) : toggleChain(draft!))
            : () => askDraft(draft!),
      draftId: draft?.id ?? null,
      title,
      wizard: !!draft?.wizard,
      chain,
      workflowId: scn ? scn.id : (draft?.workflowId ?? null),
      // Шагов у ряда-черновика в выгрузке нет — судим по следам: есть триггер или
      // сервисы стопки логотипов (оба берутся из узлов схемы). Готовый на сервере —
      // шаги заведомо есть (пустым его не задеплоить).
      hasNodes: scn ? true : !!(draft!.triggerType || (draft!.services && draft!.services.length > 0)),
      deployed: scn ? true : draft!.status !== 'draft',
    };
    return { id, title, on, edit, stats, bc, href, menu };
  };

  /** СОСТАВ И РУКА МЕНЮ «⋯» — одна на три показа: веб-выпадашка, родное меню у точки и
   *  тап «⋯» на родном списке приложения. Второго меню карточки в кабинете нет (Э2
   *  BOTS-MENU-NOTIFY): пункты приходят из общего scenarioMenuItems. */
  const cardMenuOf = (o: CardMenuOpts, opts: { onStatsPage?: boolean } = {}) => {
    // ИФ №3: у рассылки первый пункт меню — «Отправить» или «Остановить» (что уместно,
    // решает её статус), у бота — «Включить/Выключить».
    const toggleLabel = o.bcAction
      ? o.bcAction === 'stop'
        ? tb('stop')
        : tb('send2')
      : o.on
        ? t('stTurnOff')
        : t('stTurnOn');
    const state = {
      t: ts,
      // «Аналитика» — по состоянию бота (владелец 31.08.2026: включён — «Аналитика»,
      // нажатие на карточку меняет смысл по тому же признаку). «РЕДАКТИРОВАТЬ» — ВСЕГДА
      // (владелец 03.09.2026: «убирать из списка я не просил»): у включённого бота пункт
      // прятался вместе с тем правилом, и править его было неоткуда, кроме страницы.
      // «Аналитика» — по состоянию бота (включён), но НЕ на самой странице цифр: вести
      // человека туда, где он стоит, незачем (П42 владельца 12.09.2026).
      // У карточки БОТА «Аналитики» в меню НЕТ ни на одной ширине (владелец 06.10.2026, ТЗ WEB-CAUSES
      // М86/В12; кадры iOS B14-card-menu / B14-off-bot-long-press: Включить · Редактировать · О боте ·
      // Дубликат · Удалить): окно бота с цифрами открывает тап по карточке (М81 — и на десктопе).
      // У РАССЫЛКИ (isBc) пункт остаётся — кадр iOS R06-broadcast-card-menu: «Остановить · Аналитика ·
      // Редактировать …».
      stats: o.on && !opts.onStatsPage && !!o.isBc,
      edit: !!o.editHref,
      // Цепочку не дублируем (М1.6): копия черновика уехала бы без самой цепочки.
      duplicate: !o.chain,
      // П18: у рассылки — «О рассылке» / «Удалить рассылку».
      bcast: !!o.isBc,
      hasNodes: o.hasNodes,
      deployed: !!o.workflowId,
      hasDraft: !!o.draftId,
      running: checkRun === o.draftId,
      deleteBusy: delBusy === o.draftId,
      deleteDeployed: o.deployed,
    };
    // Один обработчик пунктов на все три меню.
    const pickItem = (k: string) => {
      // Журнал (П79): какой пункт выбран на странице бота — по жалобе «не работает» видно.
      if (opts.onStatsPage) noteBridgeEvent('botMenu', k);
      if (k === 'toggle') o.onToggle();
      // ОБА ПУТИ КАРТОЧКИ ВСЕГДА В МЕНЮ (ТЗ 4): клик по самой карточке ведёт то в цифры,
      // то в редактор — из меню доступно и то, и другое.
      // В РОДНОМ РАЗДЕЛЕ «Рассылки» цифры и журнал — страницы стопки: веб-слой под
      // родным экраном был бы не виден вовсе.
      else if (k === 'stats') {
        if (bcNative) {
          setBcStack((st) => [...st.filter((k2) => k2 !== bcastStatsKey(o.id)), bcastStatsKey(o.id)]);
        }
        // ОДНО ОКНО АНАЛИТИКИ НА ОБА ВХОДА (Н38): тап по включённой карточке уже открывает
        // родное окно цифр — пункт меню обязан открывать ЕГО ЖЕ, а не веб-страницу слоем.
        else if (
          !o.statsRow ||
          // «⋯» шапки родной страницы бота — ЭТО ЖЕ меню (П42): состав и руки пунктов
          // остаются здесь, окно только просит показать его у рамы своей кнопки.
          !openWindow(
            { kind: 'stats', rowKey: o.statsRow },
            {
              menu: (rect) => openCardMenuNative(o, rect, { onStatsPage: true }),
              toggle: windowToggle(o.id, o.draftId),
              // APP-CLAIMS-1020 ред. 6, П20: «Редактировать» отчёта рассылки — та же рука, что пункт меню.
              ...(o.isBc && o.editHref ? { edit: () => cardMenuOf(o, { onStatsPage: true }).pickItem('edit') } : {}),
            },
          )
        )
          goScn(o.statsHref);
      }
      // СО СТРАНИЦЫ БОТА (владелец 13.09.2026, П79: «Редактировать» и «Создать дубликат»
      // не работают): окно — системный лист, и мастер (или второе окно) поверх него
      // UIKit не показывает (Н45). Сначала закрываем окно, потом идём дальше; из списка
      // карточек — как было.
      // СО СТРАНИЦЫ БОТА (владелец 13.09.2026, П98/П103: «где открыл, там сверху открыл»):
      // мастер встаёт ПОВЕРХ окна (натив показывает его над верхним предъявленным
      // контроллером), «О боте» и «Дубликат» — страницами этого же окна. Окно не закрываем.
      else if (k === 'edit' && o.editHref) goScn(o.editHref, o.wizard ? o.draftId : null);
      // Вопрос задаёт скрытый ConfirmInline: он умеет и родное окно приложения,
      // и окошко сайта — свой диалог тут был бы копией. На родном списке веб-карточки
      // в разметке нет вовсе — спрашиваем тем же системным окном напрямую.
      else if (k === 'delete' && o.draftId) {
        // QUICK-START §11.34 п.13 (владелец 30.10): телефон в браузере — вопрос полушторкой с героем, как «Выйти» и
        // «Удалить учётную запись» и как в приложении (`bot.ts deleteAct`, `style: 'hero'`); десктоп — как было.
        if (!isApp && layoutMode === 'phone') {
          askDeleteHalf(o.draftId);
          return;
        }
        const ask = delAskRef.current[o.draftId];
        if (ask) ask();
        else askDeleteNative(o.draftId);
      } else if (k === 'about' && o.draftId) {
        if (!(opts.onStatsPage && pushNativeBotWindowPage('about'))) openCardAbout(o.draftId);
      } else if (k === 'duplicate' && o.draftId) {
        if (!(opts.onStatsPage && pushNativeBotWindowPage('dup')))
          openDuplicate({ draftId: o.draftId, title: o.title, wizard: o.wizard });
      }
      else if (k === 'share' && o.draftId)
        // Те же самостоятельные панели, что на странице сценария (Э2.3) — копий нет.
        panel.open({
          title: ts('shareTitle'),
          ...EDITOR_SHEET,
          // Стопкой над окном бота (ТЗ WEB-LOOK Э2.5); в приложении как было.
          ...webOnly({ winKey: BOT_SHEET }),
          body: <ScenarioSharePanel draftId={o.draftId} title={o.title} />,
        });
      else if (k === 'runs' && o.workflowId) {
        if (bcNative) {
          const key = bcastRunsKey(o.workflowId);
          setBcStack((st) => [...st.filter((k2) => k2 !== key), key]);
        } else openRuns(o.workflowId);
      }
      else if (k === 'run' && o.draftId) setCheckRun(o.draftId);
    };
    /** Пункты РОДНОГО меню: «Включить/Выключить» первым (владелец 2026-08-09, IMG_1539),
     *  дальше полный набор пунктов страницы сценария из общего источника (Э2). */
    const nativeItems = [
      {
        key: 'toggle',
        label: toggleLabel,
        // П18: у рассылки — самолётик / стоп в круге, у бота — питание.
        icon: menuToggleSf(o.bcAction),
        section: 'main',
        ...(o.busy ? { disabled: true } : {}),
      },
      ...scenarioMenuNativeItems(state),
    ];
    return { toggleLabel, state, pickItem, nativeItems };
  };

  /** Меню «⋯» РОДНОЙ карточки: то же родное меню у точки, что в вебе, только рама
   *  кнопки-якоря приходит от натива, а не из разметки. */
  const openCardMenuNative = (
    o: CardMenuOpts,
    rect: NativeBotsRect,
    opts: { onStatsPage?: boolean } = {},
  ) => {
    const { pickItem, nativeItems } = cardMenuOf(o, opts);
    void nativeBridge()
      ?.pointMenu?.({
        items: nativeItems,
        x: rect.x,
        y: rect.y,
        w: rect.w,
        h: rect.h,
        theme: themeAttr,
      })
      .then((res) => {
        if (res?.key) pickItem(res.key);
      })
      .catch(() => {});
  };

  // ── РОДНОЙ СПИСОК «БОТОВ» В ПРИЛОЖЕНИИ (docs/NATIVE-TABS-PLAN.md) ──────────────
  // Карточки рисует натив (BotsScreen.swift), веб остаётся мозгом: те же ряды, те же
  // слова, те же руки тапов. Ряды собираем ЗДЕСЬ, до ранних выходов: хук обязан
  // зваться безусловно. В «Рассылках» родного экрана нет — там витрина прежняя.
  // ФОТО АККАУНТОВ ДЛЯ РОДНЫХ КАРТОЧЕК — ТЕМ ЖЕ КОНВЕЙЕРОМ, ЧТО АВАТАР «АККАУНТА»
  // (base64, уменьшенные): клиент ничего не качает сам, картинку готовит веб. Пустая
  // строка — фото не взялось: карточка рисует плитку канала, а ожидание не виснет.
  // Конвейер ОДИН на все родные экраны (native-photos.ts): готовое лежит на общем складе,
  // и «Аналитика» с «Рассылками» берут те же лица без второго круга ожидания (Н17).
  const photoUrls = bcast
    ? []
    : ordered
        .map(({ scn, draft }) => (draft ?? (scn ? draftByWorkflow.get(scn.id) : null))?.accountAvatar)
        .filter((u): u is string => !!u);
  // Рука сама сторожит список по адресам (не по ссылке на массив) — свежий массив
  // каждый рендер ей не мешает.
  const { photos } = useNativePhotos(photoUrls);
  // ЖДЁМ ФОТО, НО НЕ ВЕЧНО. Первый кадр обязан быть полным (правило волны), однако
  // «Боты» — ГЛАВНАЯ вкладка: веб-список под родным экраном не рисуется, и зависший
  // снимок оставил бы человека с пустой страницей. Поэтому ожидание ограничено: не
  // собрались за это время — экран встаёт с плитками каналов, а фото доедут патчем.
  const [photoWait, setPhotoWait] = useState(true);
  useEffect(() => {
    const id = window.setTimeout(() => setPhotoWait(false), 1500);
    return () => window.clearTimeout(id);
  }, []);

  const cards = new Map<
    string,
    {
      href: string;
      wizId: string | null;
      draftId: string | null;
      menu: CardMenuOpts;
      /** Ключ строки для страницы бота, если карточка ведёт в цифры; иначе null. */
      statsRow: string | null;
      /** Куда ведёт «открыть бота» из окна «Не включился» (тот же адрес, что в вебе). */
      failHref: string | null;
      workflowId: string | null;
      /** Живые адреса мини-аппов — их открывают значки-ссылки у имени карточки. */
      formUrl: string | null;
      chatUrl: string | null;
      /** Невстреченная ссылка в Telegram (UT 14.10 п.10) — её открывает значок у имени и (п.26) тап по карточке. */
      tgWarn?: TgLinkWarnView | null;
    }
  >();
  const nativeRows: NativeBotsRow[] = bcast
    ? []
    : ordered.map(({ scn, draft }) => {
        // Ряд готового бота несёт СВОЙ черновик (его кладёт `withDraft` выше) — ровно как
        // веб-карточка: без него у включённого бота не было бы ни категории с логином,
        // ни метки мастера, ни двух цифр, ни фото аккаунта.
        const c = cardCore(scn ?? null, draft ?? null);
        const bad = fail[c.id];
        cards.set(c.id, {
          href: c.href,
          wizId: c.href === c.edit && draft?.wizard ? draft.id : null,
          draftId: draft?.id ?? null,
          menu: c.menu,
          statsRow: c.href === c.stats ? (draft?.id ?? c.id) : null,
          failHref: draft ? scnHref(draft.id, !!draft.wizard) : null,
          workflowId: scn ? scn.id : (draft?.workflowId ?? null),
          formUrl: draft?.formUrl ?? null,
          chatUrl: draft?.chatUrl ?? null,
          tgWarn: draft?.tgLinkWarn ?? null,
        });
        const trigger = draft?.triggerType ?? scn?.triggerType ?? null;
        const rowBusy = busy === c.id || checking === c.id;
        const live = c.on && !bad;
        // Сеть карточки — по аккаунту-отправителю (у цепочки — её кред), иначе по узлу запуска.
        const chan = draft?.accountChannel ?? chanOf(trigger);
        const withLogin = CHAN_WITH_LOGIN.has(chan);
        const avatar = draft?.accountAvatar ?? null;
        return {
          id: c.id,
          title: c.title,
          // Описание — ТЕМ ЖЕ выражением, что у веб-карточки (ScnCardBody desc): у бота
          // список описания не показывает вовсе, оно есть только у рассылки, а её
          // карточек в родном списке не бывает. Своих слов натив не добавляет.
          //
          // СТРОКИ ВХОДОВ В ПОДЗАГОЛОВКЕ БОЛЬШЕ НЕТ (К17.2 п.3, претензия владельца П8
          // 06.09.2026: «я тебя не просил её делать, убери»): подстрока родной карточки
          // повторяет веб-карточку, а на ней чипов входов тоже больше нет.
          // ОПИСАНИЕ БОТА — на карточке (владелец 10.09.2026, п.16в): то, что записано
          // в «О боте», правится там же. У рассылки — «вид начала» словами.
          desc: c.bc?.desc ?? draft?.description ?? '',
          // ЛИНИЯ ОТКРЫТИЙ ПО ДНЯМ (п.16б) — те же точки, что у веб-карточки.
          ...(() => {
            const prefs = cardPrefsFor(draft?.cardPrefs, draft?.chainActive != null);
            const line = lineData(draft?.goalDaily, draft?.enteredDaily, draft?.cardLine ?? null);
            if (!line || prefs.lines.length === 0) return {};
            // ДВЕ ЛИНИИ И ЛЕГЕНДА (П77): контакты (`line`) и конверсия (`line2`, новый
            // необязательный ключ), под каждой — своя строка легенды.
            return {
              // Карточка (П113): контакты цветом сети и «запустили бота» серым — обе в людях,
              // одна ось; легенда — контакты и конверсия цифрами.
              // Цифр шкалы нет (владелец 14.09.2026): hi/lo пустые — натив подписи не рисует.
              // ЛИНИИ — ТЕ, ЧТО ВЫБРАЛ ЧЕЛОВЕК (П119): первая — контакты, если показываются,
              // иначе первая из выбранных; вторая — запуски, когда показаны обе; третья —
              // конверсия своей осью 0…100 (новые необязательные ключи моста).
              line: {
                points: prefs.lines.includes('contacts')
                  ? line.contacts
                  : prefs.lines.includes('runs')
                    ? line.entered
                    : line.conv,
                hi: '',
                lo: '',
                // ЦВЕТ ЛИНИИ И ЕЁ ПОДПИСИ — ОДИН (владелец 14.09.2026: «график одного цвета,
                // а конверсия точка другого — у тебя там разлад»). Осталась одна конверсия —
                // линия бирюзовая своей осью 0…100, как её кружок; иначе ключей нет и натив
                // рисует цветом сети по своим точкам, как было.
                // П10 (20.10.2026): роли серий как на телефоне — конверсия `--accent-stroke`, запуски `--blue`,
                // контакты `--green` (цвет сети серию больше не красит).
                ...(prefs.lines.length === 1 && prefs.lines[0] === 'conv'
                  ? { ink: convInk(), max: 100 }
                  : !prefs.lines.includes('contacts') && prefs.lines.includes('runs')
                    ? { ink: runsInk() }
                    : { ink: contactsInk() }),
              },
              // Запуски — `--blue` (П10), и второй линией тоже.
              ...(prefs.lines.includes('contacts') && prefs.lines.includes('runs') && line.entered.length > 0
                ? { line2: { points: line.entered, hi: '', lo: '', ink: runsInk() } }
                : {}),
              ...(prefs.lines.includes('conv') && prefs.lines.length > 1
                ? { line3: { points: line.conv, max: 100, ink: convInk() } }
                : {}),
              legend: lineLegend(line, trigger, prefs, chan),
              // Срок графика одной цифрой (П119) — рисуется в конце строки подписей.
              lineDays: prefs.step === 'week' ? t('cardDaysWeeks', { n: prefs.days }) : t('cardDays', { n: prefs.days }),
            };
          })(),
          // ПАПКА БОТА — НАТИВУ ВСЕГДА (П209, 09.10.2026). Страницы папок приложения отбирают ряды
          // ПО ЭТОМУ ПОЛЮ (`BotsScreen.swift` rows(for:) — `category == folderId`). 14.09 исполнитель
          // обнулил его (68920c916), чтобы родная карточка не рисовала имя папки (претензия
          // владельца была про надпись на карточке, не про папки) — и этим сломал отбор по папкам:
          // все папки в приложении стали пустыми. Данные — обязаны ехать; не рисовать имя папки
          // на карточке — дело родной карточки (следующая сборка, П209). Цвет не шлём.
          category: draft?.category ?? '',
          // П11: все папки бота (первая = `category`).
          categories: draft ? draftFolders(draft) : [],
          categoryColor: '',
          wand: !!draft?.wizard,
          wandLabel: t('scnWizMark'),
          login: '',
          loginIcon: '',
          // ЗНАЧОК СЕТИ У НАЗВАНИЯ (П78) — после значков-ссылок, 14 цветом сети. Ключи
          // НОВЫЕ и необязательные (запрет №8): старая сборка их не знает и рисует имя
          // как раньше. Рисуем только у настоящих сетей — у формы и чата сети нет.
          ...(withLogin
            ? { titleIcon: nativeChannelTile(chan).icon, titleIconColor: channelHex(chan) }
            : {}),
          // Значки-ссылки у имени — те же два, что в вебе (.scn-name-link): открыть
          // форму и открыть чат. SF-символы подбирает ВЕБ (натив ничего не решает).
          links: [
            // ССЫЛКУ В TELEGRAM НИКТО НЕ ВСТРЕЧАЕТ (UT 14.10.2026 п.10): значок-предупреждение
            // первым в ряду у названия; тап приходит ключом 'tglink:<id>'. Сборка 1.1 рисует
            // любой SF-символ ряда — правка iOS не нужна.
            ...(draft?.tgLinkWarn
              ? [{ key: 'tglink', icon: 'exclamationmark.triangle', label: t('tgLinkWarnText') }]
              : []),
            ...(draft?.formUrl ? [{ key: 'form', icon: 'list.bullet.rectangle', label: t('openForm') }] : []),
            ...(draft?.chatUrl ? [{ key: 'chat', icon: 'ellipsis.bubble', label: t('openChat') }] : []),
          ],
          // ЛИЦО АККАУНТА главнее плитки канала (владелец 2026-08-01, ScnCardAvatar):
          // фото готовит веб (base64), не взялось — встаёт плитка канала.
          photo: avatar ? (photos[avatar] ?? '') : '',
          ...nativeChannelTile(chan),
          // Состояние СЛОВАМИ — тот же расчёт, что у чипа веб-карточки (ScnCardBody).
          // СЛОВА «ВКЛЮЧЕНО» НА КАРТОЧКЕ НЕТ (П119, владелец 14.09.2026): у включённого бота
          // в подвале не стоит ничего, выключенный помечен значком у названия (`off`), а
          // словом остаются только сбой и переключение — это не обычная жизнь бота.
          // Цепочка, у которой все входы — удалённые боты (П203): слово состояния
          // «Не запускается: бот удалён» сигнальным тоном, как сбой; тумблер она не трогает.
          state: rowBusy
            ? t(live ? 'chipBusyOff' : 'chipBusyOn')
            : bad
              ? t('stFailed')
              : live
                ? chainGoneLabel(draft)
                : '',
          off: !live && !bad && !rowBusy,
          stateTone: rowBusy ? 'wait' : bad || (live && chainGoneLabel(draft) !== '') ? 'err' : live ? 'run' : 'off',
          // Переключатель подвала карточки (П114): включён ли бот сейчас.
          stateOn: live && !bad,
          // СОСТОЯНИЕ НА КАРТОЧКЕ НЕ НАЖИМАЕТСЯ (владелец 14.09.2026): включение и выключение —
          // только в меню (у приложения — долгое нажатие карточки, у сайта — «⋯»).
          stateTap: false,
          stateBusy: rowBusy,
          // Две цифры карточки показываются ВСЕГДА (владелец 2026-08-18): ноль — честный
          // ответ «пока никого», оценок «мало/много» рядом нет.
          // Цифры карточки теперь в легендах линий (П77): «Контакты · N», «Конверсия · K %».
          nums: [],
          // Сбои клиенту не показываем (владелец 19.09.2026, запрет №7): поле пустое,
          // родной ряд без строки сбоев; техошибки — владельцу в TG.
          errors: '',
          errorsTap: false,
        };
      });
  // Снимок карточек для тумблера окна бота (П158 п.2) — отдельной копией: сам `cards`
  // дозаполняется ниже, а эффекту трогать его нельзя.
  const cardsNow = new Map(cards);
  // Шапка окна бота в браузере перечитывает «⋯», когда карточки доехали (окно по адресу).
  useEffect(() => {
    bumpBotWindowWebMenu();
  }, [cards]);
  useEffect(() => {
    cardsRef.current = cardsNow;
  });
  // Руки окна бота в браузере (см. эффект у `statsId`): всегда свежие.
  webHooksRef.current = { goScn, reload, reloadPulse, fresh: setFreshId };
  // Карточка открытого окна: адрес окна несёт id черновика ИЛИ ключ ряда статистики (у
  // цепочки) — ищем по обоим, одним поиском для тумблера и меню (тумблер искал только по
  // черновику и у цепочки молчал — поймано экранным тестом 01.10.2026).
  const webCard = () =>
    statsId ? [...cardsNow.values()].find((x) => x.menu.draftId === statsId || x.statsRow === statsId) : undefined;
  webToggleRef.current = () => {
    // Карточки в текущем отборе может не быть (окно по адресу, другая папка) — тогда та же
    // рука из данных окна (О7/О8), как у меню «⋯».
    const c = webCard() ?? webDraftCard();
    return c && !c.menu.busy ? c.menu.onToggle() : undefined;
  };
  // РАССЫЛКА, ОТКРЫТАЯ ИЗ СПИСКА «РАССЫЛКИ» (круг 3, Н6 п.9; кадр iOS R07-broadcast-analytics):
  // адрес её окна — общий `/app/bots/<id>/stats`, и окно поднимает витрина БОТОВ, в карточках
  // которой рассылок нет (своя половина разбиения, keepKind). Меню приходило пустым — и под
  // «Остановить» пропадало «Редактировать» (его берёт BcastRootBody пунктом `edit` меню).
  // Карточку такой рассылки собираем тем же cardCore по её черновику из общей выгрузки.
  // МЕНЮ ОКНА — ИЗ ДАННЫХ ОКНА, НЕ ИЗ КАРТОЧКИ СПИСКА (ТЗ WEB-CAUSES О7, TODO 363/505): ключ
  // окна — id черновика, а у ряда без черновика — id сценария; ищем по обоим (и по воркфлоу
  // черновика), во всей выгрузке, без отбора папки/поиска/половины «боты ↔ рассылки».
  const webDraftCard = (): { menu: CardMenuOpts } | undefined => {
    if (!statsId) return undefined;
    const drafts = data?.drafts ?? [];
    const scns = data?.scenarios ?? [];
    const draft = drafts.find((d) => d.id === statsId) ?? drafts.find((d) => d.workflowId === statsId) ?? null;
    const scn = draft?.workflowId
      ? scns.find((x) => x.id === draft.workflowId)
      : draft
        ? undefined
        : scns.find((x) => x.id === statsId);
    if (!draft && !scn) return undefined;
    return { menu: cardCore(scn ?? null, draft).menu };
  };
  webMenuRef.current = () => {
    // Первым — сборка из данных окна (одна на все входы: адрес, «Рассылки», поиск); карточка
    // списка — запаской, если выгрузка ещё не пришла, а карточка уже есть (кэш списка).
    const c = webDraftCard() ?? webCard();
    if (!c) return null;
    const { toggleLabel, state, pickItem } = cardMenuOf(c.menu, { onStatsPage: true });
    return {
      label: c.menu.isBc ? ts('cardMenuBcastLabel') : t('cardMenuLabel'),
      sections: scenarioMenuSections(
        { key: 'toggle', label: toggleLabel, disabled: c.menu.busy, icon: menuToggleIcon(c.menu.bcAction) },
        scenarioMenuItems(state),
      ),
      onPick: pickItem,
    };
  };
  useNativeBots({
    enabled: !bcast,
    // Ни одного бота (не отбор и не поиск) — строки поиска в приложении нет, полоса
    // открывается на ровном месте (владелец 16.09.2026).
    searchHidden: !!data && !bcast && !filtered && ordered.length === 0,
    // Первый кадр ждёт полные данные: список без карточек с наездом карточек через
    // сотни миллисекунд и есть тот «скачок контента», которого не бывает. Фото
    // аккаунтов ждём так же: иначе аватарки «наезжают» на уже нарисованные карточки.
    ready: !!data && (!photoWait || photoUrls.every((u) => photos[u] !== undefined)),
    rows: nativeRows,
    // Пересечение триггеров: под родным экраном веб-полосы не видно — её рисует натив
    // теми же словами и гаснет она тем же таймером веба.
    toast: warnToast ?? '',
    // ПОЛОСУ ПАПОК ЧЕЛОВЕК МОГ УБРАТЬ (владелец 13.09.2026, П85: «Настройки → Папки →
    // Боты» → «Скрыть папки»). Флаг едет вместе со списком ботов — второго запроса ради
    // булева нет.
    foldersHidden: data?.foldersHidden === true,
    dict: {
      search: t('searchPh'),
      query: q,
      // Пустой итог обязан сказать СЛОВАМИ, чем он пустой (инвариант списка ниже).
      // В ПРИЛОЖЕНИИ пустой список зовёт к пасхалке (владелец 16.09.2026: «потяните
      // вниз, чтобы узнать, как всё устроено»); в браузере полосы нет — там слово прежнее.
      // «…ИЛИ НАЖМИТЕ ⚡» — только сборке с молнией у пустого списка (новая, признак — метод authSession; UT 14.10
      // п.20). Сборка 1.1 рисует у пустого списка стрелку без молнии — ей прежнее «Потяните страницу вниз».
      empty: t(botsEmptyWordKey({ filtered, keyBad: data?.apiKeyValid === false, newBuild: isNewAppBuild() })),
      emptyFolder: t('emptyFolder'),
    },
    // Капсула категорий (П81): текущая — из адреса страницы (`/app/bots/category/<slug>`),
    // иначе «Все». Тап папки меняет адрес, как выбор в колонке категорий.
    ...(botFolders
      ? { folders: { items: botFolders, current: catName ?? 'all', settingsLabel: t('foldersSettings') } }
      : {}),
    theme: themeAttr,
    onTap: (key, rect) => {
      if (key === 'folder:settings') {
        // КЛЮЧ СТРАНИЦЫ — С ПРИСТАВКОЙ `page:` (владелец 14.09.2026: «шестерёнка нажатие
        // не пашет»). Без неё натив страницу в каталоге не находит и молча ничего не
        // делает — берём ключ у самой страницы папок, а не пишем строкой.
        openNativeSettingsPage(['settings', `${BT_FOLDERS_PAGE}list`]);
        return;
      }
      if (key.startsWith('folder:')) {
        const id = key.slice('folder:'.length);
        router.push(id === 'all' ? '/app/bots' : scriptsPathFor(`cat:${slugify(id)}`));
        return;
      }
      // Строка поиска родного экрана — вход в ТОТ ЖЕ родной поиск, что открывала
      // веб-строка под ним (openSearch): набранное приезжает событием searchQuery и
      // становится `q` этого раздела. Второго поиска в приложении нет.
      if (key === 'search') {
        void nativeBridge()?.openSearch?.({ placeholder: t('searchPh') });
        return;
      }
      const id = key.slice(key.indexOf(':') + 1);
      const c = cards.get(id);
      if (!c) return;
      // Окно бота — то, что открывает тап по карточке (и «Перейти к аналитике» окна предупреждения, п.26).
      const openBotWindow = () => {
        // Включённый бот ведёт в свои цифры — в приложении это РОДНОЕ окно (волна 3),
        // а не веб-страница слоем. Выключенный открывается прежним переходом.
        if (
          c.statsRow &&
          openWindow(
            { kind: 'stats', rowKey: c.statsRow },
            {
              menu: (r) => openCardMenuNative(c.menu, r, { onStatsPage: true }),
              toggle: windowToggle(id, c.draftId),
              // APP-CLAIMS-1020 ред. 6, П20: «Редактировать» отчёта рассылки — та же рука, что пункт меню.
              ...(c.menu.isBc && c.menu.editHref ? { edit: () => cardMenuOf(c.menu, { onStatsPage: true }).pickItem('edit') } : {}),
            },
          )
        )
          return;
        // Греем то же, что греет палец на веб-карточке (данные бота и его маршрут):
        // слой ждёт отрисовки страницы, а она не начнётся без них.
        warmDraft(c.draftId, true);
        warmRoute(c.href);
        goScn(c.href, c.wizId);
      };
      if (key.startsWith('card:')) {
        // Предупреждение «ссылку никто не встречает» есть — тап по всей карточке открывает его окно (п.26).
        if (c.tgWarn) {
          openTgLinkWarn(c.draftId, c.tgWarn, openBotWindow);
          return;
        }
        openBotWindow();
      } else if (key.startsWith('menu:')) openCardMenuNative(c.menu, rect);
      // ЧИП СОСТОЯНИЯ — та же рука, что у веб-кнопки чипа (onSt в ScnCardBody):
      // «Не включился» открывает причину, остальное переключает бота.
      else if (key.startsWith('st:')) {
        if (c.menu.busy) return;
        const bad = fail[id];
        if (bad) openFail(bad, c.failHref);
        else c.menu.onToggle();
      }
      // ЧИП СБОЕВ — тот же журнал на упавших прогонах, что открывает веб-кнопка.
      else if (key.startsWith('err:') && c.workflowId) openRuns(c.workflowId, true);
      // ЗНАЧКИ-ССЫЛКИ У ИМЕНИ — тем же путём, что веб-значок (.scn-name-link → openExt):
      // мини-апп открывается новым окном, второго пути открытия не заводим.
      else if (key.startsWith('form:') && c.formUrl) openLink(c.formUrl);
      else if (key.startsWith('chat:') && c.chatUrl) openLink(c.chatUrl);
      else if (key.startsWith('tglink:') && c.tgWarn) openTgLinkWarn(c.draftId, c.tgWarn, openBotWindow);
    },
  });


  // ── РОДНОЙ РАЗДЕЛ «РАССЫЛКИ» В ПРИЛОЖЕНИИ (docs/NATIVE-TABS-PLAN.md, волна 3) ──
  // Витрину рисует натив (BcastListScreen.swift), веб остаётся мозгом: те же карточки,
  // тот же порядок (ordered выше), те же слова и те же руки тапов, что у веб-разметки.
  // ФОТО АККАУНТОВ готовит веб (клиент ничего не качает сам) — тем же конвейером, что
  // аватар «Аккаунта»; не взялось — карточка рисует плитку канала, как в вебе.
  const bcPhotoUrls = useMemo(
    () =>
      bcast
        ? Array.from(
            new Set(
              (data?.drafts ?? [])
                .map((d) => d.accountAvatar)
                .filter((u): u is string => !!u),
            ),
          )
        : [],
    [bcast, data],
  );
  const { photos: bcPhotos, ready: bcPhotosReady } = useNativePhotos(bcPhotoUrls);
  // Мастер отказался вести рассылку (нет телеграм-аккаунта) — вместо веб-анкеты раздел
  // показывает родную страницу «Подключите телеграм-бота» (реестр волны 3).
  // Подписка ОДНА на кабинет — берёт её только ПОКАЗАННЫЙ раздел (bcNative): скрытая
  // фоновая панель стека перехватила бы её у настоящего хозяина, и плашка «Подключите
  // телеграм-бота» уходила бы в невидимую стопку.
  useEffect(() => {
    if (!bcNative) return;
    subscribeBcastConnect(() =>
      setBcStack((st) => [...st.filter((k) => k !== BCAST_CONNECT_KEY), BCAST_CONNECT_KEY]),
    );
    return () => subscribeBcastConnect(null);
  }, [bcNative]);
  const bcNativeRows: NativeBcastCard[] = bcast
    ? ordered.map(({ scn, draft }) => {
        const c = cardCore(scn ?? null, draft ?? null);
        cards.set(c.id, {
          href: c.href,
          wizId: c.href === c.edit && draft?.wizard ? draft.id : null,
          draftId: draft?.id ?? null,
          menu: c.menu,
          statsRow: c.href === c.stats ? (draft?.id ?? c.id) : null,
          failHref: draft ? scnHref(draft.id, !!draft.wizard) : null,
          workflowId: scn ? scn.id : (draft?.workflowId ?? null),
          formUrl: draft?.formUrl ?? null,
          chatUrl: draft?.chatUrl ?? null,
        });
        const trigger = draft?.triggerType ?? scn?.triggerType ?? null;
        const category = draft?.category ?? '';
        const avatar = draft?.accountAvatar ?? '';
        return {
          id: c.id,
          title: c.title,
          // Описание карточки — описание из «О рассылке», как у бота (APP-CLAIMS-1020 ред. 6, П17).
          desc: c.bc?.desc ?? draft?.description ?? '',
          // Подстрока — ЧАСТЯМИ, в том же порядке, что складывает веб-карточка:
          // категория своим цветом · метка мастера · значок сети и логин аккаунта.
          category,
          categoryColor: category ? catColor(category, cats) : '',
          // «Быстрой настройки» и логина у карточки рассылки нет (APP-CLAIMS-1020 ред. 6, П17/П26.3) — пустые, строка
          // не рисуется; карточка та же, что у бота.
          wand: false,
          wandLabel: '',
          login: '',
          loginIcon: '',
          ...nativeChannelTile(chanOf(trigger)),
          // Свой выбор логотипа (logo) на карточке главнее фото — тогда фото не шлём:
          // в вебе то же правило (AccountPhoto получает src только без logo).
          photo: avatar && !draft?.logo ? (bcPhotos[avatar] ?? '') : '',
          nums: c.bc?.numsText ?? '',
          state: c.bc?.stLabel ?? '',
          stateTone: c.bc?.stTone ?? 'off',
          // Сбои клиенту не показываем (владелец 19.09.2026, запрет №7).
          errors: '',
          // Только что созданная копия — та же вспышка, что у веб-карточки (.scn-fresh).
          ...(freshId && draft?.id === freshId ? { fresh: true } : {}),
        };
      })
    : [];
  // Страницы стопки собирает своя рука (bcast-pages-native): те же данные и те же
  // ручки сервера, что у веб-шторок этих окон.
  const bcHands = useBcastPages({
    active: bcNative,
    stack: bcStack,
    cardOf: (id) => {
      const c = [...cards.values()].find((x) => x.draftId === id);
      return c ? { title: c.menu.title, wizard: c.menu.wizard, draftId: c.draftId } : null;
    },
    onSaved: reload,
    onDuplicated: (id) => {
      setFreshId(id);
      reload();
    },
    onClose: (key) => setBcStack((st) => st.filter((k) => k !== key)),
    // «Подключить» с плашки: раздел уходит целиком (родной экран нельзя оставлять над
    // страницей, куда мы переходим), дальше — прежний путь кабинета в «Подключения».
    onConnect: () => {
      closeNativeBroadcasts();
      router.push('/app/connections');
    },
  });
  // Цифры рассылки, журнал запусков и деталь прогона — своя рука (bcast-stats-native):
  // те же ручки и те же слова, что у веб-страницы цифр.
  const bcStats = useBcastStats({
    active: bcNative,
    stack: bcStack,
    // ТОЛЬКО те карточки, чей тап ведёт в ЦИФРЫ (правило перехода — общее, cardCore):
    // у карточки, ведущей в редактор, страницы цифр в каталоге быть не должно, иначе
    // натив открыл бы её по тапу, а веб в тот же миг увёл бы человека в редактор.
    cards: [...cards.entries()]
      .filter(([, c]) => c.href.endsWith('/stats'))
      .map(([id, c]) => ({ id, title: c.menu.title, draftId: c.draftId })),
    onEdit: (draftId, wizard) => goScn(scnHref(draftId, wizard), wizard ? draftId : null),
  });
  const bcPages: NativeBcastPage[] = [
    ...bcHands.pages,
    ...bcStats.pages,
    ...(bcMsg ? [{ key: 'msg', title: bcMsg.title, blocks: [{ kind: 'hint' as const, text: bcMsg.text }] }] : []),
  ];
  // ГЕРОЙ ПУСТОГО РАЗДЕЛА — по тому же правилу, что у веб-витрины: список пуст, отбора
  // нет и архива нет (иначе пустота говорит словами, а раздел живёт списком).
  const bcHeroOn = bcast && ordered.length === 0 && !filtered && archive.length === 0;
  // Полосы над списком — ТЕ ЖЕ, что рисует веб-витрина, и в том же порядке: беда
  // запуска, состояние сервера (готовится / заморожен) и протухший ключ. В приложении
  // зова оплатить в «замороженной» полосе нет вовсе (CLAUDE.md запрет №7) — берём тот же
  // текст, что берёт веб под приложением.
  const bcBanners = [
    ...(bcActs.err ? [bcActs.err] : []),
    ...(data?.state === 'none' || data?.state === 'provisioning'
      ? [t(data.provisioningStuck ? 'provisioningStuck' : 'instanceProvisioning')]
      : []),
    ...(data?.state === 'frozen' ? [t('frozenBannerApp')] : []),
    ...(data && !data.apiKeyValid ? [t('keyBanner')] : []),
  ];
  useNativeBcastList({
    active: bcNative,
    data: {
      theme: themeAttr,
      title: tc('sections.broadcasts'),
      plus: tb('newTitle'),
      rows: bcNativeRows,
      archive: { label: tb('archTitle'), rows: archive },
      // Герой пустого раздела — тот же, что в вебе (Д5): серый самолётик без плитки,
      // заголовок про СОСТОЯНИЕ и обычная кнопка-капсула без градиента (И10 п.4).
      ...(bcHeroOn
        ? {
            hero: {
              icon: 'paperplane',
              title: tb('emptyHeroTitle'),
              // Без Pro — факт тарифа второй фразой (владелец 12.10.2026).
              meta: proLock
                ? `${tb('emptyHeroMeta')} ${tBill('proAppBcastNote')}`
                : tb('emptyHeroMeta'),
              cta: tb('emptyCta'),
              flat: true,
            },
          }
        : {}),
      // Пустой итог обязан сказать СЛОВАМИ, чем он пустой (инвариант списка ниже).
      // «Сделайте свою первую рассылку» — только когда рассылок НЕ БЫЛО вовсе: прошедшие
      // в архиве — тоже рассылки (владелец 15.09.2026, скрин «Рассылок»).
      emptyLine:
        ordered.length > 0 || bcHeroOn || (!filtered && archive.length > 0)
          ? ''
          : filtered
            ? t('emptyFiltered')
            : tb('emptyLine'),
      banners: bcBanners,
      // Первый кадр ждёт полные данные: список, цифры рассылок и фото аккаунтов.
      ready: !!data && !!bcData && bcPhotosReady,
      skin: {
        bg: readRoleHex('--bg', themeAttr === 'dark' ? '#101014' : '#fbfbfd'),
        spotPink: readRoleHex('--accent-soft', '#ffe7ed'),
        spotGreen: readRoleHex('--green-soft', '#e0f5f7'),
        // Пара градиента кнопки героя (.cta-grad): от акцента до фиолетового.
        ctaFrom: readRoleHex('--accent', '#feb3c8'),
        ctaTo: readRoleHex('--violet', '#2481cc'),
      },
      tones: {
        run: readRoleHex('--green', '#00a884'),
        off: OFF_AMBER,
        wait: readRoleHex('--tg-blue', '#2aabee'),
        err: readRoleHex('--red', '#ff3b30'),
        // Янтарный плашек-предупреждений (.scn-warn веба) — та же роль палитры.
        amber: readRoleHex('--amber', '#e0a020'),
      },
      // Столбики графика — те же роли, которыми покрашен график в вебе (.ov-bars).
      bars: {
        ink: readRoleHex('--ov-bar', '#8a8a8f'),
        zero: readRoleHex('--line2', '#d7dbe1'),
        alpha: readRoleAlpha('--ov-bar', 0.3),
      },
      pages: bcPages,
      stack: bcStack,
    },
    onTap: (key, rect) => {
      // Кнопки страниц стопки разбирают их же руки (сохранить, копия, отправить…).
      if (bcHands.onTap(key) || bcStats.onTap(key)) return;
      // Строка журнала запусков — деталь прогона страницей стопки поверх цифр.
      if (key.startsWith('run:')) {
        setBcStack((st) => [...st.filter((k) => k !== key), key]);
        return;
      }
      // Чип «Сбои: N» карточки — журнал СРАЗУ на упавших прогонах (как в вебе).
      if (key.startsWith('errors:')) {
        const c = cards.get(key.slice('errors:'.length));
        const wf = c?.menu.workflowId;
        if (wf) {
          setBcStack((st) => [...st.filter((k) => k !== bcastRunsKey(wf, true)), bcastRunsKey(wf, true)]);
        }
        return;
      }
      // «+» шапки и кнопка героя — ОДНА рука создания витрины (у веб-раздела тоже одна).
      if (key === 'plus' || key === 'hero') {
        onCreate?.();
        return;
      }
      const c = cards.get(key.slice(key.indexOf(':') + 1));
      if (!c) return;
      if (key.startsWith('card:')) {
        // КУДА ВЕДЁТ КАРТОЧКА, решает то же правило, что в вебе (cardCore): у рассылки
        // «включённость» — это статус запуска, и по нему адрес ведёт либо в цифры, либо
        // в редактор. Цифры теперь родная страница стопки (её натив уже поднял тапом),
        // редактор — прежний переход.
        if (c.href.endsWith('/stats')) {
          setBcStack((st) => [...st.filter((k) => k !== key), key]);
          return;
        }
        // Греем то же, что греет палец на веб-карточке (данные рассылки и её маршрут).
        warmDraft(c.draftId, true);
        warmRoute(c.href);
        goScn(c.href, c.wizId);
      } else if (key.startsWith('menu:')) {
        openCardMenuNative(c.menu, rect);
      }
    },
    onSet: (key, value) => {
      if (bcHands.onSet(key, value)) return;
      bcStats.onSet(key, value);
    },
    onBack: (key) => {
      setBcStack((st) => st.filter((k) => k !== key));
      if (key === 'msg') setBcMsg(null);
    },
    // Раздел закрыли (крестик, свайп от края) — мозг убирает сам себя.
    onGone: () => closeNativeBroadcasts(),
  });

  // Вторая колонка десктопа: выбранный путь СОЗДАНИЯ занимает всё правое поле
  // (владелец 2026-08-01) — список сценариев в это время не рисуется.
  // Заголовок правого поля — подпись выбранного пункта колонки (владелец 2026-08-01):
  // три полосы начинаются с одной линии — бренд · раздел · выбранное.
  // На экране БЕЗ второй колонки (до 1024) со страницы пути создания было не уйти
  // (владелец 11.08.2026: «нажал — и не закрыть, чтобы вернуться к ботам») — слева от
  // заголовка встаёт «✕» к списку; на широком (колонка есть) его прячет CSS.
  const paneX = (
    <Link href="/app/bots" className="dr-close pane-x-narrow" aria-label={t('wizBack')}>
      ✕
    </Link>
  );
  const paneTitle = (label: string) => (
    <div className="split-pane-t pane-t-row">
      {paneX}
      {label}
    </div>
  );
  if (pick === 'chat') {
    return (
      <>
        {paneTitle(t('splitWayAssistant'))}
        {/* key — новый разговор на каждый выбор пункта: прежний черновик уже живёт
            в «Моих скриптах», держать его в правом поле вечно незачем. */}
        <SplitNewChat key={chatNonce} />
      </>
    );
  }
  if (pick === 'tpl' || pick?.startsWith('tpl:')) {
    const cat = scriptsPickCategory(pick);
    return (
      <>
        {paneTitle(cat ?? t('splitWayCatalog'))}
        <SplitTemplates category={cat} />
      </>
    );
  }
  if (pick === 'schema') {
    // «Вручную» (владелец 2026-08-03): плитки запусков + настройка старта шторкой прямо
    // здесь; заголовок «С чего начинается» страница рисует сама (один на десктоп и телефон).
    // «✕» к списку — тем же рядом, что у других путей создания (владелец 11.08.2026).
    return (
      <>
        <div className="pane-t-row pane-t-bare">{paneX}</div>
        <ManualStart />
      </>
    );
  }
  if (pick?.startsWith('stats:') && isApp) {
    // Страница одного бота (ТЗ 3): на широком экране — СПРАВА, в поле контента (правило
    // второй колонки), на телефоне — обычная страница раздела. Шторкой не открывается.
    return <BotStatsPane key={pick.slice(6)} draftId={pick.slice(6)} />;
  }

  if (!data || !listReady) {
    // Обрыв сети на холодном старте (кэша нет) — не вечный скелетон, а повтор (Ф28).
    if (!data && error) return <LoadError retry={reload} />;
    // Экран рисует натив — веб-скелетона под ним не бывает: родной список поднимается
    // сразу с полными данными (ready в водителе), мигать нечему.
    if (nativeDraws) return null;
    // ЗАГЛУШКА ФОРМЫ (закон «экран показывается один раз», владелец 21.10.2026, ред. 9 §2.3; уточняет Н17 02.09 —
    // «поддельных карточек нет»): форма — снятая при прошлом показе ЭТОГО списка (столько карточек тех же высот),
    // а не выдуманные три. Формы ещё нет — пустое место, как прежде.
    return <Shape name={shapeName} groupsClass="ai-vgroups scn-vgroups scn-grid" />;
  }

  // Ряды черновиков: живут в ОБЩЕМ списке с серверными, всегда сверху и без
  // заголовков «Черновики»/«Работают на сервере» (владелец 2026-07-18: высота
  // панели совпадает с «Аккаунтами»/«Таблицами», свайп между ними не дёргается).
  // Черновики — карточки ТОГО ЖЕ вида, что готовые, ВКЛЮЧАЯ пустой график (владелец
  // 2026-07-21: черновик не должен отличаться от свежего готового). Статусов-бейджей НЕТ,
  // только точка статуса у заголовка (черновик всегда «выключен» — чёрная).
  // Описание в карточках не показываем.
  // Мини-ссылка формы/чата у названия — открываем руками (вложенный <a> в Link невалиден).
  const miniLink = (url: string, label: string, icon: ReactNode) => (
    <span className="scn-name-link" role="link" title={label} aria-label={label} onClick={openExt(url)}>
      {icon}
    </span>
  );
  // «⋯» карточки (владелец 2026-08-06): включить/выключить и удалить прямо из списка —
  // «чтобы эти три точечки мы потом смогли убрать с открытой страницы». Меню — общий
  // PopMenu (канон разовых выборов: копий разметки выпадашек не заводим), удаление —
  // ConfirmInline (в приложении родное окно iOS, на сайте окошко по центру).
  // Вся карточка — ссылка, поэтому тап по меню гасим: он не должен проваливаться внутрь.
  const cardMenu = (o: CardMenuOpts) => {
    const { toggleLabel, state, pickItem, nativeItems } = cardMenuOf(o);
    // Точки — общий глиф кабинета (card-tools): свои копии «⋯» разъезжались по размеру.
    const dots = DOTS;
    // Подпись кружка у рассылки — «Действия с рассылкой» (APP-CLAIMS-1020 ред. 6, П26.3, вопрос 85).
    const menuLabel = o.isBc ? ts('cardMenuBcastLabel') : t('cardMenuLabel');
    return (
    <span className="scn-card-menu" onClick={stopCard} role="presentation">
      {pointMenuOk ? (
        // Приложение с pointMenu (iOS 17.4+): родное UIMenu у самой кнопки «⋯» —
        // пункты и подписи те же, что у PopMenu ниже (общий scenarioMenuNativeItems).
        <KBareButton
          className="scn-card-dots"
          aria-label={menuLabel}
          aria-haspopup="menu"
          onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            void nativeBridge()
              ?.pointMenu?.({
                items: nativeItems,
                x: r.left,
                y: r.top,
                w: r.width,
                h: r.height,
                theme: themeAttr,
              })
              .then((res) => {
                if (res?.key) pickItem(res.key);
              })
              .catch(() => {});
          }}
        >
          {dots}
        </KBareButton>
      ) : (
        <PopMenu
          ariaLabel={menuLabel}
          triggerClassName="scn-card-dots"
          trigger={dots}
          // «Включить/Выключить» — первым (владелец 2026-08-09, IMG_1539), дальше ПОЛНЫЙ набор
          // пунктов страницы сценария из общего scenarioMenuItems (Э2); группы с разделителями
          // и красное «Удалить» — как в родном меню iOS (Б27, scenarioMenuSections).
          sections={scenarioMenuSections(
            { key: 'toggle', label: toggleLabel, disabled: o.busy, icon: menuToggleIcon(o.bcAction) },
            scenarioMenuItems(state),
          )}
          onPick={pickItem}
        />
      )}
      {o.draftId ? (
        <ConfirmInline
          label=""
          triggerClassName="scn-card-del-hidden"
          triggerTitle={t('cardDelete')}
          question={deleteAsk(o.draftId).question}
          confirmLabel={deleteAsk(o.draftId).confirmLabel}
          onConfirm={() => void removeDraft(o.draftId!)}
          registerAsk={(fn) => {
            delAskRef.current[o.draftId!] = fn;
          }}
        />
      ) : null}
    </span>
    );
  };
  // Карточка скрипта: аватар · название · описание · строка «категория, значок канала,
  // логин». Ни точки «включён/выключен», ни стрелки «›» справа НЕТ (владелец 2026-08-02:
  // убрать) — вся карточка и так ведёт на страницу скрипта.
  // КАРТОЧКА СКРИПТА (владелец 2026-08-01): название · описание · строка «категория +
  // аккаунт». Большого логотипа канала НЕТ — он был крупнее текста и забирал всё внимание.
  // Даты создания в карточке НЕТ (владелец 2026-08-01, вторая проба: карточка выглядела
  // нагромождённо, дата в ней не работает). Своя разметка, а не ряды списка: у рядов свои
  // колонки, новые строки в них ломали раскладку (проба владельца, IMG_1173).
  // Аватар и тело карточки — общие компоненты ScnCardAvatar/ScnCardBody (выше): ими же
  // рисует свои списки «Аналитика» (владелец 2026-08-03).
  const draftRow = (d: DraftItem) => {
    const bad = fail[d.id];
    // Черновик включённым не бывает (ряды этого списка — без живого сценария на сервере):
    // клик по карточке ведёт в редактирование, как и раньше (ТЗ 4). Адрес перехода и
    // начинка меню — из общей руки карточки (cardCore выше): второй сборки нет.
    const { edit, href, bc, menu: menuOpts } = cardCore(null, d);
    return (
    <Link
      href={href}
      className={`scn-card${bc ? ' scn-card-bc' : ''}${freshId === d.id ? ' scn-fresh' : ''}`}
      key={d.id}
      data-warm-id={d.id}
      onPointerDown={() => {
        warmDraft(d.id, true);
        warmRoute(href);
      }}
      onClick={(e) => {
        // Предупреждение есть — тап по всей карточке открывает его окно (п.26).
        if (d.tgLinkWarn && !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) {
          e.preventDefault();
          openTgLinkWarn(d.id, d.tgLinkWarn, () => goScn(href, href === edit && d.wizard ? d.id : null));
          return;
        }
        openScn(href, href === edit && d.wizard ? d.id : null)(e);
      }}
    >
      <ScnCardAvatar trigger={d.triggerType} services={d.services} logo={d.logo} accountAvatar={d.accountAvatar} channel={d.accountChannel} bcast={!!bc} />
      {cardMenu(menuOpts)}
      <div className="scn-card-col">
        <ScnCardBody
          title={d.title}
          // У БОТА состояние стоит в нижней строке cardWide (ниже колонки), в теле — только у рассылки.
          st={bc && !bc.noState ? (d.chainActive == null ? 'draft' : d.chainActive ? 'run' : 'off') : undefined}
          desc={bc?.desc ?? d.description ?? undefined}
          stLabel={bc?.stLabel}
          stTone={bc?.stTone}
          chart={bc?.chart}
          contacts={bc ? undefined : d.people}
          conv={d.conv}
          igQueue={d.igQueue ?? null}
          trigger={d.triggerType}
          channel={d.accountChannel}
          account={d.account}
          category={d.category}
          // Рассылке — прежняя карточка с логином, боту — П78 (значок сети у названия).
          bcast={!!bc}
          // Выключенный бот помечается значком у названия (П119) — ТЕМ ЖЕ расчётом, что уходит
          // родной карточке приложения (`off` в рядах для BotsScreen): не работает, не сбой и
          // не переключается. Раньше значок получала только цепочка, и на сайте у
          // выключенных ботов его не было (ТЗ WEB-LOOK-CLAIM-1 Р4).
          off={!menuOpts.on && !bad && busy !== d.id && checking !== d.id}
          wizard={!!d.wizard}
          extra={
            <>
              {d.tgLinkWarn ? tgWarnMark(d.id, d.tgLinkWarn, () => goScn(href, href === edit && d.wizard ? d.id : null)) : null}
              {d.formUrl ? miniLink(d.formUrl, t('openForm'), <FormSvg />) : null}
              {d.chatUrl ? miniLink(d.chatUrl, t('openChat'), <ChatSvg />) : null}
            </>
          }
        />
        {/* График и подписи — В КОЛОНКЕ СЛОВ, под названием (владелец 14.09.2026: на всю
            ширину растягивать не надо). */}
        {bc
          ? null
          : cardWide({
              goalDaily: d.goalDaily,
              enteredDaily: d.enteredDaily,
              cardLine: d.cardLine ?? null,
              prefs: cardPrefsFor(d.cardPrefs, d.chainActive != null),
              trigger: d.triggerType,
              // Канал — по аккаунту-отправителю (у цепочки её кред), как у значка сети.
              chan: d.accountChannel ?? null,
              st: d.chainActive == null ? 'draft' : d.chainActive ? 'run' : 'off',
              stFail: !!bad,
              stBusy: busy === d.id || checking === d.id,
              stNoEntry: chainGoneLabel(d),
            })}
      </div>
    </Link>
    );
  };
  const sceneRow = (s: Scenario) => {
    const draft = draftByWorkflow.get(s.id) ?? null;
    // Адрес перехода (включённый бот — цифры, выключенный — редактор), пункты меню и
    // начинка рассылки приходят из общей руки карточки (cardCore выше).
    const { edit, href: cardHref, bc, menu: menuOpts } = cardCore(s, draft);
    // СБОИ КЛИЕНТУ НЕ ПОКАЗЫВАЕМ (владелец 19.09.2026, запрет №7): чип «Сбои: N за сутки»
    // (2026-08-20) снят вместе с прежними рамкой и точкой — упавший прогон уходит владельцу
    // в TG через центр ошибок; журнал «Запуски» у бота остаётся.
    const bad = fail[s.id];
    const body = (
      <ScnCardBody
        // Название человека, а не имя схемы (владелец 2026-07-31).
        title={draft?.title ?? s.name}
        // У БОТА состояние и сбои — в нижней строке cardWide (ниже колонки), в теле — у рассылки.
        st={bc && !bc.noState ? (s.active ? 'run' : 'off') : undefined}
        desc={bc?.desc ?? draft?.description ?? undefined}
        stLabel={bc?.stLabel}
        stTone={bc?.stTone}
        chart={bc?.chart}
        contacts={bc ? undefined : draft?.people}
        conv={draft?.conv}
        igQueue={draft?.igQueue ?? null}
        trigger={draft?.triggerType ?? s.triggerType}
        channel={draft?.accountChannel}
        account={draft?.account}
        category={draft?.category}
        // Рассылке — прежняя карточка с логином, боту — П78 (значок сети у названия).
        bcast={!!bc}
        off={!s.active}
        wizard={!!draft?.wizard}
        extra={
          <>
            {draft?.tgLinkWarn
              ? tgWarnMark(draft.id, draft.tgLinkWarn, () =>
                  cardHref ? goScn(cardHref, cardHref === edit && draft.wizard ? draft.id : null) : undefined,
                )
              : null}
            {draft?.formUrl ? miniLink(draft.formUrl, t('openForm'), <FormSvg />) : null}
            {draft?.chatUrl ? miniLink(draft.chatUrl, t('openChat'), <ChatSvg />) : null}
          </>
        }
      />
    );
    const menu = cardMenu(menuOpts);
    const wide = bc
      ? null
      : cardWide({
          goalDaily: draft?.goalDaily,
          enteredDaily: draft?.enteredDaily,
          cardLine: draft?.cardLine ?? null,
          prefs: cardPrefsFor(draft?.cardPrefs, draft?.chainActive != null),
          trigger: draft?.triggerType ?? s.triggerType,
          chan: draft?.accountChannel ?? null,
          st: s.active ? 'run' : 'off',
          stFail: !!bad,
          stBusy: busy === s.id || checking === s.id,
        });
    return cardHref ? (
      <Link
        href={cardHref}
        className={`scn-card${bc ? ' scn-card-bc' : ''}${freshId === draft?.id ? ' scn-fresh' : ''}`}
        key={s.id}
        data-warm-id={draft?.id}
        onPointerDown={() => {
          warmDraft(draft?.id);
          warmRoute(cardHref);
        }}
        onClick={(e) => {
          // Предупреждение есть — тап по всей карточке открывает его окно (п.26).
          if (draft?.tgLinkWarn && !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) {
            e.preventDefault();
            openTgLinkWarn(draft.id, draft.tgLinkWarn, () =>
              goScn(cardHref, cardHref === edit && draft.wizard ? draft.id : null),
            );
            return;
          }
          openScn(cardHref, cardHref === edit && draft?.wizard ? draft.id : null)(e);
        }}
      >
        <ScnCardAvatar
          trigger={draft?.triggerType ?? s.triggerType}
          services={draft?.services ?? s.services}
          logo={draft?.logo}
          accountAvatar={draft?.accountAvatar}
          channel={draft?.accountChannel}
          bcast={!!bc}
        />
        {menu}
        <div className="scn-card-col">
          {body}
          {wide}
        </div>
        </Link>
    ) : (
      // Выключенный бот без черновика: редактировать нечего, цифры — только пунктом меню
      // (карточка не ведёт никуда, как и раньше).
      <div className="scn-card" key={s.id}>
        <ScnCardAvatar trigger={s.triggerType} services={s.services} logo={null} />
        {menu}
        <div className="scn-card-col">
          {body}
          {wide}
        </div>
      </div>
    );
  };

  // ОТДЕЛЬНЫЙ ПУЗЫРЬ НА КАЖДЫЙ СКРИПТ (владелец 2026-08-01: «группировку по каналу убрать»).
  // Карточка полегче общей: тонкая кромка и мягкая тень вместо плотной.
  // Быстрая ссылка на шаблон — ПЕРВАЯ ПЛИТКА этой же сетки (владелец 2026-08-06, Ч6а:
  // «она у тебя во всю строчку идёт — она просто первая плитка в списке»): на телефоне
  // колонка одна и вид прежний, на широком экране плитка занимает одну ячейку из ряда.
  // Ищут — плитки-предложения уезжают, список поднимается на их место (владелец
  // 2026-08-07). Признак поиска: набранное слово тут, открытый РОДНОЙ поиск приложения —
  // классом на <html> от натива (оба условия ловит одно правило в cabinet.css).
  // ДВУХ БЫСТРЫХ ПЛИТОК ШАБЛОНОВ В СПИСКЕ БОЛЬШЕ НЕТ (владелец 02.09.2026, Н6
  // docs/NATIVE-DESIGN-FIXLIST.md: «убрать совсем: те же шаблоны есть в выборе по „+“»).
  // Родной полосы-пасхалки ПРИЛОЖЕНИЯ (натив, configureQuickTiles) это не касается —
  // она живёт своим железным каноном (UI-FIXES-JOURNAL 07–08.08).
  const triggerGroups = (rows: ReactNode[]) => (
    <div className={`ai-vgroups scn-vgroups scn-grid${needle ? ' scn-searching' : ''}`} data-shape={shapeName}>
      {rows}
    </div>
  );
  const draftGroupRows = drafts.map(draftRow);

  // Экран рисует натив — веб-версии этих состояний под ним не бывает (полосы уехали
  // в родной экран строками, см. bcBanners выше).
  if (!nativeDraws && (data.state === 'none' || data.state === 'provisioning')) {
    return (
      <>
        {drafts.length > 0 && triggerGroups(draftGroupRows)}
        <div className="rows scn-cards">
          {/* Подготовка застряла (потолок в /api/cabinet/scenarios) — не врём «несколько минут». */}
          <div className="empty">{t(data.provisioningStuck ? 'provisioningStuck' : 'instanceProvisioning')}</div>
        </div>
        {botExists.sheet}
      </>
    );
  }

  // Сервер заморожен (подписка кончилась): ключ тут ни при чём — честно зовём продлить (Ф28).
  // В мобильном приложении зов оплатить запрещён (CLAUDE.md п.7) — только нейтральный текст.
  if (!nativeDraws && data.state === 'frozen') {
    return (
      <>
        {drafts.length > 0 && triggerGroups(draftGroupRows)}
        <div className="keybanner">
          {native ? (
            t('frozenBannerApp')
          ) : (
            <>
              {t('frozenBanner')}{' '}
              <Link href="/app/account" className="lnk">
                {t('frozenCta')}
              </Link>
            </>
          )}
        </div>
        {botExists.sheet}
      </>
    );
  }

  const sumTotal = shownScns.length + drafts.length;
  const sumRun = shownScns.filter((s) => s.active).length;
  // «ЗА НЕДЕЛЮ N ЗАПУСКОВ» — ТА ЖЕ ЦИФРА И ТО ЖЕ СЛОВО, ЧТО НА КАРТОЧКАХ (владелец 14.09.2026:
  // «прохождения» рядом с «запусками» путали): люди на первом шаге за 7 дней, не прогоны движка.
  const sumWeek = shownScns.reduce(
    (acc, s) => acc + (draftByWorkflow.get(s.id)?.enteredDaily ?? []).reduce((a, v) => a + v, 0),
    0,
  );
  // Разметка списка — по ОБЩЕМУ порядку (ordered выше): им же кормится родной список
  // приложения, второго порядка карточек в разделе нет.
  const listRows = ordered.map((r) => (r.scn ? sceneRow(r.scn) : draftRow(r.draft!)));

  // ПУСТОЙ РАЗДЕЛ «РАССЫЛКИ» — ГЕРОЙ ПО КАНОНУ ПУСТОГО СОСТОЯНИЯ APPLE (владелец
  // 04.09.2026, И10 п.4 docs/MESSAGE-COMPOSER-PLAN.md; отменяет вид от 20.08):
  // системный значок СЕРЫМ и без плитки, заголовок ПРО СОСТОЯНИЕ («Рассылок пока нет», а
  // не дубль шапки раздела: шапка говорит, ГДЕ ты, герой — ЧТО здесь), короткое
  // пояснение и ОБЫЧНАЯ кнопка-капсула ролью --accent — градиента розовый→синий больше
  // нет. Разметка — общая (SettingsHero + KButton набора), числа этого героя живут
  // ОДНИМ местом в cabinet.css (скоуп .bcast-empty): остальные герои кабинета правило
  // не трогает — их вид владелец принимает отдельно.
  // Плавающего «+» при этом НЕТ (владелец 21.08, претензия 4: пусто — только герой).
  // Отбор ни при чём: под фильтром пустота говорит словами, как у ботов. Событие тапа
  // отдаём наверх целиком: пути создания выпадашка витрины показывает У ЭТОЙ кнопки (Р1).
  // Экран рисует натив (гейт nativeDraws ниже) — веб-героя под ним не рисуем вовсе:
  // герой пустого раздела уехал в родной экран строкой протокола.
  if (bcast && !nativeDraws && listRows.length === 0 && !filtered && !tail) {
    return (
      <div className="bcast-empty">
        {/* Без Pro — факт тарифа второй фразой, как у героя в приложении (владелец 12.10.2026). */}
        <SettingsHero
          icon={<IconPlane />}
          title={tb('emptyHeroTitle')}
          meta={proLock ? `${tb('emptyHeroMeta')} ${tBill('proAppBcastNote')}` : tb('emptyHeroMeta')}
        />
        {/* Значок «Pro» на углу кнопки героя (TARIFF-PRO-TZ §5) — своей кнопкой рядом, не внутри. */}
        <span className="pro-badge-host">
          <KButton tone="primary" className="bc-hero-cta" onClick={onCreate}>
            {tb('emptyCta')}
          </KButton>
          <ProBadge className="pro-badge--corner" />
        </span>
      </div>
    );
  }

  // ЭКРАН РИСУЕТ НАТИВ — вебу тут рисовать нечего (гейт nativeDraws выше). Остаётся
  // только НЕВИДИМЫЙ хозяин пробного прогона, которого зовёт родное меню карточки.
  // ПОЛОСЫ ПЕРЕСЕЧЕНИЯ ТРИГГЕРОВ ЗДЕСЬ БОЛЬШЕ НЕТ (волна 3): веб-разметка стоит ПОД
  // родным экраном и не видна вовсе — полосу теми же словами рисует натив (её текст
  // уезжает водителю полем `toast`, гасит её прежний таймер веба).
  if (nativeDraws) {
    return checkRun ? (
      <CardCheckHost key={checkRun} draftId={checkRun} onClose={() => setCheckRun(null)} />
    ) : null;
  }

  // Секции отбора «Состояние»: у бота два значения, у рассылки — пять её статусов.
  const stateOpts = bcast
    ? [
        { key: '', label: tb('fltStateAll'), on: !state },
        ...BCAST_STATES.map((s) => ({ key: s, label: statusChip(s).label, on: state === s })),
      ]
    : [
        { key: '', label: t('fltStateAll'), on: !state },
        { key: 'on', label: t('stRun'), on: state === 'on' },
        { key: 'off', label: t('stOff'), on: state === 'off' },
      ];
  const stateLabel =
    stateOpts.find((o) => o.on)?.label ?? (bcast ? tb('fltStateAll') : t('fltStateAll'));
  // Селекты в колонке — поповер слоем поверх раскладки и от левого края строки.
  const fltPop = { float: true, align: 'left' } as const;
  const fltRow = (
    <div className="tpl-flt scn-flt-right">
      {/* Фильтр состояния — ОДИН механизм на оба раздела (владелец 21.08,
          претензия 5): у бота «Включено/Выключено», у рассылки — её пять
          состояний. Своего фильтра рассылки не пишем. */}
      <PopSelect
        {...fltPop}
        ariaLabel={bcast ? tb('fltState') : t('fltState')}
        label={stateLabel}
        options={stateOpts}
        onPick={(k) => onFilter?.('state', k)}
      />
      {!bcast && channels.length > 0 && (
        <PopSelect
          {...fltPop}
          ariaLabel={t('fltChannel')}
          label={bySlug(channels, channel) ?? t('fltAllChannels')}
          options={[
            { key: '', label: t('fltAllChannels'), on: !channel },
            ...channels.map((c) => ({ key: slugify(c), label: c, on: channel === slugify(c) })),
          ]}
          onPick={(k) => onFilter?.('channel', k)}
        />
      )}
      {accounts.length > 0 && (
        <PopSelect
          {...fltPop}
          ariaLabel={t('fltAccount')}
          label={bySlug(accounts, account) ?? t('fltAllAccounts')}
          options={[
            { key: '', label: t('fltAllAccounts'), on: !account },
            ...accounts.map((a) => ({ key: slugify(a), label: a, on: account === slugify(a) })),
          ]}
          onPick={(k) => onFilter?.('account', k)}
        />
      )}
    </div>
  );

  return (
    <>
      {/* Список своих скриптов в развороте подписан выбранной категорией (или «Все»),
          а под подписью — фильтры канала и аккаунта (владелец 2026-08-01). */}
      {viewPick !== null ? (
        <>
          {/* ОДНА ШАПКА ПОЛЯ НА ПЯТЬ РАЗДЕЛОВ (пакет Д1.5, владелец 04.10.2026): та же разметка, что
              <PaneHead> (page-action.tsx) — `.pane-head` + `.pane-head-t` + `.pane-head-act`; своего
              правила у «Ботов» больше нет. `scn-pane-head` остался только меткой для экранных тестов. */}
          <div className="split-pane-t pane-head scn-pane-head">
            {/* Заголовок правого поля — «Все скрипты», а не голое «Все» (владелец 2026-08-02);
                у выбранной категории — её имя. Фильтры — В ЭТОЙ ЖЕ строке, по центру её
                высоты (владелец 2026-08-02: «по высоте хуй пойми с чем выравнены»). */}
            {/* «Рассылки» (Д1.1-д): поле подписано именем раздела, «+» справа — как шапка телефона R06. */}
            {/* ТРИ СЛОЯ, КОМПЬЮТЕР (владелец 13.10.2026): стрелка — следующим символом заголовка через пробел («Все боты ⚡»),
                нажатие раскрывает полосу ссылок сверху поля тем же ходом, что жест на телефоне (держится 4 с, уезжает). */}
            <span className="pane-head-t">
              {bcast ? tsec('broadcasts') : (catName ?? t('paneAllScripts'))}
              {deskStrip ? (
                <>
                  {' '}
                  <QuickStripButton />
                </>
              ) : null}
            </span>
            <span className="pane-head-act">
            {/* ТРИ СЛОЯ: ОТБОР «БОТОВ» — В КОЛОНКЕ, верх контента чистый (владелец 30.09.2026,
                ТЗ WEB-LOOK Э3.2): FilterSlot уносит селекты порталом в блок «Отбор» колонки
                (split-menu.tsx), там они строками той же ширины, что пункты; поповер —
                слоем поверх раскладки (float), иначе его резала бы прокрутка колонки.
                «Рассылки» — тем же механизмом, свой блок в BroadcastsSplitMenu (Fable 30.09). */}
            {/* Отбор «Ботов» спрятан флагом (BOTS_FILTER_HIDDEN): его блока нет и в колонке. */}
            {/* У «Рассылок» отбора в колонке нет (Д1.1-д: колонка — «Создать» и «Папки», как у «Ботов»). */}
            {!bcast && !BOTS_FILTER_HIDDEN ? <FilterSlot name="bots">{fltRow}</FilterSlot> : null}
            {/* Значок «Pro» на углу «+» рассылок (TARIFF-PRO-TZ §5): пилюля своей кнопкой рядом, не внутри. */}
            {bcast && onCreate ? (
              <span className="pro-badge-host">
                <PageAddButton label={tb('newTitle')} onClick={() => onCreate()} />
                <ProBadge className="pro-badge--corner" />
              </span>
            ) : null}
            {/* «+» СПРАВА В ШАПКЕ ПОЛЯ (партия Н4, владелец 03.10.2026): тот же кружок, что «+»
                шапки телефона (PageAddButton, `.page-add` = `.k-round`), вместо прежней группы
                «Создать бота / Каталог шаблонов» колонки. Единственная дорога (конструктор и
                ассистент спрятаны) — каталог шаблонов МОДАЛЬНОЙ ШТОРКОЙ (панель 460 с затемнением,
                владелец 08.10.2026, PAY-SCREEN-2-TZ §0 п.0), не страницей в поле; вернутся
                дороги — откроется общая выпадашка путей у кнопки. */}
            {!bcast && (
              <PageAddButton
                label={t('create')}
                accent
                disabled={create.busy}
                onClick={(e) => (BUILDER_HIDDEN && ASSISTANT_HIDDEN ? openChooser('templates') : createWays.open(e))}
              />
            )}
            </span>
          </div>
          {/* Сводка разворота — только у ботов: у рассылки цифры свои и живут на её
              карточке и странице цифр («за неделю прохождений» ей нечего сказать). */}
          {!bcast && sumTotal > 0 && (
            <div className="scn-pane-sum">
              {t('paneSummary', { n: sumTotal, run: sumRun })}
              {sumWeek > 0 ? ` · ${t('paneSummaryWeek', { week: sumWeek })}` : ''}
            </div>
          )}
        </>
      ) : (
        !bcast && createChips
      )}
      {/* «+» — В ПРАВОМ ВЕРХНЕМ УГЛУ шапки (владелец 31.08.2026, отменяет плавающий
          кружок от 12.08): только узкий вид (телефон, Safari; в приложении веб-шапка
          скрыта — там родная кнопка «+»). На развороте с колонкой (pick) кнопки нет —
          там блок путей создания на странице, его не трогаем.
          У рассылок этот «+» — ЕДИНСТВЕННАЯ кнопка непустого раздела (владелец 21.08,
          претензия 4): строки-кнопки под списком больше нет, а на пустом стоит герой. */}
      {viewPick === null && (
        <PageAction>
          {bcast ? (
            // Событие тапа уходит витрине рассылок: её выпадашка путей встаёт У КНОПКИ.
            <span className="pro-badge-host">
              <PageAddButton label={tb('newTitle')} onClick={(e) => onCreate?.(e)} />
              <ProBadge className="pro-badge--corner" />
            </span>
          ) : (
            <PageAddButton label={t('create')} accent onClick={createWays.open} disabled={create.busy} />
          )}
        </PageAction>
      )}
      {!bcast && createWays.pop}
      {/* Беда запуска рассылки словами — там же, где её увидели (список), и без
          техподробностей: техническое уходит владельцу (CLAUDE.md запрет №7). */}
      {bcast && bcActs.err ? <div className="keybanner">{bcActs.err}</div> : null}
      {!data.apiKeyValid && <div className="keybanner">{t('keyBanner')}</div>}
      {/* Полос ошибок включения над списком БОЛЬШЕ НЕТ (Г2 BUILDER-INSTANT, владелец
          13.08): не включился — говорит сам чип карточки, причина открывается окном. */}
      {/* «Проверить» из меню карточки (Э2.3): ход и итог прогона — теми же плашками,
          что на странице сценария; key — новый прогон при выборе другой карточки. */}
      {checkRun ? (
        <CardCheckHost key={checkRun} draftId={checkRun} onClose={() => setCheckRun(null)} />
      ) : null}
      {/* Пересечение триггеров (Э3, Р2): предупреждение тостом, включение уже идёт. */}
      {warnToast ? (
        <KToast tone="warn" text={warnToast} />
      ) : null}
      {/* Пузыри по триггерам (владелец 2026-07-23, как модели по компаниям): черновики
          первыми, за ними готовые — всё раскладывается по группам-триггерам внутри.
          Плитки быстрых шаблонов держит сам контейнер, поэтому пустой список ими и
          начинается; белой плиты «Пока нет сценариев» тут БОЛЬШЕ НЕТ (владелец
          2026-08-06: «когда нет сценариев, выглядит уёбищно») — пустота говорит одной
          серой строкой, как подпись. */}
      {/* КАПСУЛА КАТЕГОРИЙ (П81, паритет с приложением): «Все» и категории с ботами; тап —
          тот же адрес, что у колонки категорий, шестерёнка — их настройки. */}
      {/* Капсула сама носит .scn-folders (обёртки нет с П3 05.10.2026: пустая обёртка-div считалась
          второй «стеклянной» поверхностью без тени и мешала прилипанию самой капсулы). */}
      {/* Полоса — в слот шапки списка (ТЗ WEB-CAUSES О3, list-head.tsx HeadFolders). */}
      {botFolders ? (
        <HeadFolders>
        <FoldersStrip
          className="scn-folders"
          folders={botFolders}
          current={catName ?? 'all'}
          onPick={folderPager.pick}
          link={folderPager.strip}
          onSettings={() => {
            if (openNativeSettingsPage(['settings', `${BT_FOLDERS_PAGE}list`])) return;
            // Два слоя (Н4): лист «Папки» стопкой справа, как у строки «Настройка папок»
            // колонки трёх слоёв. Телефон в браузере — ТОТ ЖЕ лист «Папки» снизу с ходом,
            // как шестерёнка «Чатов» и «Контактов» (ТЗ ANIM-FIX О5 П4, видео владельца
            // 04.10.2026: прежний уход в свод «Настроек» менял вкладку без хода).
            openBotsFolders();
          }}
        />
        </HeadFolders>
    ) : null}
    {/* Список папки — страница пейджера (ТЗ ANIM-FIX О5 П6): тап по папке и свайп по списку
          листают папки ходом, как Telegram iOS. */}
      <FolderPage pager={folderPager}>
      {triggerGroups(listRows)}
      {/* INVARIANT(scriptera): у списка ботов ТРИ состояния и ни одного молчаливого —
          кэш/данные дают карточки, холодный старт даёт скелетон (выше по коду), пустой
          итог обязан сказать СЛОВАМИ, чем он пустой. Причина (владелец 2026-08-20, ТЗ 8
          фикслиста кабинета, IMG_2262): страница «Ботов» без карточек и без единого слова
          читается как поломка. Ловушка, из-за которой так и было: место пустого списка
          занимали плитки шаблонов, поэтому отфильтрованный «в ноль» список оставался
          ПУСТЫМ экраном, а прежняя строка показывалась только когда ботов нет вовсе
          (плиток с 02.09.2026 нет вовсе — Н6, а строка обязана быть по-прежнему).
          Заводишь новый отбор — добавь его в `filtered`, иначе вернёшь немую пустоту. */}
      {listRows.length === 0 && !(bcast && !filtered && archive.length > 0) && (
        // ПУСТО — ЛОТОК И СЛОВО ПО ЦЕНТРУ, как в iOS (кадры B03-bots-search-empty,
        // B01-bots-list-folder-filter; ТЗ WEB-PARITY-3 Б4/Б6): одно пустое состояние набора.
        // ТЕЛЕФОН В БРАУЗЕРЕ БЕЗ ПОИСКА — ПОДСКАЗКА «ПОТЯНИТЕ ВНИЗ», как родная
        // (ScrPullHintView; кадры B00-first-screen-after-login и B01-bots-list-folder-filter):
        // розовая стрелка вниз и слово серым — зовут к полосе-пасхалке, она в браузере есть.
        // Ботов нет вовсе — «Потяните страницу вниз» (строки поиска тогда нет, см. cabinet.css
        // `.scn-empty-all`); пустая папка — «Здесь пока пусто». Поиск и прочий отбор — прежний
        // лоток со словом. Вид — тот же KEmpty набора, своей разметки нет.
        // СТРЕЛКА — ТОЛЬКО КОГДА БОТОВ НЕТ ВООБЩЕ (ТЗ WEB-CAUSES М53, кадр владельца 1c31213a):
        // пустая папка при живых ботах — лоток и «Здесь пока пусто», как пустая папка «Чатов»
        // (кадр iOS C01-chats-folder-empty; родной ScrEmptyView.symbol — всегда `tray`).
        // КОМПЬЮТЕР (владелец 13.10.2026; постоянная полоса §3.12 от 12.10 снята): тянуть нечем — стрелки нет ни при
        // какой ширине окна; ботов нет вовсе — «Ботов нет, нажмите» со значком-молнией, нажатие раскрывает полосу тем
        // же ходом, что жест на телефоне. Строки поиска тогда нет (scn-empty-all, как у подсказки телефона).
        // ТЕЛЕФОН (≤ 820) — БЕЗ МОЛНИИ (APP-CLAIMS-1020 ред. 8, Р19, вопрос 108, решение ведущего 21.10.2026): молнии в
        // шапке телефона нет (вход в полосу — кружок-указатель слева), поэтому и пустой экран зовёт не к ней, а к «+» и
        // указателю: «Ботов пока нет. Нажмите «+», чтобы создать первого, или указатель [значок] слева…». Сенсор и мышь —
        // одинаково (шапка решает по ширине). Шире 820 — прежнее «Ботов нет, нажмите ⚡» (ниже).
        !bcast && !isApp && layoutMode === 'phone' && !filtered && !anyBots && data.apiKeyValid ? (
          // QUICK-START §11.34 п.16б (владелец 30.10: «я не говорил»): прежний текст «Потяните вниз или нажмите ⚡» со
          // стрелкой и молнией (п.20 14.10), как в приложении; вариант с «+»/указателем (Р19) снят.
          <KEmpty
            className="scn-empty-note scn-pull-hint scn-empty-all scn-pull-tap"
            icon={<IconPullDown />}
            title={
              <>
                {t('emptyApp')} <QuickStripButton />
              </>
            }
          />
        ) : deskStrip && !filtered && !anyBots && data.apiKeyValid ? (
          <KEmpty
            className="scn-empty-note scn-empty-all scn-empty-tap"
            title={
              <>
                {t('emptyTap')} <QuickStripButton />
              </>
            }
          />
        ) : (
          <KEmpty
            className="scn-empty-note"
            icon={<IconTray />}
            title={
              catFilterOn && !needle && !channel && !account && !state && !bcast
                ? t('emptyFolder')
                : filtered
                ? t('emptyFiltered')
                : bcast
                  ? tb('emptyLine')
                  : data.apiKeyValid
                    ? t('empty')
                    : t('keyEmpty')
            }
          />
        )
      )}
      </FolderPage>
      {/* Хвост витрины: архив старого журнала рассылок — только чтение, в конце списка. */}
      {tail}
      {/* «Такой запуск уже занят» (запрет одинаковых, раздел Л правило 4): общая шторка
          тумблеров бота и цепочки — выключить хозяина слова или поменять запуск. */}
      {botExists.sheet}
    </>
  );
}

/** ПОЧЕМУ БОТ НЕ ВКЛЮЧИЛСЯ (Г2 BUILDER-INSTANT, владелец 13.08). Окно открывает чип
 *  карточки; текст — по ВИНОВНИКУ (запрет №7 CLAUDE.md): что чинит человек — словами и
 *  с ходом, что сломалось у нас — одной честной строкой без техподробностей (алярм нам
 *  уже ушёл reportUiError). Кнопка «Пополнить» в приложении не рисуется: покупок в
 *  приложениях нет (запрет позиционирования №7). */
function EnableFailBody({
  kind,
  href,
  onClose,
}: {
  kind: FailKind;
  href: string | null;
  onClose: () => void;
}) {
  const t = useTranslations('cabinet.automations');
  const tb = useTranslations('cabinet.billing');
  const native = useNativeApp();
  // Кнопка «Пополнить» — общее правило (приложение и валюта не RUB — нет, MULTICURRENCY Э5.2).
  const topupOk = useTokensTopupAllowed();
  const panel = useSlidePanel();
  const router = useRouter();
  const slideOpen = useSlideOpen();
  // В ПРИЛОЖЕНИИ ПРО ПОПОЛНЕНИЕ НИ СЛОВА (ТЗ П127.2, владелец 18.09.2026: «"Пополнить
  // баланс" убери из приложения»): кнопку прятали и раньше, а текст звал платить — это
  // запрет №7. Выбор слова — одной рукой на оба экрана (lib/bot-fail-text.ts).
  const text = t(botFailTextKey(kind, native));
  // Ход есть только там, где человеку есть что сделать: открыть бота (ошибки в шагах)
  // или пополнить баланс. У нашей поломки хода нет — чиним мы.
  const openBot = () => {
    if (!href) return;
    onClose();
    if (!slideOpen(href)) router.push(href);
  };
  return (
    <FPane>
      <FHint>{text}</FHint>
      {kind === 'lint' && href ? (
        <KActionBar>
          <KButton tone="primary" onClick={openBot}>
            {t('failOpenCta')}
          </KButton>
        </KActionBar>
      ) : null}
      {kind === 'balance' && topupOk ? (
        <KActionBar>
          <KButton tone="primary" onClick={() => panel.open({ title: tb('topupTitle'), ...webOnly({ sheet: true }), body: <TopupForm /> })}>
            {t('failTopupCta')}
          </KButton>
        </KActionBar>
      ) : null}
    </FPane>
  );
}

/** «О сценарии» из карточки списка (Э2.3): подтягивает черновик ТЕМ ЖЕ кэшом, что
 *  страница сценария, и рисует ту же ScenarioAboutForm — правки видят один снимок. */
function CardAboutSheet({
  draftId,
  saveRef,
  onSaved,
}: {
  draftId: string;
  saveRef: React.MutableRefObject<(() => void) | null>;
  onSaved: () => void;
}) {
  const { data, mutate } = usePersistentData<{ ok: boolean; draft: DraftSnapshot }>(
    `cabinet:scenario-draft:${draftId}`,
    `/api/cabinet/scenarios/drafts/${draftId}`,
    { accept: (j) => !!(j as { ok?: boolean })?.ok },
  );
  const draft = data?.draft ?? null;
  if (!draft) return <Sk w={220} h={22} />;
  return (
    <ScenarioAboutForm
      draft={draft}
      saveRef={saveRef}
      onSaved={(d) => {
        mutate({ ok: true, draft: d });
        onSaved(); // список перечитывает название/категорию карточки
      }}
    />
  );
}

/** «Проверить» из карточки списка (Э2.3): тот же живой прогон, что на странице
 *  сценария — состояние ведёт useScenarioRun, черновик приходит тем же кэшом.
 *  Итог — те же плашки: зелёный тост успеха (сам гаснет) / закрываемая ошибка. */
function CardCheckHost({ draftId, onClose }: { draftId: string; onClose: () => void }) {
  const ts = useTranslations('cabinet.scenario');
  const tc = useTranslations('cabinet');
  const { data, mutate } = usePersistentData<{ ok: boolean; draft: DraftSnapshot }>(
    `cabinet:scenario-draft:${draftId}`,
    `/api/cabinet/scenarios/drafts/${draftId}`,
    { accept: (j) => !!(j as { ok?: boolean })?.ok },
  );
  const draft = data?.draft ?? null;
  const run = useScenarioRun({ draftId, draft, onDraft: (d) => mutate({ ok: true, draft: d }) });
  // Прогон стартует сам, один раз — как нажатие «Проверить» на странице.
  const started = useRef(false);
  useEffect(() => {
    if (!draft || started.current) return;
    started.current = true;
    run.start();
  }, [draft, run]);
  if (run.note && run.note.ok)
    return (
      <KToast text={run.note.text} />
    );
  if (run.note)
    return (
      <div className="scn-run-note closable" role="status">
        <span className="scn-run-note-text">{run.note.text}</span>
        <KBareButton
          className="scn-run-note-x"
          aria-label={tc('close')}
          onClick={() => {
            run.stop();
            onClose();
          }}
        >
          ✕
        </KBareButton>
      </div>
    );
  // Плашки от хука ещё нет (грузим черновик / прогон стартует) — честное «Проверяем…».
  if (run.phase === 'idle' || run.phase === 'starting' || run.phase === 'running')
    return (
      <div className="scn-run-note" role="status">
        {ts('runBusy')}
      </div>
    );
  return null;
}
